export const physicalAIArticles2026 = [
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-09-29",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "vision-language-action-models",
    "slug": "vision-language-action-models",
    "path": "/robotics-news/vision-language-action-models",
    "title": "Vision-Language-Action Models",
    "headline": "Vision-Language-Action Models How Images and Instructions Become Robot Motion",
    "seoTitle": "How Vision-Language-Action Models Control Physical Robots",
    "description": "A technical guide to VLA models, action tokenization, diffusion control, robot data, real-world evidence, latency, generalization and failure modes.",
    "primaryKeyword": "vision-language-action models",
    "keywords": [
      "vision-language-action models",
      "VLA robotics",
      "robot foundation models",
      "action tokenization",
      "diffusion policy",
      "OpenVLA",
      "Octo robot policy",
      "pi0.5 robot model",
      "GR00T N1.6",
      "Gemini Robotics"
    ],
    "searchQuestions": [
      "How do vision-language-action models work?",
      "What can a VLA model control?",
      "Are VLA robots autonomous?",
      "What is action chunking in robotics?",
      "Which VLA models are open source?"
    ],
    "longTailKeywords": [
      "how VLA models convert language into robot actions",
      "open source vision language action model comparison",
      "VLA inference latency and closed loop robot control",
      "difference between VLA model and robot foundation model"
    ],
    "entities": [
      "RT-1",
      "RT-2",
      "OpenVLA",
      "Octo",
      "π0.5",
      "GR00T N1.6",
      "Gemini Robotics 1.5",
      "Helix",
      "SmolVLA"
    ],
    "imageAlt": "Technical illustration of cameras, language instructions and robot state flowing through a VLA policy into robot actions",
    "introduction": [
  "A robot can recognize a cup and still fail to pick it up. The missing step is not object detection but control the system must connect pixels and words to a timed sequence of joint or end-effector commands, observe the result and correct the next action. Vision-language-action models, usually shortened to VLA models, attempt to learn that connection from large collections of robot demonstrations."
],
    "keyFindings": [
      "A VLA policy outputs robot actions or action trajectories; a vision-language model that only describes a scene is not a VLA.",
      "Action representation is a central design choice systems use discretized tokens, continuous regression, diffusion trajectories or embodiment-specific action heads.",
      "Most public zero-shot claims concern new objects, instructions or scenes on known hardware, not deployment on an unseen robot without adaptation.",
      "Closed-loop control depends on observation frequency and inference latency; a strong semantic model can still fail when control updates arrive too slowly.",
      "Cross-embodiment datasets improve transfer, but differences in kinematics, grippers, camera placement and action frequency still require normalization or adaptation."
    ],
    "comparisonTables": [
  {
    "id": "vla-model-comparison",
    "title": "Major VLA systems and the evidence they publish",
    "columns": [
      "Model",
      "Organization",
      "Year",
      "Action output",
      "Robot coverage",
      "Availability",
      "Main limitation"
    ],
    "rows": [
      [
        "RT-2",
        "Google DeepMind",
        "2023",
        "Discretized action tokens",
        "Multiple Google robot setups",
        "Paper; weights unavailable",
        "Closed system and limited reproducibility"
      ],
      [
        "OpenVLA",
        "Stanford, UC Berkeley and collaborators",
        "2024",
        "Tokenized continuous control",
        "Open X-Embodiment fine-tuning targets",
        "Code and 7B weights released",
        "Large compute footprint and adaptation still required"
      ],
      [
        "Octo",
        "UC Berkeley and collaborators",
        "2024",
        "Diffusion action chunks",
        "Nine training embodiments reported",
        "Code and checkpoints released",
        "Evaluation protocols vary by target robot"
      ],
      [
        "π0.5",
        "Physical Intelligence",
        "2025",
        "Continuous action chunks from a flow-matching model",
        "Company robots and mobile manipulators",
        "Code and π0.5 checkpoints released in openpi",
        "Results reported by the developer"
      ],
      [
        "GR00T N1.6",
        "NVIDIA",
        "2025",
        "Embodiment-conditioned action policy",
        "Several humanoid embodiments",
        "Open model release reported by NVIDIA",
        "Real deployment evidence remains task-specific"
      ],
      [
        "Gemini Robotics 1.5",
        "Google DeepMind",
        "2025",
        "High-level reasoning plus robot action policy",
        "Multiple robot embodiments reported",
        "Closed access",
        "Architecture and training data not fully public"
      ],
      [
        "Helix",
        "Figure AI",
        "2025",
        "Whole upper-body action generation",
        "Figure humanoid only in public evidence",
        "Closed",
        "Single company embodiment and company-reported tests"
      ],
      [
        "SmolVLA",
        "Hugging Face",
        "2025",
        "Compact action chunks",
        "LeRobot-compatible platforms",
        "Code, model and training stack released",
        "Smaller scale does not remove robot-specific calibration"
      ]
    ],
    "description": "Rows use the latest named version discussed in the cited primary material. Benchmarks are not ranked because tasks, hardware and success definitions differ.",
    "note": "Named historical versions are compared, not a claim to cover every latest model. π0.5 availability was corrected from the official openpi repository on September 29, 2026."
  }
],
    "sections": [
      {
        "heading": "Definition what a VLA model is, and what it is not",
        "paragraphs": [
          "A vision-language-action model receives visual observations, a natural-language instruction and, in many implementations, proprioceptive state such as joint positions or gripper status. It produces an action or a short sequence of actions that can be executed by a robot controller. The action may be expressed as joint targets, end-effector pose changes, gripper commands, base motion or a combination of these.",
          "A VLA is not simply a large language model connected to a robot API. It is also not a vision-language model that returns text, a planner that writes symbolic steps without executing them or a video generator that predicts what a successful task might look like. The defining property is a learned mapping from multimodal observations and instructions to control-relevant output."
        ]
      },
      {
        "heading": "How the VLA pipeline works",
        "paragraphs": [
          "Cameras provide one or more RGB images, sometimes with depth. A visual encoder converts those images into feature tokens. A language tokenizer and encoder represent the instruction. Proprioceptive values may be projected into the same latent space or passed to a separate action module. Cross-attention, early fusion or a shared transformer then connects words such as “red mug” with regions in the image and with the current robot state.",
          "The policy head generates control commands. Autoregressive systems predict one action token after another. Continuous regression predicts numerical deltas directly. Diffusion policies start from noise and iteratively refine a trajectory. Action chunking predicts several future control steps in one inference pass, reducing repeated model calls but increasing the risk that later actions become stale when the world changes. A low-level controller converts the policy output into motor commands, while new sensor observations close the loop."
        ],
        "subsections": [
          {
            "heading": "Discrete actions, continuous actions and diffusion trajectories",
            "paragraphs": [
              "Discretization lets a transformer treat action values like language tokens, but precision depends on binning and the action vocabulary. Continuous heads preserve numerical resolution but can average incompatible behaviors. Diffusion policies represent multimodal trajectories more naturally, although iterative sampling can add latency. None of these choices is universally superior; the useful comparison is control quality at the required frequency on the target robot."
            ]
          },
          {
            "heading": "Temporal context and proprioception",
            "paragraphs": [
              "A single image cannot reveal whether a gripper is closing, slipping or already in contact. VLA systems therefore use short observation histories, previous actions or proprioceptive state. Temporal context helps infer motion and task progress, while proprioception anchors visual predictions to the robot’s actual configuration. Systems that omit these signals may work on slow pick-and-place tasks yet struggle with dynamic contact or recovery."
            ]
          }
        ]
      },
      {
        "heading": "Key systems and what they actually demonstrate",
        "paragraphs": [
          "RT-1 established a transformer policy trained across many tasks on a shared mobile-manipulator platform. RT-2 transferred visual-language representations into action tokens and reported improved semantic generalization, but its weights and full training data were not released. OpenVLA trained a 7-billion-parameter open model on Open X-Embodiment data and made code and weights available, creating a reproducible baseline for adaptation rather than a plug-and-play universal controller.",
          "Octo uses a transformer backbone with diffusion action decoding and was designed for fine-tuning across robot platforms. Physical Intelligence’s π0 and π0.5 combine large-scale multimodal pre-training with continuous robot control; π0.5 emphasizes task generalization in new homes, but the company’s reported evaluations are not independently replicated at equivalent scale. NVIDIA’s GR00T line targets humanoids and exposes embodiment conditioning, while Gemini Robotics and Figure’s Helix remain closed systems with selective public technical detail."
        ],
        "sources": [
          {
            "label": "OpenVLA project",
            "url": "https://github.com/openvla/openvla"
          },
          {
            "label": "Octo project",
            "url": "https://octo-models.github.io/"
          },
          {
            "label": "π0.5 report",
            "url": "https://www.physicalintelligence.company/blog/pi05"
          },
          {
            "label": "GR00T N1.6",
            "url": "https://research.nvidia.com/labs/gear/gr00t-n1_6/"
          }
        ]
      },
      {
        "heading": "What a VLA model can actually control",
        "paragraphs": [
          "Public VLA demonstrations most often control a robot arm, a mobile manipulator or the upper body of a humanoid. Outputs typically cover Cartesian end-effector motion, wrist rotation, gripper opening and closing, base velocity or a fixed set of joint targets. Whole-body humanoid control adds balance, foot contacts and collision constraints, so many systems delegate locomotion and stabilization to separate controllers even when the VLA chooses the task-level action.",
          "The boundary matters. A model that tells a motion planner to “move to this pose” controls task intent but not every torque. A policy that emits joint targets at several hertz controls more of the motion but still relies on servo loops, safety limits and state estimation. Describing both as end-to-end control hides where perception, planning, stabilization and protection actually occur."
        ]
      },
      {
        "heading": "Evidence from real robots and the meaning of zero-shot",
        "paragraphs": [
          "Real-robot evidence ranges from hundreds of short tabletop trials to mobile manipulation in multiple rooms. New-object tests ask whether the policy can act on an unseen instance within a familiar task. New-task tests change the instruction or object relation. New-environment tests change backgrounds, layouts or homes. New-embodiment transfer is harder because the action space and camera geometry change; most systems require fine-tuning, adapters or an embodiment-specific head.",
          "Zero-shot therefore has no single meaning. In RT-2 it included semantic and reasoning generalization on known robot hardware. In π0.5, the company reported execution in new homes after broad training. In open models, zero-shot may mean applying a checkpoint before target-robot fine-tuning. A responsible comparison states exactly which variable was unseen and which parts of the stack remained fixed."
        ]
      },
      {
        "heading": "Failure modes where VLA policies break",
        "paragraphs": [
          "Incorrect visual grounding can attach an instruction to the wrong object. Depth errors produce grasps that stop short or collide. Long action chunks can continue after an object moves. Autoregressive error accumulates across long tasks, while diffusion sampling may miss a real-time deadline. Camera relocation changes the visual distribution, and a new gripper changes the meaning of the same end-effector command.",
          "Recovery is a separate capability from initial success. Many benchmarks reset the scene after failure, which hides whether the policy can notice a dropped object, replan around an obstruction or safely abandon a task. Language can also be correlated rather than causally grounded a policy may choose the correct action because a phrase resembles training examples, without maintaining an explicit model of object state or task constraints."
        ]
      },
      {
        "heading": "Practical applications and current maturity",
        "paragraphs": [
          "The most credible present uses are bounded manipulation jobs with known safety envelopes sorting, tote transfer, laboratory handling, mobile pick-and-place and data collection. VLA policies are useful when task variation is too large for one hand-coded sequence but the environment can still be monitored and reset. They can also provide a high-level layer above conventional motion planning and force control.",
          "Unsupervised household work, long industrial shifts and safety-critical contact remain less mature. These settings demand consistent perception under lighting changes, rapid recovery, calibrated force, low latency and validated fail-safe behavior. A VLA can broaden the task vocabulary, but it does not replace mechanical design, deterministic safety controls or application-specific validation."
        ]
      },
      {
        "heading": "2026 update flow-based action experts and calibrated uncertainty",
        "paragraphs": [
          "Many current VLA architectures pair a semantic vision-language backbone with a continuous flow-matching action expert. The action expert transports a sampled source into an action chunk conditioned on images, language and proprioception. This design can represent multiple valid trajectories, but its useful control rate depends on the complete encoder, solver, chunk horizon and replanning loop rather than the action head alone.",
          "A second research direction measures when those generated actions may be unreliable. Sample diversity is not automatically calibrated confidence, so 2026 work studies flow-specific uncertainty, conformal action-chunk sets, execution residuals and failure-aware fallback decisions. These topics now have separate TechniaHQRobot guides because action generation and uncertainty estimation are distinct technical questions within the wider VLA architecture."
        ]
      }
    ],
    "limitations": [
      "Training-set sizes are reported using different units, including episodes, trajectories, hours and transitions, so direct scale comparisons are unreliable.",
      "Closed models do not expose complete datasets, architecture details, weights or failure logs; published results are developer-reported.",
      "Success rates use different task definitions, reset rules and horizons and should not be ranked in one league table.",
      "Many demonstrations use prepared objects, fixed camera layouts or controlled lighting even when the instruction is novel.",
      "Control frequency and end-to-end inference latency are not consistently reported, despite their importance for contact-rich tasks."
    ],
    "conclusion": [],
    "faqs": [
  {
    "question": "What is a vision-language-action model?",
    "answer": "A vision-language-action model is a learned robot policy that combines visual observations, a language instruction and often robot state to produce executable actions. Unlike a vision-language model that answers with text, a VLA returns joint commands, end-effector changes, gripper actions, base motion or short action trajectories that another controller sends to the hardware."
  },
  {
    "question": "Are VLA models autonomous?",
    "answer": "A VLA can execute actions without a human moving the robot moment by moment, but autonomy depends on the complete system. Human task selection, resets, safety supervision, remote intervention and conventional low-level controllers may still be present. Published papers should be checked for who starts the task, how failures are handled and whether demonstrations were collected through teleoperation."
  },
  {
    "question": "What is action chunking in robotics?",
    "answer": "Action chunking predicts several future control steps from one observation window. It can reduce inference calls and produce smoother motion, especially when a large model is too slow to run at servo frequency. The trade-off is responsiveness later actions in the chunk may no longer match the scene after contact, object motion or an unexpected disturbance."
  },
  {
    "question": "What does zero-shot mean for a VLA?",
    "answer": "Zero-shot should name the unseen variable. It may mean a new object, a new instruction, a new room or a new task on familiar hardware. It rarely means that the same policy can control an arbitrary unseen robot without adaptation. Cross-robot transfer usually needs normalized actions, embodiment tokens, adapters or target-specific fine-tuning."
  },
  {
    "question": "Which VLA models are open source?",
    "answer": "OpenVLA, Octo, SmolVLA and Physical Intelligence's openpi provide public code and checkpoints under their respective licenses. The openpi release includes π0.5. Public weights still require compatible observations, action conventions and target-robot integration."
  },
  {
    "question": "Why do VLA models need so much robot data?",
    "answer": "Robot actions depend on embodiment, contact, camera geometry and timing. Internet images contain semantics but usually lack joint states, force, gripper commands and failure recovery. Demonstration data supplies those action labels, yet it is expensive because hardware must be operated, maintained, reset and recorded safely. Cross-embodiment datasets reduce duplication but do not remove calibration differences."
  }
],
    "methodology": [
  "This page compares the named model versions using their projects, papers and repositories. It does not rank success rates across incompatible robots, tasks or reset protocols. The September 29 correction checks π0.5 release status against openpi; it does not certify every current model version."
],
    "sourceLinks": [
  {
    "label": "Brohan et al.",
    "organization": "Google Robotics",
    "documentTitle": "RT-1 Robotics Transformer for Real-World Control at Scale",
    "date": "December 2022 · accessed July 11, 2026",
    "url": "https://arxiv.org/abs/2212.06817"
  },
  {
    "label": "Brohan et al.",
    "organization": "Google DeepMind",
    "documentTitle": "RT-2 Vision-Language-Action Models Transfer Web Knowledge to Robotic Control",
    "date": "July 2023 · accessed July 11, 2026",
    "url": "https://arxiv.org/abs/2307.15818"
  },
  {
    "label": "Kim et al.",
    "organization": "Stanford and UC Berkeley",
    "documentTitle": "OpenVLA An Open-Source Vision-Language-Action Model",
    "date": "June 2024 · accessed July 11, 2026",
    "url": "https://arxiv.org/abs/2406.09246"
  },
  {
    "label": "Octo Model Team",
    "organization": "UC Berkeley and collaborators",
    "documentTitle": "Octo An Open-Source Generalist Robot Policy",
    "date": "2024 · accessed July 11, 2026",
    "url": "https://octo-models.github.io/"
  },
  {
    "label": "Physical Intelligence",
    "organization": "Physical Intelligence",
    "documentTitle": "π0.5 A Vision-Language-Action Model with Open-World Generalization",
    "date": "April 22, 2025 · accessed July 11, 2026",
    "url": "https://www.physicalintelligence.company/blog/pi05"
  },
  {
    "label": "NVIDIA GEAR Lab",
    "organization": "NVIDIA",
    "documentTitle": "GR00T N1.6",
    "date": "December 15, 2025 · accessed July 11, 2026",
    "url": "https://research.nvidia.com/labs/gear/gr00t-n1_6/"
  },
  {
    "label": "Google DeepMind",
    "organization": "Google DeepMind",
    "documentTitle": "Gemini Robotics 1.5 brings AI agents into the physical world",
    "date": "September 25, 2025 · accessed July 11, 2026",
    "url": "https://deepmind.google/blog/gemini-robotics-15-brings-ai-agents-into-the-physical-world/"
  },
  {
    "label": "Figure AI",
    "organization": "Figure AI",
    "documentTitle": "Helix A Vision-Language-Action Model for Generalist Humanoid Control",
    "date": "February 20, 2025 · accessed July 11, 2026",
    "url": "https://www.figure.ai/news/helix"
  },
  {
    "label": "Hugging Face",
    "organization": "Hugging Face",
    "documentTitle": "SmolVLA A compact vision-language-action model",
    "date": "June 3, 2025 · accessed July 11, 2026",
    "url": "https://huggingface.co/blog/smolvla"
  },
  {
    "label": "Official openpi release",
    "organization": "Physical Intelligence",
    "documentTitle": "openpi: π0, π0-FAST and π0.5",
    "date": "Accessed September 29, 2026",
    "url": "https://github.com/Physical-Intelligence/openpi"
  }
],
    "internalLinks": [
      {
        "label": "Generalist robot policies and the limits of task coverage",
        "path": "/robotics-news/generalist-robot-policies"
      },
      {
        "label": "Robot foundation models evidence, scale and adaptation",
        "path": "/robotics-news/robot-foundation-models"
      },
      {
        "label": "Cross-embodiment learning across different robot bodies",
        "path": "/robotics-news/cross-embodiment-learning"
      },
      {
        "label": "Flow matching for continuous robot action chunks",
        "path": "/robotics-news/flow-matching-robot-control"
      },
      {
        "label": "Robot policy uncertainty, calibration and fallbacks",
        "path": "/robotics-news/robot-policy-uncertainty-estimation"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "A new TechniaHQRobot pipeline graphic showing cameras, language, proprioception, fusion and action output",
        "alt": "Diagram of RGB cameras, text instruction and joint state entering a multimodal policy that outputs robot actions",
        "source": "TechniaHQRobot original based on cited model papers",
        "sourceUrl": "https://arxiv.org/abs/2406.09246"
      },
      {
        "type": "Secondary media",
        "caption": "OpenVLA operating a real robot from the official project material",
        "alt": "Robot arm executing a language-conditioned manipulation task with OpenVLA",
        "source": "OpenVLA official repository",
        "sourceUrl": "https://github.com/openvla/openvla"
      },
      {
        "type": "Secondary media",
        "caption": "Octo cross-embodiment evaluation montage",
        "alt": "Several robot embodiments performing manipulation tasks under Octo policies",
        "source": "Octo official project page",
        "sourceUrl": "https://octo-models.github.io/"
      },
      {
        "type": "Original chart",
        "caption": "Control-output comparison discrete tokens, continuous regression, diffusion and action chunks",
        "alt": "Chart comparing four action representations by precision, latency and multimodality",
        "source": "TechniaHQRobot original based on cited papers",
        "sourceUrl": "https://www.physicalintelligence.company/research/fast"
      },
      {
        "type": "Architecture",
        "caption": "Closed-loop VLA architecture with low-level safety controller separated from the learned policy",
        "alt": "Architecture diagram separating multimodal policy, action decoder, motor controller and safety monitor",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://deepmind.google/blog/gemini-robotics-15-brings-ai-agents-into-the-physical-world/"
      }
    ],
    "structuredDataSummary": [
      "Article schema uses the headline, description, TechniaHQRobot author and publisher, publication and modification dates, canonical page and hero image.",
      "FAQPage schema is generated from the six visible questions and answers.",
      "BreadcrumbList schema follows Home, Robotics News and this article."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "OpenVLA, Octo and SmolVLA publish code and checkpoints through their official projects.",
        "The listed model dates, organizations and stated embodiment coverage match the cited primary sources.",
        "Real-robot and simulation evidence are identified separately in the article."
      ],
      "unconfirmed": [
        "Closed-model parameter counts, complete training datasets and end-to-end inference latency are not publicly disclosed in comparable form.",
        "Company-reported generalization results have not been independently reproduced at the same scale.",
        "Benchmarks are not directly comparable across robots, tasks, horizons and reset rules."
      ],
      "fastChanging": [
        "Model versions, access programs, repositories and weights can change after publication.",
        "New cross-embodiment datasets may alter the open-model comparison."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-10-11",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "world-action-models",
    "slug": "world-action-models",
    "path": "/robotics-news/world-action-models",
    "title": "World Action Models",
    "headline": "World Action Models Predicting Consequences Before a Robot Moves",
    "seoTitle": "World Action Models for Robot Planning and Control Systems",
    "description": "A rigorous guide to world action models, action-conditioned prediction, counterfactual rollouts, visual planning, uncertainty and real-robot control limits.",
    "primaryKeyword": "world action models",
    "keywords": [
      "world action model",
      "robot world model",
      "action-conditioned video prediction",
      "latent dynamics model",
      "model predictive control robotics",
      "visual planning",
      "1X World Model",
      "DreamZero",
      "WorldPlanner",
      "Cosmos Predict"
    ],
    "searchQuestions": [
      "What is a world action model?",
      "How is a world action model different from a video generator?",
      "Can world models control robots?",
      "What is action-conditioned prediction?",
      "Are generated futures physically accurate?"
    ],
    "longTailKeywords": [
      "world action models for robot planning",
      "difference between world model and robot simulator",
      "action conditioned video prediction for robotics",
      "can predictive video safely control a real robot"
    ],
    "entities": [
      "1X World Model",
      "DreamZero",
      "WorldPlanner",
      "Cosmos-Predict",
      "MotuBrain",
      "OSCAR"
    ],
    "imageAlt": "Robot planning pipeline that predicts several action-conditioned future observations before selecting an action",
    "introduction": [
  "World action models can connect future visual states with actions in different ways. The distinction affects whether a robot searches over candidate motions or directly uses a learned policy. DreamZero and WorldPlanner illustrate why these control mechanisms should be identified separately."
],
    "keyFindings": [
  "Most systems plan over short horizons because prediction errors compound and multiple candidate rollouts are computationally expensive.",
  "Pixels can look plausible while encoding the wrong contact state, friction, mass or hidden geometry.",
  "Model predictive control reduces drift by replanning after new observations, but it does not correct a systematically wrong dynamics model.",
  "Real-robot evidence remains narrower than the visual quality of the best generated futures suggests."
],
    "comparisonTables": [
  {
    "id": "world-action-model-comparison",
    "title": "Action-conditioned predictive systems for robotics",
    "columns": [
      "System",
      "Organization",
      "Year",
      "Predicted representation",
      "Action use",
      "Real robot evidence",
      "Public status"
    ],
    "rows": [
      [
        "1X World Model",
        "1X Technologies",
        "2025",
        "Future egocentric video",
        "Robot actions condition generation and policy improvement",
        "NEO data and company demonstrations",
        "Technical report; weights unavailable"
      ],
      [
        "DreamZero",
        "Research collaboration",
        "2026",
        "Joint video and action generation",
        "Policy inference",
        "Reported real-robot manipulation",
        "Paper; release status varies"
      ],
      [
        "WorldPlanner",
        "Research collaboration",
        "2025",
        "Predicted visual futures",
        "Monte Carlo tree search plus MPC",
        "Reported robot experiments",
        "Paper; code availability limited"
      ],
      [
        "Cosmos-Predict",
        "NVIDIA",
        "2025",
        "General physical video prediction",
        "Can be conditioned for downstream physical AI workflows",
        "Platform examples, not a standalone verified robot policy",
        "Models and tooling released in selected forms"
      ],
      [
        "MotuBrain",
        "Research collaboration",
        "2026",
        "Joint video and action",
        "Policy, world modeling and action-only inference",
        "Author-reported robot tests",
        "Paper; weights not fully public"
      ],
      [
        "OSCAR",
        "Research collaboration",
        "2026",
        "Action-conditioned video",
        "Policy evaluation using rendered skeleton conditioning",
        "Authors compare virtual and real policy evaluations",
        "Paper; public artifacts incomplete"
      ]
    ],
    "description": "The table includes systems that explicitly condition prediction on actions or use an equivalent predictive-control architecture. It does not label every video model a world action model.",
    "note": "Descriptions reflect primary sources checked July 11, 2026. Different projects use the term world model at different abstraction levels."
  }
],
    "sections": [
  {
    "heading": "Definition world model, world action model and policy",
    "paragraphs": [
      "The literature uses world action model for more than one architecture. An action-conditioned world model predicts consequences of supplied actions; a joint video-action model can generate actions as well as future observations. A planner may search over candidate actions, while a policy can emit actions directly. The name alone does not identify which control path a project implements.",
      "A simulator uses an engineered physics and rendering pipeline. A learned latent dynamics model approximates transitions from data. A video generation model may synthesize plausible motion without exposing an action space or calibrated uncertainty. A predictive controller uses a model repeatedly during execution. These components can be combined, but their names describe different functions and should not be treated as synonyms."
    ]
  },
  {
    "heading": "How action-conditioned prediction becomes control",
    "paragraphs": [
      "The pipeline begins with current observations, proprioception and sometimes a language goal. An encoder constructs a latent state that compresses task-relevant information. Candidate action sequences are sampled from a policy, optimizer or action library. The predictive model rolls each sequence forward, producing future latent states, images, rewards or success scores. A selector chooses the first action from the best sequence.",
      "Model predictive control, or MPC, repeats this process after each short execution window. Receding-horizon replanning limits open-loop drift because the model sees the actual result before predicting again. Longer horizons can capture delayed consequences but amplify uncertainty. Practical systems therefore balance rollout depth, number of candidates, inference cost and the control deadline."
    ],
    "subsections": [
      {
        "heading": "Counterfactual rollouts and uncertainty",
        "paragraphs": [
          "A counterfactual rollout asks what would happen under an action that has not yet been executed. Useful planners need more than one attractive future; they need alternatives and an estimate of uncertainty. Ensembles, stochastic latent variables or diffusion sampling can represent multiple outcomes, but a broad visual distribution is not the same as calibrated probability. Safety decisions require knowing when the model is outside its training distribution."
        ]
      },
      {
        "heading": "Reward prediction and visual planning",
        "paragraphs": [
          "Some systems predict a scalar reward or success probability. Others compare generated frames with a goal image or use a vision-language model to score progress. Visual similarity can fail when two images look alike but differ in contact, force or stability. A cup balanced at the edge of a table may match the goal image while being one vibration away from falling."
        ]
      }
    ]
  },
  {
    "heading": "Key systems and the evidence behind them",
    "paragraphs": [
      "DreamZero jointly generates video and actions and is evaluated as a policy. WorldPlanner combines an action-conditioned visual model with Monte Carlo tree search and model predictive control. Both report real-robot experiments, but they do not use the same action-selection mechanism.",
      "NVIDIA’s Cosmos-Predict is broader it is a physical-world prediction platform that can support synthetic data and downstream world-model workflows. It should not be described as a complete robot controller without the policy, action interface and selection loop built around it. Recent projects such as MotuBrain and OSCAR extend action-conditioned prediction toward embodied control and cross-embodiment transfer, but their benchmarks remain too different for a single ranking."
    ],
    "sources": [
      {
        "label": "1X World Model",
        "url": "https://www.1x.tech/discover/world-model-self-learning"
      },
      {
        "label": "DreamZero",
        "url": "https://arxiv.org/abs/2602.15922"
      },
      {
        "label": "WorldPlanner",
        "url": "https://arxiv.org/abs/2511.03077"
      },
      {
        "label": "Cosmos-Predict",
        "url": "https://research.nvidia.com/labs/cosmos-lab/cosmos-predict1/"
      }
    ]
  },
  {
    "heading": "Evidence from real robots",
    "paragraphs": [
      "Real-robot evaluation should state the hardware, action space, task horizon, number of trials, human resets and whether generated rollouts run online. A model trained from robot video can predict familiar interactions while failing on a new object mass or camera position. Results on one gripper do not establish that the same latent dynamics transfer to a humanoid hand.",
      "Simulation results are useful for controlled ablations and large trial counts, but they do not validate real contact. Lab experiments usually involve bounded workspaces, selected objects and short tasks. Reports rarely test weeks of drift, sensor degradation or repeated disturbances. The evidence is therefore strongest for short-horizon planning assistance, not unrestricted physical forecasting."
    ]
  },
  {
    "heading": "Can a generated future safely control a real robot?",
    "paragraphs": [
      "A generated future can support control when the action interface is grounded, prediction errors are monitored and conventional safety layers constrain execution. It cannot be treated as a guarantee. Video models may hallucinate that a gripper passed through an occluded object, preserve an item that should have fallen or smooth over a collision. Contacts, friction and deformable materials are especially difficult because small geometric errors change the outcome.",
      "Safe use requires short replanning horizons, uncertainty thresholds, collision checking, force limits and fallback behavior when prediction and observation diverge. Multiple rollouts increase coverage but also computation. If the planner needs seconds to compare futures, it may be unsuitable for dynamic interaction. A predictive image is evidence for an action choice only within the distribution and timing regime where the model was validated."
    ]
  },
  {
    "heading": "Failure modes and open problems",
    "paragraphs": [
      "Temporal inconsistency causes objects to appear, disappear or change identity across frames. Occlusion hides whether contact occurred. Latent models may discard small but safety-critical variables. Reward predictors can prefer visually neat outcomes over physically stable ones. Errors accumulate when a predicted state is fed back into the next prediction rather than corrected with a real observation.",
      "Open problems include calibrated uncertainty, long-horizon memory, force-aware prediction, deformable-object dynamics and efficient search. Another unresolved issue is causal identifiability observational data may show two events together without revealing which action caused the transition. Interventions and diverse action data are needed to learn controllable dynamics rather than visual correlation."
    ]
  },
  {
    "heading": "Practical applications",
    "paragraphs": [
      "Near-term applications include ranking grasp candidates, anticipating collisions, selecting among short manipulation plans, generating training data and checking whether a task is progressing. In a digital twin, a learned model can complement an engineered simulator by modeling residual effects that are difficult to specify analytically.",
      "Experimental applications include long-horizon household planning, humanoid whole-body prediction and open-world visual navigation. These remain research settings because failure cost rises with contact and task duration. Deployment should keep the predictive model inside a monitored architecture rather than granting unconstrained authority over motors."
    ]
  }
],
    "limitations": [
      "The phrase world action model is not standardized, and projects differ in whether they predict pixels, latent states, rewards or action-conditioned trajectories.",
      "Visual fidelity is not a validated measure of physical accuracy.",
      "Several recent systems report results only through author-controlled benchmarks and videos.",
      "Compute cost, online rollout count and end-to-end planning latency are often incompletely disclosed.",
      "Long-horizon real-robot trials with calibrated uncertainty and independent replication remain scarce."
    ],
    "conclusion": [],
    "faqs": [
  {
    "question": "What is a world action model?",
    "answer": "The term covers multiple architectures. An action-conditioned model predicts the consequences of supplied actions; a joint video-action model such as DreamZero predicts actions alongside future observations. Read the project’s control path to determine whether it uses search, direct policy inference or both."
  },
  {
    "question": "How is a world action model different from a video generator?",
    "answer": "A video generator may produce plausible motion without an executable robot-action interface. Robotics world action models connect predicted observations to actions, either as inputs to a predictive model or as joint outputs. Visual quality alone does not establish control accuracy."
  },
  {
    "question": "Can world models directly control a robot?",
    "answer": "Some systems generate robot actions as a policy, as DreamZero does; others supply predictions to a planner that searches over candidate actions. Both require a compatible controller, state estimation and safety constraints. Model predictive control is one approach, not a requirement for every world action model."
  },
  {
    "question": "What is model predictive control?",
    "answer": "Model predictive control repeatedly predicts the result of several candidate action sequences, selects the best sequence, executes only its first part and then replans from a new observation. This receding-horizon design limits accumulated prediction error. Its practical performance depends on model accuracy, optimization speed, horizon length and the number of candidates evaluated."
  },
  {
    "question": "Why are contact and friction difficult for generated futures?",
    "answer": "Contact outcomes can change abruptly with millimeters of pose error, surface compliance, friction and force. RGB video does not directly measure all of these variables. A model may generate a smooth, believable interaction while missing slip, jamming or unstable support. Tactile sensing and force-aware state representations can reduce, but not eliminate, this ambiguity."
  },
  {
    "question": "Are world action models the same as simulators?",
    "answer": "No. A simulator applies an explicit physics and rendering model to a defined state and action. A world action model learns transition patterns from data and may operate in pixels or latent space. Learned models can capture residual effects missing from simulation, while simulators expose controllable parameters and constraints. Hybrid systems often use both."
  }
],
    "methodology": [
  "DreamZero, WorldPlanner, Motubrain and OSCAR were checked against their research papers on September 29, 2026 to correct the control-path classification. Other availability entries remain historical and require a versioned release review. No cross-paper performance ranking is made.",
  "The October 11, 2026 FAQ correction follows DreamZero’s primary abstract, which describes joint video/action prediction and closed-loop policy control."
],
    "sourceLinks": [
      {
        "label": "1X Technologies",
        "organization": "1X Technologies",
        "documentTitle": "World Model Self-Learning from Robot Experience",
        "date": "2025 · accessed July 11, 2026",
        "url": "https://www.1x.tech/discover/world-model-self-learning"
      },
      {
        "label": "1X Technologies",
        "organization": "1X Technologies",
        "documentTitle": "1X World Model Technical Report",
        "date": "2025 · accessed July 11, 2026",
        "url": "https://www.1x.tech/1x-world-model.pdf"
      },
      {
        "label": "DreamZero authors",
        "organization": "Research collaboration",
        "documentTitle": "DreamZero World Action Models for Robot Learning",
        "date": "February 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2602.15922"
      },
      {
        "label": "WorldPlanner authors",
        "organization": "Research collaboration",
        "documentTitle": "WorldPlanner",
        "date": "November 2025 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2511.03077"
      },
      {
        "label": "NVIDIA Research",
        "organization": "NVIDIA",
        "documentTitle": "Cosmos-Predict1",
        "date": "2025 · accessed July 11, 2026",
        "url": "https://research.nvidia.com/labs/cosmos-lab/cosmos-predict1/"
      },
      {
        "label": "MotuBrain authors",
        "organization": "Research collaboration",
        "documentTitle": "MotuBrain",
        "date": "April 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2604.27792"
      },
      {
        "label": "OSCAR authors",
        "organization": "Research collaboration",
        "documentTitle": "OSCAR",
        "date": "June 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2606.04463"
      },
      {
        "label": "World Action Models survey team",
        "organization": "Research collaboration",
        "documentTitle": "World Action Models project map",
        "date": "Accessed July 11, 2026",
        "url": "https://world-action-models.github.io/"
      }
    ],
    "internalLinks": [
      {
        "label": "Synthetic robotics data and simulation fidelity",
        "path": "/robotics-news/synthetic-robotics-data"
      },
      {
        "label": "Real2Sim2Real pipelines for digital twins and transfer",
        "path": "/robotics-news/real2sim2real"
      },
      {
        "label": "Safety architectures for learned physical control",
        "path": "/robotics-news/physical-ai-safety-architectures"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot action-conditioned rollout graphic with three candidate futures",
        "alt": "Robot observes a scene, simulates three action-conditioned futures and selects one action",
        "source": "TechniaHQRobot original based on cited papers",
        "sourceUrl": "https://world-action-models.github.io/"
      },
      {
        "type": "Secondary media",
        "caption": "Official 1X World Model future-prediction example",
        "alt": "NEO first-person observation beside generated future frames conditioned on action",
        "source": "1X Technologies",
        "sourceUrl": "https://www.1x.tech/discover/world-model-self-learning"
      },
      {
        "type": "Secondary media",
        "caption": "Cosmos-Predict physical-world generation example from NVIDIA",
        "alt": "Sequence of predicted physical-world frames from the official Cosmos-Predict page",
        "source": "NVIDIA Research",
        "sourceUrl": "https://research.nvidia.com/labs/cosmos-lab/cosmos-predict1/"
      },
      {
        "type": "Original chart",
        "caption": "Prediction horizon versus physical uncertainty",
        "alt": "Graph showing compounding uncertainty as rollout horizon increases",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/abs/2511.03077"
      },
      {
        "type": "Architecture",
        "caption": "MPC loop separating action proposal, world prediction, scoring, execution and sensor correction",
        "alt": "Block diagram of a receding-horizon robot controller using a learned world model",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/abs/2602.15922"
      }
    ],
    "structuredDataSummary": [
      "Article schema describes this technical guide and its canonical URL.",
      "FAQPage schema mirrors the six visible world-model questions.",
      "BreadcrumbList schema uses Home, Robotics News and World Action Models."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "Included systems explicitly use action-conditioned prediction or an equivalent predictive-control formulation.",
        "The article distinguishes video generation, learned dynamics, planning, policy and simulation.",
        "Company and author claims are attributed rather than presented as independent validation."
      ],
      "unconfirmed": [
        "Comparable online planning latency, rollout counts and energy cost are not reported across projects.",
        "Long-horizon safety and calibrated uncertainty have not been independently demonstrated at deployment scale.",
        "Several weights, datasets and failure logs remain unavailable."
      ],
      "fastChanging": [
        "World action model terminology and project releases are evolving quickly.",
        "New 2026 papers may revise access status or add real-robot evidence."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-08-15",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "whole-body-loco-manipulation",
    "slug": "whole-body-loco-manipulation",
    "path": "/robotics-news/whole-body-loco-manipulation",
    "title": "Whole-Body Loco-Manipulation",
    "headline": "Whole-Body Loco-Manipulation Walking, Balance and Manipulation as One Control Problem",
    "seoTitle": "Whole-Body Loco-Manipulation for Modern Humanoid Robots",
    "description": "How humanoids coordinate feet, center of mass, arms and contact forces during real loco-manipulation, with systems, evidence, failures and safety limits.",
    "primaryKeyword": "whole-body loco-manipulation",
    "keywords": [
      "humanoid loco-manipulation",
      "whole-body control",
      "humanoid balance control",
      "floating-base dynamics",
      "contact planning",
      "model predictive control humanoid",
      "reinforcement learning locomotion",
      "bimanual walking manipulation",
      "HOVER humanoid",
      "WholeBodyVLA"
    ],
    "searchQuestions": [
      "What is whole-body loco-manipulation?",
      "How do humanoids manipulate objects while walking?",
      "What is floating-base control?",
      "Why does arm motion affect humanoid balance?",
      "Can humanoids recover while carrying objects?"
    ],
    "longTailKeywords": [
      "whole body control for humanoid manipulation while walking",
      "humanoid center of mass and contact planning explained",
      "reinforcement learning for loco manipulation on real humanoids",
      "difference between mobile manipulation and loco manipulation"
    ],
    "entities": [
      "HOVER",
      "WholeBodyVLA",
      "DemoHLM",
      "HOMIE",
      "Helix 02",
      "HumanoidMimicGen"
    ],
    "imageAlt": "Humanoid carrying an object while coordinating foot contacts, center of mass and arm forces",
    "introduction": [
      "A humanoid that lifts a box changes its own balance before it takes a single step. The payload moves the combined center of mass, the arms create reaction forces and each foot contact limits which accelerations are physically possible. Treating walking and manipulation as separate modules can therefore produce a stable gait that fails as soon as the robot reaches, pushes or carries.",
      "Whole-body loco-manipulation treats locomotion, balance and object interaction as one coupled control problem. This article explains floating-base dynamics, contact planning, center-of-mass regulation, inverse dynamics, model predictive control and reinforcement learning. It examines evidence from real humanoids and separates autonomous policies from teleoperation and motion imitation. Concrete tasks include carrying loads, opening doors, moving containers, reaching toward the floor, pushing carts and manipulating while recovering from disturbances."
    ],
    "keyFindings": [
      "The arms, payload, torso and feet share one momentum budget; manipulation can destabilize locomotion even when each subsystem works alone.",
      "Whole-body controllers enforce contact, torque and balance constraints, while learned policies can improve adaptation and task coverage.",
      "Teleoperated whole-body demonstrations prove hardware coordination and data collection, not autonomous task execution.",
      "Reported success depends strongly on terrain, payload, sequence length, external support and whether falls or resets are counted.",
      "Real deployment needs recovery, collision limits and safe failure states, not only a successful nominal trajectory."
    ],
    "comparisonTables": [
      {
        "id": "loco-manipulation-systems",
        "title": "Verified whole-body loco-manipulation systems",
        "columns": [
          "System",
          "Robot",
          "Task evidence",
          "Control approach",
          "Real or simulation",
          "Autonomy classification",
          "Main limitation"
        ],
        "rows": [
          [
            "HOVER",
            "Humanoid platforms in NVIDIA research",
            "Whole-body motion tracking and control",
            "Hierarchical policy with learned control",
            "Simulation and reported real transfer",
            "Autonomous policy execution after training",
            "Task-level manipulation evidence is narrower than motion tracking"
          ],
          [
            "WholeBodyVLA",
            "Humanoid platform reported by authors",
            "Language-conditioned whole-body tasks",
            "VLA plus whole-body action representation",
            "Reported real robot",
            "Supervised autonomous in experiments",
            "Recent system with limited independent replication"
          ],
          [
            "DemoHLM",
            "Humanoid platform reported by authors",
            "Demonstration-driven loco-manipulation",
            "Human demonstration learning and policy adaptation",
            "Reported real robot",
            "Autonomous execution from demonstrations",
            "Training and test distributions remain controlled"
          ],
          [
            "HOMIE",
            "Humanoid robot",
            "Real-time whole-body teleoperation",
            "Human motion retargeting and stabilization",
            "Real robot",
            "Teleoperated",
            "Does not establish autonomous task planning"
          ],
          [
            "Helix 02",
            "Figure humanoid",
            "Long mobile manipulation sequence",
            "Company VLA and whole-body control stack",
            "Real robot company demo",
            "Autonomy claimed by company; supervision details limited",
            "No independent protocol or full failure log"
          ],
          [
            "HumanoidMimicGen",
            "Humanoid platforms reported by authors",
            "Generated whole-body manipulation demonstrations",
            "Data generation and imitation learning",
            "Simulation and reported transfer",
            "Policy execution after synthetic demonstration generation",
            "Transfer quality depends on reconstruction and contact fidelity"
          ]
        ],
        "description": "Systems are compared by demonstrated role, not by a single success score. Teleoperation, imitation and autonomous execution are labeled separately.",
        "note": "Payload, speed and trial counts are not shown where authors did not publish directly comparable values."
      }
    ],
    "sections": [
      {
        "heading": "Definition why locomotion and manipulation are coupled",
        "paragraphs": [
          "A legged robot has a floating base its torso is not bolted to the world, so every arm or leg acceleration creates reaction forces that must be balanced through contacts. Loco-manipulation combines base movement and physical interaction with objects. Whole-body loco-manipulation additionally coordinates all available joints, contacts and momentum rather than treating the legs as transport for an independent arm.",
          "A wheeled mobile manipulator also couples base and arm motion, but a humanoid faces changing foot contacts and a smaller support polygon. A stationary dual-arm task is manipulation, not loco-manipulation. Walking to a table and stopping before reaching is sequential locomotion and manipulation; carrying, pushing or reaching while stepping is the coupled case."
        ]
      },
      {
        "heading": "How whole-body control works",
        "paragraphs": [
          "State estimation fuses joint encoders, inertial measurements, foot contact estimates and vision. A planner chooses footholds, object contacts and task goals. The controller regulates center of mass, torso orientation, swing feet, hands and contact forces under joint, torque and friction constraints. Inverse dynamics computes joint torques or accelerations consistent with the full rigid-body model.",
          "Model predictive control optimizes several future steps and contacts, then replans as the robot moves. Reinforcement-learning policies can learn residual control, contact transitions or full-body behaviors from simulation. Hybrid systems often use a learned policy for motion generation and a model-based layer for stabilization, torque limits and collision constraints."
        ],
        "subsections": [
          {
            "heading": "Center of mass, zero moment point and support",
            "paragraphs": [
              "The center of mass summarizes mass distribution, but balance also depends on momentum and contact forces. The zero moment point is useful for planned walking on approximately flat support, while dynamic motions may require capture-point or centroidal-momentum reasoning. Reaching with both arms can shift the center of mass beyond the current support polygon unless the robot steps, leans or creates an additional hand contact."
            ]
          },
          {
            "heading": "Contact planning and force control",
            "paragraphs": [
              "A task may involve feet, hands, forearms and the manipulated object as contacts. The planner must decide when each contact is created or released. Force control matters for doors, tools and carts because pose control alone can generate excessive force when geometry is uncertain. Contact estimates also fail under slip, soft surfaces or delayed sensing."
            ]
          }
        ]
      },
      {
        "heading": "Key systems and what their demonstrations prove",
        "paragraphs": [
          "HOVER studies robust whole-body control and motion tracking, providing a reusable layer for humanoid behaviors. WholeBodyVLA and DemoHLM move toward instruction- or demonstration-conditioned loco-manipulation, but public evidence remains tied to specific robots and task sets. HOMIE demonstrates high-quality whole-body teleoperation and is valuable for collecting coordinated data; the human still supplies task decisions and motion intent.",
          "Figure’s Helix 02 demonstration showed a company-reported multi-minute mobile manipulation sequence on a humanoid. That evidence is more informative than a cut of isolated motions, but it is not an independent deployment study and does not disclose every intervention, reset or failure. HumanoidMimicGen explores scalable demonstration generation, with transfer quality bounded by the fidelity of retargeted motion and simulated contacts."
        ],
        "sources": [
          {
            "label": "HOVER",
            "url": "https://research.nvidia.com/labs/lpr/publication/he2025hover/"
          },
          {
            "label": "WholeBodyVLA",
            "url": "https://github.com/OpenDriveLab/WholebodyVLA"
          },
          {
            "label": "HOMIE",
            "url": "https://www.roboticsproceedings.org/rss21/p070.html"
          },
          {
            "label": "Helix 02",
            "url": "https://www.figure.ai/news/helix-02"
          }
        ]
      },
      {
        "heading": "Evidence from real robots",
        "paragraphs": [
          "A complete evaluation should report robot mass, terrain, payload, speed, task duration, number of trials and perturbations. Carrying a light box on a flat laboratory floor is different from moving an unknown load across a ramp. Opening a known door with a prepositioned handle is different from locating and manipulating doors of varied geometry.",
          "Simulation enables thousands of falls and contact variations without damaging hardware, but transfer exposes actuator delay, joint friction, structural compliance and imperfect state estimation. Real tests are strongest when they include pushes, object shifts, missed contacts and recovery rather than replaying one successful trajectory. Human supervision should be reported even when the learned policy generates every nominal action."
        ]
      },
      {
        "heading": "Why walking and manipulation cannot be evaluated separately",
        "paragraphs": [
          "A locomotion benchmark may reward speed while ignoring how the gait behaves with arms extended or a payload held asymmetrically. A manipulation benchmark may assume a fixed base and stable camera. Combining two high-scoring modules can fail because the arm planner requests motion that violates balance constraints or the gait moves the camera faster than the perception system can track.",
          "The relevant unit is the complete task. For carrying, measure pickup, transition to walking, transport, placement and recovery. For a door, measure approach, handle contact, force application, stepping through and avoiding collision. Success should include the absence of unsafe contact, not only task completion."
        ]
      },
      {
        "heading": "Failure modes",
        "paragraphs": [
          "Foot-slip or a false contact estimate can invalidate the planned support. Payload mass may be wrong, shifting momentum. An arm collision can rotate the torso and trigger a step. Perception delay causes the hand target to move relative to the body. Learned policies can exploit simulation artifacts, while model-based controllers can fail when contact geometry differs from the model.",
          "Recovery may be blocked by the object itself a robot holding a crate cannot freely swing both arms, and dropping the load may be unsafe. Controllers therefore need priorities and fallback states, such as placing the object down, widening stance, releasing contact or stopping before joint or force limits are exceeded."
        ]
      },
      {
        "heading": "Practical applications and maturity",
        "paragraphs": [
          "Credible applications include moving standardized containers, tending workstations separated by short walks, pushing known carts and handling light materials in controlled facilities. These tasks can use mapped floors, restricted human access and predefined object interfaces while still benefiting from a humanoid’s reach and ability to use human-designed spaces.",
          "Irregular homes, construction sites and emergency response remain experimental. They combine unknown terrain, moving people, deformable objects and high consequence failures. For those settings, whole-body capability must be paired with validated perception, safe contact forces, protective hardware and a recovery strategy that has been tested under realistic disturbances."
        ]
      }
    ],
    "limitations": [
      "Published systems use different robots, payloads, terrains and task definitions, preventing an absolute ranking.",
      "Many videos do not disclose failed trials, resets, remote supervision or safety personnel.",
      "Whole-body teleoperation is often confused with autonomy even though a human supplies continuous intent.",
      "Simulation success may rely on actuator and contact models that do not match the physical robot.",
      "Long-duration wear, thermal limits and repeated fall recovery are rarely included in academic evaluations."
    ],
    "conclusion": [
      "Whole-body loco-manipulation is the control problem that appears when a humanoid must move and physically interact at the same time. Foot placement, center of mass, arm motion, payload and contact forces cannot be optimized independently because each changes the robot’s momentum and feasible support.",
      "Research systems now demonstrate increasingly complex sequences on real hardware, using model-based control, reinforcement learning, imitation and multimodal policies. The evidence is still task- and robot-specific, and teleoperation remains important for data collection and difficult demonstrations. A mature evaluation should cover the complete task, disturbances, recovery, force limits and the conditions under which human intervention occurs. Walking speed and grasp success alone do not describe whether a humanoid can safely carry, push or manipulate while remaining balanced."
    ],
    "faqs": [
      {
        "question": "What is whole-body loco-manipulation?",
        "answer": "Whole-body loco-manipulation is coordinated control of locomotion, balance and object interaction using the robot’s full body. The controller considers foot contacts, torso motion, arms, hands and payload together. It differs from walking to a fixed pose and then manipulating with a stationary base because the motions and forces overlap in time."
      },
      {
        "question": "Why does moving an arm affect humanoid balance?",
        "answer": "The torso of a humanoid is a floating base. Accelerating an arm produces reaction momentum, and holding a payload shifts the combined center of mass. The feet must generate compensating contact forces or the robot must step. Fast reaches, long lever arms and asymmetric loads therefore change balance even when the feet do not move."
      },
      {
        "question": "Is whole-body teleoperation autonomous?",
        "answer": "No. Whole-body teleoperation maps a human operator’s motion or commands onto the robot while stabilization software keeps the machine feasible. It proves that hardware and controllers can coordinate complex motion and can produce useful training data. Autonomous execution requires the robot to perceive, select and complete the task without continuous human motion input."
      },
      {
        "question": "What is floating-base dynamics?",
        "answer": "Floating-base dynamics models a robot whose main body is not fixed to the ground. The base position and orientation change according to joint motion, gravity and external contacts. Humanoids need this model because feet alternately create and release support, and arm forces can move the torso or change the required ground reaction forces."
      },
      {
        "question": "Can reinforcement learning replace whole-body control?",
        "answer": "Reinforcement learning can produce effective whole-body policies, especially after large-scale simulation training, but deployed systems still need state estimation, actuator limits, collision checks and emergency behavior. Many architectures combine learned motion generation with model-based stabilization or safety constraints. The balance depends on task complexity, hardware reliability and the evidence required for certification."
      },
      {
        "question": "How should loco-manipulation be benchmarked?",
        "answer": "A benchmark should measure the complete sequence, including approach, contact, transport or manipulation, release and recovery. It should report terrain, payload, duration, trials, failures, human intervention and safety violations. Separate walking and grasp scores are insufficient because the difficult part is maintaining control when the two behaviors interact."
      }
    ],
    "methodology": [
      "The review separates autonomous policy execution, demonstration learning, simulation transfer and teleoperation. Systems were included when the public material involved coordinated legged movement and manipulation, not merely a walking robot with independently posed arms.",
      "Numeric payloads and success rates are omitted when protocols are incompatible or not publicly reported. Company demonstrations are labeled as company-reported and are not treated as independent deployment evidence."
    ],
    "sourceLinks": [
      {
        "label": "He et al.",
        "organization": "NVIDIA Research",
        "documentTitle": "HOVER Versatile Neural Whole-Body Controller for Humanoid Robots",
        "date": "2025 · accessed July 11, 2026",
        "url": "https://research.nvidia.com/labs/lpr/publication/he2025hover/"
      },
      {
        "label": "OpenDriveLab contributors",
        "organization": "OpenDriveLab",
        "documentTitle": "WholeBodyVLA official repository",
        "date": "Accessed July 11, 2026",
        "url": "https://github.com/OpenDriveLab/WholebodyVLA"
      },
      {
        "label": "DemoHLM authors",
        "organization": "Research collaboration",
        "documentTitle": "DemoHLM Learning Humanoid Loco-Manipulation from Demonstrations",
        "date": "October 2025 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2510.11258"
      },
      {
        "label": "HOMIE authors",
        "organization": "Robotics Science and Systems",
        "documentTitle": "HOMIE Humanoid Whole-Body Teleoperation",
        "date": "RSS 2025 · accessed July 11, 2026",
        "url": "https://www.roboticsproceedings.org/rss21/p070.html"
      },
      {
        "label": "Task and Motion Planning authors",
        "organization": "Research collaboration",
        "documentTitle": "Task and Motion Planning for Humanoid Loco-Manipulation",
        "date": "August 2025 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2508.14099"
      },
      {
        "label": "Figure AI",
        "organization": "Figure AI",
        "documentTitle": "Helix 02 Full-Body Autonomy",
        "date": "January 27, 2026 · accessed July 11, 2026",
        "url": "https://www.figure.ai/news/helix-02"
      },
      {
        "label": "HumanoidMimicGen authors",
        "organization": "Research collaboration",
        "documentTitle": "HumanoidMimicGen",
        "date": "May 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2605.27724"
      }
    ],
    "internalLinks": [
      {
        "label": "Safety architectures for high-force Physical AI systems",
        "path": "/robotics-news/physical-ai-safety-architectures"
      },
      {
        "label": "Vision-language-action models for robot control",
        "path": "/robotics-news/vision-language-action-models"
      },
      {
        "label": "Real2Sim2Real training for contact-rich humanoid tasks",
        "path": "/robotics-news/real2sim2real"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot force-and-contact diagram for a humanoid carrying a box while stepping",
        "alt": "Humanoid carrying a box with arrows for foot forces, payload, center of mass and momentum",
        "source": "TechniaHQRobot original based on whole-body control literature",
        "sourceUrl": "https://research.nvidia.com/labs/lpr/publication/he2025hover/"
      },
      {
        "type": "Secondary media",
        "caption": "Official HOVER whole-body control demonstration",
        "alt": "Humanoid tracking a dynamic whole-body motion using the HOVER controller",
        "source": "NVIDIA Research",
        "sourceUrl": "https://research.nvidia.com/labs/lpr/publication/he2025hover/"
      },
      {
        "type": "Secondary media",
        "caption": "HOMIE whole-body teleoperation experiment",
        "alt": "Humanoid mirroring an operator during coordinated arm and leg motion",
        "source": "RSS 2025 authors",
        "sourceUrl": "https://www.roboticsproceedings.org/rss21/p070.html"
      },
      {
        "type": "Original chart",
        "caption": "Maturity matrix for walking, carrying, pushing, door opening and disturbance recovery",
        "alt": "Matrix comparing task complexity, contact uncertainty and public real-robot evidence",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/abs/2510.11258"
      },
      {
        "type": "Architecture",
        "caption": "Whole-body control stack from perception and task goal to contacts, MPC, inverse dynamics and actuator safety",
        "alt": "Block diagram of a humanoid loco-manipulation controller with feedback and fallback states",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/abs/2508.14099"
      }
    ],
    "structuredDataSummary": [
      "Article schema represents the technical guide and its publication metadata.",
      "FAQPage schema contains the six visible whole-body control questions.",
      "BreadcrumbList schema follows Home, Robotics News and Whole-Body Loco-Manipulation."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "Each included example coordinates locomotion or floating-base motion with manipulation.",
        "Teleoperation is explicitly separated from autonomous policy execution.",
        "Simulation-only and real-robot evidence are identified."
      ],
      "unconfirmed": [
        "Independent success rates and long-duration reliability are unavailable for several recent systems.",
        "Complete intervention and failure logs for company demonstrations are not public.",
        "Payload and speed values are not comparable across different robots and protocols."
      ],
      "fastChanging": [
        "Recent 2026 whole-body systems may release code, datasets or additional trials after verification.",
        "Company autonomy descriptions may change as deployment programs mature."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-07-11",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "tactile-language-action-models",
    "slug": "tactile-language-action-models",
    "path": "/robotics-news/tactile-language-action-models",
    "title": "Tactile-Language-Action Models",
    "headline": "Tactile-Language-Action Models Using Contact to Guide Robot Actions",
    "seoTitle": "Tactile-Language-Action Models for Robot Control Systems",
    "description": "How robot policies fuse language, vision and touch for slip recovery, insertion and contact-rich manipulation, with verified models, sensor limits and evidence.",
    "primaryKeyword": "tactile-language-action models",
    "keywords": [
      "tactile-language-action model",
      "tactile VLA",
      "robot touch sensing",
      "GelSight robot learning",
      "slip detection robot",
      "contact-rich manipulation",
      "force torque sensing",
      "vision tactile language action",
      "Tactile-VLA",
      "OmniVTLA"
    ],
    "searchQuestions": [
      "What is a tactile-language-action model?",
      "Why do robots need touch if they have cameras?",
      "How is tactile data represented for AI models?",
      "Which tactile VLA models exist?",
      "Can tactile models recover from slip?"
    ],
    "longTailKeywords": [
      "vision tactile language action models for robot manipulation",
      "how GelSight data is used in robot policies",
      "tactile feedback for insertion and cable handling",
      "difference between tactile sensor and tactile action model"
    ],
    "entities": [
      "Tactile-VLA",
      "VTLA",
      "OmniVTLA",
      "VLA-Touch",
      "Dream-Tac",
      "TAP-VLA",
      "Octopi 1.5"
    ],
    "imageAlt": "Robot fingers with tactile arrays sending pressure and contact signals into a language-conditioned action policy",
    "introduction": [
      "A camera can show that a gripper surrounds an object while missing the fact that the object is already slipping. During insertion, cable routing or fragile grasping, the decisive state may exist only at the contact patch. Tactile-language-action models add touch to the visual and linguistic inputs of a robot policy, allowing actions to depend on pressure, force, deformation or slip rather than image appearance alone.",
      "This article defines the category narrowly a tactile sensor by itself is not a TLA model, and a tactile classifier is not an action policy. A qualifying system must use language, tactile observations and an action output. The guide explains force-torque sensors, fingertip arrays, optical tactile images, pressure maps and torque estimates, then follows their encoding and fusion with vision and text. It compares recent systems, real-robot evidence, contact failure modes and the limits of current datasets."
    ],
    "keyFindings": [
      "Touch becomes most valuable after visual occlusion or contact, when geometry alone cannot reveal force, slip or seating.",
      "Tactile data may be encoded as images, pressure maps, time series, force vectors or learned tokens; these formats are not interchangeable.",
      "A tactile sensor or tactile-language model is not a TLA policy unless it produces robot actions.",
      "Most published systems cover narrow contact-rich tasks on one gripper or arm, not broad cross-hand generalization.",
      "Sensor calibration, wear and mounting can shift the data distribution as much as changing the manipulated object."
    ],
    "comparisonTables": [
      {
        "id": "tla-model-comparison",
        "title": "Verified tactile-language-action research systems",
        "columns": [
          "System",
          "Year",
          "Tactile input",
          "Action output",
          "Tasks",
          "Real robot evidence",
          "Public status"
        ],
        "rows": [
          [
            "Tactile-VLA",
            "2025",
            "Tactile images or embeddings with vision and language",
            "Robot manipulation actions",
            "Contact-rich manipulation reported by authors",
            "Yes, controlled experiments",
            "Project page and paper"
          ],
          [
            "VTLA",
            "2025",
            "Vision and tactile streams conditioned by language",
            "Action sequence",
            "Grasping and contact tasks",
            "Yes, author-reported",
            "Paper; release details limited"
          ],
          [
            "OmniVTLA",
            "2025",
            "Multiple visual-tactile modalities",
            "General manipulation actions",
            "Multi-task contact manipulation",
            "Yes, reported",
            "Paper; artifacts vary"
          ],
          [
            "VLA-Touch",
            "2025",
            "Touch features fused with VLA observations",
            "Action chunks",
            "Manipulation requiring contact feedback",
            "Yes, controlled setting",
            "Paper; weights not broadly available"
          ],
          [
            "Dream-Tac",
            "2026",
            "Tactile observations and predictive representations",
            "Contact-aware actions",
            "Contact-rich manipulation",
            "Author-reported real robot",
            "Recent paper; limited replication"
          ],
          [
            "TAP-VLA",
            "2026",
            "Tactile tokens aligned with vision and language",
            "Policy actions",
            "Fine manipulation and recovery",
            "Author-reported real robot",
            "Recent paper; access incomplete"
          ]
        ],
        "description": "Only systems whose public description includes language, tactile input and action generation are listed. Tactile encoders without control output are excluded.",
        "note": "Tasks and success rates are not ranked because sensors, grippers, objects and contact protocols differ."
      }
    ],
    "sections": [
      {
        "heading": "Definition what qualifies as a tactile-language-action model",
        "paragraphs": [
          "A tactile-language-action model receives a language goal and tactile observations, usually together with camera images and robot state, and produces an executable action. The tactile signal may come from fingertip arrays, optical sensors, wrist force-torque sensors, joint torque estimation or another contact-sensitive modality. Language specifies the task or desired property, while touch updates the policy when physical interaction begins.",
          "The category does not include a pressure sensor connected to a hand-coded threshold, a model that names textures without moving the robot or a vision-language model that merely describes tactile images. Those systems can be components. The defining feature is closed-loop action generation conditioned on both language and contact information."
        ]
      },
      {
        "heading": "Tactile sensors and what they measure",
        "paragraphs": [
          "Six-axis force-torque sensors measure net forces and moments at the wrist or joint but do not localize contact across a fingertip. Capacitive and piezoresistive arrays produce spatial pressure maps. Optical sensors such as GelSight image the deformation of an elastomer, revealing fine geometry, shear and incipient slip. Joint torque estimates provide broad contact cues without a dedicated skin, although transmission friction and model error reduce precision.",
          "Other modalities include temperature, vibration and acoustic contact. Their usefulness depends on the task. Texture recognition may need high-frequency vibration, while insertion needs pose-sensitive pressure and force. A policy trained on one sensor geometry cannot assume that values from another sensor mean the same thing."
        ]
      },
      {
        "heading": "How tactile, visual and language streams are fused",
        "paragraphs": [
          "Tactile images can pass through a visual encoder, while pressure arrays and force vectors use temporal convolution, recurrent networks or transformer tokenization. The model aligns these features with image tokens, language tokens and proprioception. Fusion may happen in one multimodal transformer or through separate encoders joined by cross-attention.",
          "The action decoder can predict continuous commands, action chunks or diffusion trajectories. Closed-loop use requires tactile updates after contact, often at a higher frequency than the large multimodal policy can run. Some architectures therefore separate a slower language-conditioned planner from a faster tactile reflex for slip or force regulation."
        ],
        "subsections": [
          {
            "heading": "Why calibration and time alignment matter",
            "paragraphs": [
              "Contact signals are time-sensitive. A pressure peak aligned to the wrong video frame can teach the policy that an action caused contact before it actually did. Sensor drift, elastomer wear and temperature change also alter readings. Useful datasets need timestamps, calibration records and synchronized robot state, not only tactile files stored beside videos."
            ]
          },
          {
            "heading": "Tactile tokens versus continuous forces",
            "paragraphs": [
              "Tokenization gives a transformer a common sequence format but can discard subtle force magnitude. Continuous force heads preserve numerical detail but require scale normalization across sensors and hands. Hybrid systems may tokenize contact events while retaining continuous force for low-level control."
            ]
          }
        ]
      },
      {
        "heading": "Key systems and contributions",
        "paragraphs": [
          "Tactile-VLA and VTLA demonstrate the central idea that touch can disambiguate actions after visual contact. OmniVTLA broadens the multimodal setup, while VLA-Touch integrates contact observations with a VLA-style policy. Dream-Tac and TAP-VLA extend predictive or token-based approaches, but their recent results remain tied to specific sensors, grippers and laboratory tasks.",
          "Octopi 1.5 is relevant as a tactile-language model for interpreting touch and language, but it should not be counted as a complete TLA controller unless paired with an action policy. This distinction prevents a perception or representation model from being credited with motor capabilities it does not directly produce."
        ],
        "sources": [
          {
            "label": "Tactile-VLA",
            "url": "https://jialeihuang.github.io/tactileVLA.github.io/"
          },
          {
            "label": "VTLA",
            "url": "https://arxiv.org/abs/2505.09577"
          },
          {
            "label": "VLA-Touch",
            "url": "https://arxiv.org/abs/2507.17294"
          },
          {
            "label": "Octopi 1.5",
            "url": "https://www.roboticsproceedings.org/rss21/p058.pdf"
          }
        ]
      },
      {
        "heading": "Why vision alone fails during contact",
        "paragraphs": [
          "Once fingers close around an object, the relevant surfaces may be occluded. RGB images cannot directly reveal normal force, shear or whether an insertion is jammed. Transparent, reflective and deformable objects further weaken visual estimates. A cable may look correctly placed while being pinched, and a plug may appear aligned while one edge is binding.",
          "Touch reveals physical consequence rather than only appearance. Slip detection can trigger a grip adjustment. A changing pressure pattern can indicate that a peg is entering at an angle. Force-torque feedback can stop an unsafe push. Vision still supplies global geometry and object identity; tactile sensing narrows uncertainty at contact."
        ]
      },
      {
        "heading": "Evidence from real robots",
        "paragraphs": [
          "Published TLA experiments commonly use one arm, one sensor family and selected objects. They provide evidence for grasp stabilization, insertion, in-hand adjustment or manipulation under visual occlusion. The strongest studies report repeated trials and compare vision-only, touch-only and fused policies under the same setup.",
          "Cross-sensor and cross-hand evidence is much weaker. A GelSight fingertip produces image-like data unlike a sparse capacitive array. Different gripper compliance changes the relationship between pressure and object motion. Results should therefore be read as task- and embodiment-specific unless the authors test sensor replacement or target-hand adaptation."
        ]
      },
      {
        "heading": "Failure modes and open problems",
        "paragraphs": [
          "A saturated sensor can hide rising force. Loose mounting creates false shear. Worn elastomer changes the image distribution. A policy can overreact to noisy contact and destabilize a grasp. Language grounding may also fail “hold gently” has no universal force value and depends on object fragility, contact area and hand geometry.",
          "Open problems include large synchronized tactile datasets, common benchmarks, sensor-agnostic representations and long-horizon contact memory. Models also need recovery policies that distinguish a recoverable slip from a jam requiring withdrawal. Safety-critical force regulation should not depend solely on a large model operating at low frequency."
        ]
      },
      {
        "heading": "Practical applications",
        "paragraphs": [
          "Credible applications include connector insertion, grasp stabilization, handling opaque or reflective objects, cable routing, packaging and delicate pick-and-place. Tactile policies are especially useful where a visual pose estimate gets the robot close and contact feedback completes the final millimeters.",
          "Broad household dexterity and unsupervised assembly remain experimental. Each new hand, sensor and material can require calibration and data. Deployment is more realistic when the tactile policy is bounded by local force limits, validated tooling and a conventional stop mechanism."
        ]
      }
    ],
    "limitations": [
      "The term tactile-language-action is recent and not used consistently across papers.",
      "Several projects publish papers but not weights, complete datasets or sensor calibration procedures.",
      "Benchmarks use different tactile hardware, contact geometry and success criteria.",
      "Real-robot evaluations are generally short, controlled and limited to one embodiment.",
      "Long-term sensor wear, replacement and recalibration are rarely evaluated."
    ],
    "conclusion": [
      "Tactile-language-action models address a real gap in robot control visual observations become incomplete at the moment physical contact matters most. By combining language goals with pressure, force, deformation or slip, these policies can adjust actions during insertion, grasping and other contact-rich tasks.",
      "The field is still narrow in evidence. Most systems are evaluated on one arm, one tactile sensor and a bounded object set, and many artifacts remain unavailable. Touch does not replace vision; it supplies local physical state that images cannot measure directly. The practical architecture today is multimodal and layered vision for global geometry, language for task intent, tactile feedback for contact and fast deterministic limits for safety. Claims of general tactile intelligence should wait for cross-hand, cross-sensor and long-duration evaluation."
    ],
    "faqs": [
      {
        "question": "What is a tactile-language-action model?",
        "answer": "A tactile-language-action model is a robot policy that uses a language instruction and tactile observations, usually with vision and proprioception, to produce actions. The tactile signal informs the policy about contact, force, pressure or slip. A tactile classifier that only names textures does not qualify unless it is connected to action generation."
      },
      {
        "question": "Why do robots need touch when they have cameras?",
        "answer": "Cameras cannot directly measure force, friction or pressure, and the contact area is often hidden by the gripper. Touch helps detect slip, jamming, seating and fragile contact after visual alignment. It is most useful during the final phase of a grasp, insertion or surface interaction, where millimeter errors can change the outcome."
      },
      {
        "question": "Is GelSight a tactile-language-action model?",
        "answer": "No. GelSight is an optical tactile sensing technology that captures deformation at a contact surface. Its images can be input to a TLA model, but the sensor does not interpret language or generate robot actions by itself. A complete system needs encoding, multimodal fusion, an action policy and closed-loop execution."
      },
      {
        "question": "How is tactile data represented in a robot model?",
        "answer": "Optical tactile sensors produce images, pressure arrays produce spatial maps and force-torque sensors produce continuous vectors over time. Models may encode these as visual features, temporal embeddings, tokens or normalized force values. The representation must preserve timing and calibration because contact events change faster than many visual-language models can infer."
      },
      {
        "question": "Can tactile models recover from object slip?",
        "answer": "They can when the sensor detects incipient slip and the controller updates grip force or pose quickly enough. Published systems demonstrate this under selected conditions, but recovery depends on object material, sensor bandwidth, hand compliance and control latency. A large multimodal model may still need a faster local reflex for reliable slip response."
      },
      {
        "question": "Are tactile VLA models open source?",
        "answer": "Availability is mixed. Several projects provide papers and project pages, while code, weights and synchronized tactile datasets are often partial or absent. Even with public weights, reproduction requires the same sensor geometry, calibration and robot action space. The official repository or model card should be checked for the exact released components."
      }
    ],
    "methodology": [
      "Inclusion required public evidence of three elements language input, tactile input and robot action output. Tactile sensing, tactile classification and language-conditioned description systems were discussed as components but excluded from the main controller comparison when they lacked action generation.",
      "Results are attributed to their authors and are not ranked across different sensors or robots. Release status, project pages and papers were checked July 11, 2026."
    ],
    "sourceLinks": [
      {
        "label": "Huang et al.",
        "organization": "Research collaboration",
        "documentTitle": "Tactile-VLA project",
        "date": "2025 · accessed July 11, 2026",
        "url": "https://jialeihuang.github.io/tactileVLA.github.io/"
      },
      {
        "label": "VTLA authors",
        "organization": "Research collaboration",
        "documentTitle": "VTLA Vision-Tactile-Language-Action Learning",
        "date": "May 2025 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2505.09577"
      },
      {
        "label": "OmniVTLA authors",
        "organization": "Research collaboration",
        "documentTitle": "OmniVTLA",
        "date": "August 2025 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2508.08706"
      },
      {
        "label": "VLA-Touch authors",
        "organization": "Research collaboration",
        "documentTitle": "VLA-Touch",
        "date": "July 2025 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2507.17294"
      },
      {
        "label": "Dream-Tac authors",
        "organization": "Research collaboration",
        "documentTitle": "Dream-Tac",
        "date": "June 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2606.08737"
      },
      {
        "label": "TAP-VLA authors",
        "organization": "Research collaboration",
        "documentTitle": "TAP-VLA",
        "date": "June 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/html/2606.29089"
      },
      {
        "label": "Octopi authors",
        "organization": "Robotics Science and Systems",
        "documentTitle": "Octopi 1.5",
        "date": "RSS 2025 · accessed July 11, 2026",
        "url": "https://www.roboticsproceedings.org/rss21/p058.pdf"
      }
    ],
    "internalLinks": [
      {
        "label": "Robot foundation models and reusable multimodal representations",
        "path": "/robotics-news/robot-foundation-models"
      },
      {
        "label": "Vision-language-action models without mandatory touch input",
        "path": "/robotics-news/vision-language-action-models"
      },
      {
        "label": "Safety architectures for force and contact control",
        "path": "/robotics-news/physical-ai-safety-architectures"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot multimodal hand diagram showing vision, language, tactile maps and action output",
        "alt": "Robot fingers with pressure maps feeding a policy that adjusts grip and wrist motion",
        "source": "TechniaHQRobot original based on cited TLA papers",
        "sourceUrl": "https://jialeihuang.github.io/tactileVLA.github.io/"
      },
      {
        "type": "Secondary media",
        "caption": "Official Tactile-VLA real-robot experiment",
        "alt": "Robot gripper using tactile observations during contact-rich manipulation",
        "source": "Tactile-VLA authors",
        "sourceUrl": "https://jialeihuang.github.io/tactileVLA.github.io/"
      },
      {
        "type": "Secondary media",
        "caption": "Octopi tactile-language representation example",
        "alt": "Optical tactile images paired with language descriptions from the official paper",
        "source": "RSS 2025 authors",
        "sourceUrl": "https://www.roboticsproceedings.org/rss21/p058.pdf"
      },
      {
        "type": "Original chart",
        "caption": "Tactile sensor comparison by spatial resolution, force information and durability",
        "alt": "Chart comparing wrist force-torque, capacitive arrays, optical tactile sensors and torque estimation",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/abs/2505.09577"
      },
      {
        "type": "Architecture",
        "caption": "Two-rate TLA controller with slow multimodal planning and fast tactile reflex",
        "alt": "Architecture diagram separating language-conditioned policy from high-frequency slip and force control",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/abs/2507.17294"
      }
    ],
    "structuredDataSummary": [
      "Article schema includes the technical headline, description, canonical URL and publication metadata.",
      "FAQPage schema is generated from the six visible tactile-robotics questions.",
      "BreadcrumbList schema follows Home, Robotics News and Tactile-Language-Action Models."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "Every model in the main table uses language, tactile observations and an action output according to its primary material.",
        "Tactile sensors and tactile interpretation models are not presented as complete policies.",
        "Real-robot evidence is identified as controlled and author-reported."
      ],
      "unconfirmed": [
        "Comparable cross-hand performance and long-term sensor durability are not published.",
        "Weights and complete datasets are unavailable for several systems.",
        "Success rates cannot be compared across different tactile hardware and task protocols."
      ],
      "fastChanging": [
        "Recent 2026 projects may release artifacts after publication.",
        "Sensor and model naming is evolving and may be standardized differently in later work."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-10-11",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "robot-foundation-models",
    "slug": "robot-foundation-models",
    "path": "/robotics-news/robot-foundation-models",
    "title": "Robot Foundation Models",
    "headline": "Robot Foundation Models What the Label Requires and Which Systems Meet It",
    "seoTitle": "Robot Foundation Models Evidence, Scale and Open Limits",
    "description": "A criteria-based guide to robot foundation models covering pre-training scale, task and embodiment diversity, adaptation, open weights, evidence and limits.",
    "primaryKeyword": "robot foundation models",
    "keywords": [
      "robot foundation model",
      "robotics foundation models",
      "generalist robot policy",
      "Open X-Embodiment",
      "Octo robot model",
      "OpenVLA",
      "pi0 robot model",
      "GR00T",
      "Gemini Robotics",
      "robot pretraining"
    ],
    "searchQuestions": [
      "What is a robot foundation model?",
      "Which robot foundation models are open source?",
      "How many robots does a foundation model support?",
      "What is the difference between a VLA and a foundation model?",
      "How are robot foundation models adapted?"
    ],
    "longTailKeywords": [
      "criteria for calling a model a robot foundation model",
      "open source robot foundation model comparison",
      "robot pretraining across tasks and embodiments",
      "foundation model versus generalist robot policy"
    ],
    "entities": [
      "Open X-Embodiment",
      "RT-X",
      "Octo",
      "OpenVLA",
      "π0",
      "π0.5",
      "GR00T N1.6",
      "Gemini Robotics",
      "SmolVLA"
    ],
    "imageAlt": "Taxonomy separating robot foundation models from visual encoders, language models, policies and world models",
    "introduction": [
      "A model trained on one arm and ten tabletop tasks may be useful, but the word foundation implies more broad pre-training, reusable representations and adaptation to downstream tasks or robots. Robotics has adopted the label faster than it has adopted a common test for whether a system deserves it.",
      "This article defines a robot foundation model using measurable axes data scale, task diversity, embodiment diversity, sensor coverage, reusable representations, transfer and access. It separates foundation models from pretrained image encoders, language models connected to tools, narrow manipulation policies, navigation systems and learned world models. The comparison covers Open X-Embodiment, Octo, OpenVLA, Physical Intelligence’s π series, NVIDIA GR00T, Gemini Robotics and compact open models. Rather than declaring a winner, it grades the strength of public evidence and identifies what remains unreported."
    ],
    "keyFindings": [
      "Scale alone is insufficient; a foundation model must support reuse or adaptation beyond its original training tasks.",
      "Task diversity and embodiment diversity are separate axes a policy can cover many tasks on one robot or several robots on a narrow task set.",
      "Open code, weights and data make adaptation claims easier to verify, but open models can still be narrow.",
      "Closed industrial models may show stronger hardware results while withholding the evidence needed for independent comparison.",
      "The term foundation model should be accompanied by the robots, tasks, data and adaptation protocol actually tested."
    ],
    "comparisonTables": [
      {
        "id": "foundation-model-evidence",
        "title": "Evidence grid for major robot model families",
        "columns": [
          "Model or dataset",
          "Scale evidence",
          "Embodiment evidence",
          "Adaptation evidence",
          "Access",
          "Classification"
        ],
        "rows": [
          [
            "Open X-Embodiment / RT-X",
            "More than one million trajectories reported",
            "22 robot embodiments reported",
            "Cross-dataset and target-task studies",
            "Dataset mixture and project artifacts public",
            "Strong dataset foundation; policy evidence depends on model"
          ],
          [
            "Octo",
            "About 800,000 episodes reported",
            "Nine training platforms reported",
            "Target-robot fine-tuning demonstrated",
            "Code and checkpoints public",
            "Strong evidence"
          ],
          [
            "OpenVLA",
            "About 970,000 robot episodes reported",
            "Trained from Open X-Embodiment mixture",
            "Fine-tuning on target robots documented",
            "Code and 7B weights public",
            "Strong evidence for open VLA pre-training"
          ],
          [
            "π0 / π0.5",
            "Large proprietary robot data mixture",
            "Multiple robot types reported",
            "Task and environment adaptation reported",
            "Code and checkpoints public; complete training data not released",
            "Strong company evidence, limited reproducibility"
          ],
          [
            "GR00T N1.6",
            "Large mixed robot and synthetic data reported",
            "Multiple humanoid embodiments targeted",
            "Embodiment conditioning and fine-tuning reported",
            "Open model release reported",
            "Strong evidence, deployment scope still bounded"
          ],
          [
            "Gemini Robotics 1.5",
            "Training scale not fully disclosed",
            "Multiple embodiments shown",
            "Transfer and reasoning claims reported",
            "Closed access",
            "Partial public evidence"
          ],
          [
            "SmolVLA",
            "Compact public training pipeline",
            "LeRobot-compatible targets",
            "Fine-tuning designed for accessible hardware",
            "Code and weights public",
            "Narrow but scalable"
          ]
        ],
        "description": "Classification reflects public evidence, not marketing language. The grid evaluates scale, diversity, adaptation and reproducibility separately.",
        "note": "Episode counts are not directly comparable because duration, sampling rate and action labeling differ."
      }
    ],
    "sections": [
      {
        "heading": "Definition minimum criteria for a robot foundation model",
        "paragraphs": [
          "A robot foundation model is pre-trained on broad robot-relevant data and designed to be reused or adapted across multiple downstream tasks, environments or embodiments. The model may output actions directly or provide representations to a downstream policy. Foundation status is stronger when performance improves after broad pre-training and when adaptation requires less target data than training from scratch.",
          "A large vision encoder is not a robot foundation model by itself because it does not model robot state or action. A language model issuing API calls may plan tasks but lacks grounded control. A generalist policy can be foundation-like if it is reusable and adaptable, while a world model can be foundational for prediction without being an action policy. These categories overlap but are not interchangeable."
        ]
      },
      {
        "heading": "How robot foundation model pre-training works",
        "paragraphs": [
          "Training data combines camera observations, language labels, proprioception and actions from many tasks. The model learns shared features for objects, spatial relations and motor patterns. Dataset mixing requires action normalization, sequence resampling and careful balancing so one robot or task does not dominate. Some systems add internet vision-language data or synthetic trajectories to strengthen semantics and coverage.",
          "Downstream adaptation may update all weights, train low-rank adapters, attach an embodiment-specific action head or fine-tune on a small target dataset. Prompting alone is rarely enough when the robot’s kinematics and action units differ. Evaluation should compare adapted performance with a same-size model trained only on target data."
        ]
      },
      {
        "heading": "A practical evidence rubric",
        "paragraphs": [
          "Strong evidence requires broad pre-training, multiple tasks and embodiments or a clearly reusable representation, demonstrated target adaptation and enough public detail to inspect the claim. Partial evidence covers systems with compelling demonstrations but incomplete data or adaptation protocols. Narrow but scalable describes open models whose scope is limited but whose training and reuse path is clear.",
          "Marketing label applies when a project uses the term foundation without publishing task diversity, robot coverage or adaptation results. Insufficient public information is appropriate when even the model’s inputs, outputs or training set are unclear. The rubric avoids forcing every system into a binary foundation or non-foundation decision."
        ],
        "subsections": [
          {
            "heading": "What should be reported",
            "paragraphs": [
              "A useful model card should state robots, tasks, episodes or hours, sensor modalities, action representation, pre-training objective, adaptation method, target data required, evaluation environments, failures, code, weights and license. Parameter count is helpful but cannot substitute for these details."
            ]
          }
        ]
      },
      {
        "heading": "Key systems and their evidence",
        "paragraphs": [
          "Open X-Embodiment created a large multi-institution robot dataset mixture and enabled RT-X studies across 22 reported embodiments. Octo converted that scale into an open policy intended for adaptation. OpenVLA used the mixture to train an open 7-billion-parameter VLA. These projects provide the clearest reproducible path from broad robot data to a reusable action model.",
          "Physical Intelligence publishes π0 and π0.5 code and checkpoints through openpi. GR00T and Gemini Robotics have different release and access arrangements. Their public demonstrations cover more complex mobile manipulation and humanoid use cases, but complete training data and failure distributions are unavailable. SmolVLA explores the opposite direction a smaller open model that can be trained and fine-tuned on accessible hardware."
        ],
        "sources": [
          {
            "label": "Open X-Embodiment",
            "url": "https://robotics-transformer-x.github.io/"
          },
          {
            "label": "Octo",
            "url": "https://octo-models.github.io/"
          },
          {
            "label": "OpenVLA",
            "url": "https://github.com/openvla/openvla"
          },
          {
            "label": "GR00T N1.6",
            "url": "https://research.nvidia.com/labs/gear/gr00t-n1_6/"
          }
        ]
      },
      {
        "heading": "Evidence from real robots",
        "paragraphs": [
          "Foundation claims are strongest when pre-training improves performance on a held-out robot or task after limited adaptation. Tests on the same robot family with new object combinations demonstrate task generalization, not necessarily embodiment transfer. Multi-robot training is useful evidence only when robots contribute meaningful action diversity rather than duplicated trajectories.",
          "Real-robot evaluation remains fragmented. Laboratories use different arms, grippers, camera layouts and reset rules. Closed systems may report household or humanoid sequences without publishing trial counts. Open systems enable replication, but reproducing performance still requires the target hardware, calibration and data collection."
        ]
      },
      {
        "heading": "Failure modes and open problems",
        "paragraphs": [
          "Dataset imbalance can cause a model to overfit the most common robot or task. Normalized action spaces may hide different physical limits. Negative transfer occurs when pre-training biases the policy toward motions that are unsuitable for the target embodiment. Semantic knowledge from web data can improve object recognition while adding no reliable information about force or contact.",
          "Open problems include consistent cross-embodiment benchmarks, long-horizon recovery, tactile and force integration, safety validation and transparent data governance. Models also need mechanisms for detecting unsupported instructions and out-of-distribution states rather than producing a confident action for every input."
        ]
      },
      {
        "heading": "Practical applications",
        "paragraphs": [
          "Today, robot foundation models are most useful as initialization for manipulation policies, language-conditioned task interfaces and data-efficient adaptation. They can reduce the number of target demonstrations needed for a bounded task and provide shared visual-language representations across a robot fleet.",
          "They are not interchangeable control software for arbitrary machines. Integration still requires an action adapter, calibration, safety constraints and target data. Deployment value comes from measured reduction in engineering or data collection, not from the foundation label itself."
        ]
      }
    ],
    "limitations": [
      "No accepted benchmark measures foundation-model quality across tasks, robots, environments and safety.",
      "Reported data scale uses incompatible units and often excludes proprietary details.",
      "Closed models prevent independent verification of training composition, weights and failure rates.",
      "Open X-Embodiment includes heterogeneous datasets with different quality and annotation practices.",
      "A model may transfer representations while still requiring substantial target-robot action data."
    ],
    "conclusion": [
      "A robot foundation model should earn the name through broad pre-training and demonstrated reuse, not parameter count or a polished demo. The strongest evidence combines diverse robot data, measurable target adaptation and enough public artifacts for others to test the claim.",
      "Open X-Embodiment, Octo and OpenVLA establish a reproducible foundation-model path. π0.5, GR00T and Gemini Robotics show broader industrial and humanoid ambitions but expose less of their full data and evaluation. Smaller systems such as SmolVLA demonstrate that accessibility can be a useful axis alongside scale. For engineers, the practical test is simple specify the target robot and task, measure how much data and tuning the pretrained model saves and document where transfer fails."
    ],
    "faqs": [
  {
    "question": "What is a robot foundation model?",
    "answer": "A robot foundation model is broadly pre-trained on robot-relevant data and designed for reuse or adaptation across multiple tasks, environments or embodiments. It can be an action policy or a reusable representation model. The foundation claim should be supported by data diversity, downstream transfer and an adaptation protocol, not only a large parameter count."
  },
  {
    "question": "Is every VLA model a robot foundation model?",
    "answer": "No. A VLA describes an input-output structure vision and language are mapped to actions. A VLA trained narrowly on one robot and a small task set may not be foundational. A VLA becomes foundation-like when broad pre-training creates reusable capabilities that transfer to new tasks or robots with documented adaptation."
  },
  {
    "question": "Which robot foundation models are open source?",
    "answer": "Octo, OpenVLA, SmolVLA and Physical Intelligence’s openpi provide public code and checkpoints. Openpi includes π0 and π0.5. Check each license and robot interface; public checkpoints do not imply release of the complete training data or production system."
  },
  {
    "question": "Why is cross-embodiment training difficult?",
    "answer": "Robots differ in arm length, joint limits, grippers, camera position, control frequency and action units. A shared model must normalize or abstract those differences, then restore embodiment-specific commands. Visual and language features may transfer well, while motor commands often need adapters, target demonstrations or an embodiment-specific output head."
  },
  {
    "question": "How should a foundation model be evaluated?",
    "answer": "Evaluation should separate new tasks, new objects, new environments and new robots. It should compare adaptation from the pretrained model with training from scratch using the same target data. Reports should include trials, resets, failures, action frequency, human intervention, code, weights and the exact data used for fine-tuning."
  },
  {
    "question": "Does a larger robot model always perform better?",
    "answer": "No. Larger models can absorb more diverse data but may run too slowly, require more compute and remain limited by poor action labels or narrow embodiments. A smaller model with relevant target data and a suitable control rate can outperform a larger general model on a specific robot. Scale must be evaluated with data quality and latency."
  }
],
    "methodology": [
  "The assessment uses a five-part rubric pre-training scale, task diversity, embodiment or sensor diversity, downstream adaptation and public evidence. Models are not ranked by a single benchmark because no compatible benchmark spans all systems.",
  "Counts are quoted only where primary sources report them. Proprietary data scale, parameter counts and model weights are marked as not publicly disclosed or unavailable. Verification date July 11, 2026.",
  "The October 11, 2026 correction checks π0 and π0.5 code and checkpoint availability against the official openpi repository. Other model entries retain their cited version and source dates."
],
    "sourceLinks": [
  {
    "label": "Open X-Embodiment Collaboration",
    "organization": "Google DeepMind and 33 institutions",
    "documentTitle": "Open X-Embodiment Robotic Learning Datasets and RT-X Models",
    "date": "2023 · accessed July 11, 2026",
    "url": "https://robotics-transformer-x.github.io/"
  },
  {
    "label": "Octo Model Team",
    "organization": "UC Berkeley and collaborators",
    "documentTitle": "Octo An Open-Source Generalist Robot Policy",
    "date": "2024 · accessed July 11, 2026",
    "url": "https://octo-models.github.io/"
  },
  {
    "label": "Kim et al.",
    "organization": "Stanford and UC Berkeley",
    "documentTitle": "OpenVLA",
    "date": "June 2024 · accessed July 11, 2026",
    "url": "https://github.com/openvla/openvla"
  },
  {
    "label": "Physical Intelligence",
    "organization": "Physical Intelligence",
    "documentTitle": "π0.5",
    "date": "April 22, 2025 · accessed July 11, 2026",
    "url": "https://www.physicalintelligence.company/blog/pi05"
  },
  {
    "label": "NVIDIA GEAR Lab",
    "organization": "NVIDIA",
    "documentTitle": "GR00T N1.6",
    "date": "December 15, 2025 · accessed July 11, 2026",
    "url": "https://research.nvidia.com/labs/gear/gr00t-n1_6/"
  },
  {
    "label": "Google DeepMind",
    "organization": "Google DeepMind",
    "documentTitle": "Gemini Robotics 1.5",
    "date": "September 25, 2025 · accessed July 11, 2026",
    "url": "https://deepmind.google/blog/gemini-robotics-15-brings-ai-agents-into-the-physical-world/"
  },
  {
    "label": "Hugging Face",
    "organization": "Hugging Face",
    "documentTitle": "SmolVLA",
    "date": "June 3, 2025 · accessed July 11, 2026",
    "url": "https://huggingface.co/blog/smolvla"
  },
  {
    "label": "Official openpi repository",
    "organization": "Physical Intelligence",
    "documentTitle": "openpi model releases and checkpoints",
    "date": "Accessed October 11, 2026",
    "url": "https://github.com/Physical-Intelligence/openpi"
  }
],
    "internalLinks": [
      {
        "label": "TurboVLA 0.2B real-time VLA robot control at 32 Hz",
        "path": "/robotics/turbovla-real-time-vla-robot-control"
      },
      {
        "label": "Vision-language-action models and action decoding",
        "path": "/robotics-news/vision-language-action-models"
      },
      {
        "label": "Generalist robot policies and their coverage axes",
        "path": "/robotics-news/generalist-robot-policies"
      },
      {
        "label": "Cross-embodiment learning and target-robot adaptation",
        "path": "/robotics-news/cross-embodiment-learning"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot evidence matrix for robot foundation models",
        "alt": "Matrix comparing model scale, task diversity, embodiment diversity, adaptation and public access",
        "source": "TechniaHQRobot original based on cited sources",
        "sourceUrl": "https://robotics-transformer-x.github.io/"
      },
      {
        "type": "Secondary media",
        "caption": "Open X-Embodiment robot montage",
        "alt": "Multiple robot embodiments represented in the official Open X-Embodiment project",
        "source": "Open X-Embodiment Collaboration",
        "sourceUrl": "https://robotics-transformer-x.github.io/"
      },
      {
        "type": "Secondary media",
        "caption": "Octo adaptation examples",
        "alt": "Several robot platforms using the open Octo policy",
        "source": "Octo Model Team",
        "sourceUrl": "https://octo-models.github.io/"
      },
      {
        "type": "Original chart",
        "caption": "Foundation-model evidence tiers",
        "alt": "Chart grouping systems into strong evidence, partial evidence, narrow but scalable and insufficient public information",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://github.com/openvla/openvla"
      },
      {
        "type": "Architecture",
        "caption": "Pre-training and adaptation pipeline",
        "alt": "Diagram from heterogeneous robot datasets through shared pre-training to embodiment-specific adapters",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://research.nvidia.com/labs/gear/gr00t-n1_6/"
      }
    ],
    "structuredDataSummary": [
      "Article schema includes the criteria-based headline, canonical URL and publication metadata.",
      "FAQPage schema contains six visible questions about foundation models.",
      "BreadcrumbList schema follows Home, Robotics News and Robot Foundation Models."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "Open X-Embodiment reports more than one million trajectories across 22 embodiments.",
        "Octo and OpenVLA provide public code and checkpoints through official projects.",
        "The evidence rubric separates task diversity, embodiment diversity and adaptation."
      ],
      "unconfirmed": [
        "Complete training scale and model details for closed systems are not publicly disclosed.",
        "Cross-system success rates are not comparable because protocols differ.",
        "Foundation-model status has no universal formal certification."
      ],
      "fastChanging": [
        "Model versions, weights and access programs can change rapidly.",
        "New multi-robot datasets may alter evidence classifications."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-09-26",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "egocentric-robotics-data",
    "slug": "egocentric-robotics-data",
    "path": "/robotics-news/egocentric-robotics-data",
    "title": "Egocentric Robotics Data",
    "headline": "Egocentric Robotics Data What First-Person Video Can and Cannot Teach a Robot",
    "seoTitle": "Egocentric Robotics Data for Learning Physical Robot Skills",
    "description": "A technical guide to human and robot first-person datasets, embodiment gaps, missing action labels, gaze, depth, privacy and transfer into robot policies.",
    "primaryKeyword": "egocentric robotics data",
    "keywords": [
      "egocentric robotics data",
      "first-person robot learning",
      "human video robot learning",
      "Ego-Exo4D",
      "EgoDex",
      "EgoMimic",
      "robot imitation from video",
      "teleoperation data",
      "embodiment gap",
      "hand-object interaction dataset"
    ],
    "searchQuestions": [
      "What is egocentric data in robotics?",
      "Can robots learn from first-person human video?",
      "What is the embodiment gap?",
      "Which egocentric datasets are useful for robotics?",
      "What cannot be learned from video alone?"
    ],
    "longTailKeywords": [
      "how first person video is converted into robot training data",
      "egocentric human datasets for robot manipulation",
      "missing action labels in human demonstration video",
      "privacy and consent in egocentric robotics datasets"
    ],
    "entities": [
      "Ego-Exo4D",
      "Ego4D",
      "EgoMimic",
      "EgoDex",
      "EgoVerse",
      "EgoLive",
      "EgoEngine",
      "HumanEgo"
    ],
    "imageAlt": "First-person human and robot camera views aligned with hands, objects, gaze and robot action labels",
    "introduction": [
      "A head-mounted camera can record thousands of hours of people opening drawers, folding fabric and using tools, but it does not record the motor torques that made those actions work. Egocentric data gives a first-person view close to the perspective of a mobile robot or humanoid, yet the useful signal is mixed with camera motion, occlusion, human hand morphology and missing force information.",
      "This article explains the difference between human head-mounted video, chest cameras, robot first-person streams, teleoperation recordings and datasets that also include gaze, depth, audio, inertial signals or proprioception. It follows the conversion pipeline from video segmentation and hand-object tracking to retargeted robot actions and policy training. Major datasets are compared by modality and control value, while the limitations section addresses embodiment gaps, privacy, consent, scene bias and the physical information that no RGB recording can recover."
    ],
    "keyFindings": [
      "First-person video provides task context and hand-object interaction, but ordinary human video lacks robot action labels and force.",
      "Teleoperation recordings are more directly useful for control because camera, proprioception and commanded actions can be synchronized.",
      "The embodiment gap includes hand morphology, reach, camera placement, dynamics and control frequency, not only visual appearance.",
      "Depth, gaze and inertial signals improve supervision but do not reveal all contacts or forces.",
      "Privacy and consent are technical dataset constraints because egocentric cameras capture bystanders, homes, screens and sensitive routines."
    ],
    "comparisonTables": [
      {
        "id": "egocentric-dataset-comparison",
        "title": "Egocentric datasets relevant to robot learning",
        "columns": [
          "Dataset",
          "Organization",
          "Scale reported",
          "Viewpoints and modalities",
          "Robot action labels",
          "Primary robotics value",
          "Main limitation"
        ],
        "rows": [
          [
            "Ego-Exo4D",
            "Meta and academic partners",
            "1,286.30 total video hours in v2, including 221.26 egocentric hours",
            "Synchronized ego and exo video; task annotations",
            "No native robot actions",
            "Cross-view skill understanding and temporal structure",
            "Human activities require retargeting"
          ],
          [
            "EgoMimic",
            "Meta and Georgia Tech",
            "Project-specific human and robot data",
            "Project Aria ego video plus robot demonstrations",
            "Robot segment includes actions",
            "Bridging human video and robot imitation",
            "Transfer remains embodiment-specific"
          ],
          [
            "EgoDex",
            "Apple",
            "829 hours and 194 tasks reported",
            "Egocentric dexterous hand-object video",
            "No complete robot control labels",
            "Large-scale manipulation priors",
            "Unknown forces and human-hand kinematics"
          ],
          [
            "EgoVerse",
            "Research collaboration",
            "1,362 hours, 80,000 episodes and 1,965 tasks reported",
            "Large egocentric activity collection",
            "Not a direct robot action dataset",
            "Broad task semantics and temporal segmentation",
            "Control conversion is indirect"
          ],
          [
            "EgoLive",
            "Research collaboration",
            "Scale reported in project paper",
            "Streaming egocentric interaction",
            "Task-dependent",
            "Online embodied perception research",
            "Recent dataset with limited downstream replication"
          ],
          [
            "Robot teleoperation logs",
            "Multiple labs and companies",
            "Varies",
            "Robot cameras, proprioception, actions and sometimes force",
            "Yes",
            "Direct imitation and VLA training",
            "Expensive hardware operation and narrow robot coverage"
          ]
        ],
        "description": "Human-video datasets and robot-control datasets are separated. A large video corpus is not automatically a robot trajectory dataset.",
        "note": "Scale figures are author-reported and use different definitions of hours, clips and episodes."
      }
    ],
    "sections": [
      {
        "heading": "Definition egocentric data and its variants",
        "paragraphs": [
          "Egocentric data is recorded from a camera attached to or moving with the acting person or robot. Human head-mounted video approximates eye or head perspective. Chest-mounted cameras are more stable but differ from head motion. Robot first-person video comes from head, torso, wrist or hand cameras and can be synchronized with joint state and motor commands.",
          "Teleoperation data records what a robot experienced while a human controlled it. Hand-object interaction datasets focus on human manipulation, sometimes with 3D hand pose, gaze or narration. These sources are related but not equivalent. The closer the recorded modalities are to the target robot’s sensors and actions, the less inference is required before control learning."
        ]
      },
      {
        "heading": "How human first-person video becomes robot training data",
        "paragraphs": [
          "A processing pipeline detects hands and objects, segments tasks, tracks 2D or 3D motion and estimates contact events. Narration or automatic captioning can add language labels. The system then maps human motion into a robot-compatible representation such as end-effector trajectories, object-centric waypoints or latent task phases.",
          "Retargeting must respect the robot’s reach, joint limits, gripper geometry and collision constraints. The converted trajectory can seed imitation learning, generate goals or guide a planner. Many projects use human video for high-level structure and robot demonstrations for the final action policy, because human motion alone does not specify the exact commands or dynamics of the robot."
        ],
        "subsections": [
          {
            "heading": "Gaze, audio, depth and inertial data",
            "paragraphs": [
              "Gaze can indicate task-relevant objects before the hand moves. Audio captures tool sounds, contact and spoken intent. Depth improves geometry, while inertial data helps separate head motion from scene motion. These signals add context, but they must be synchronized and calibrated. Missing timestamps can turn a useful multimodal recording into ambiguous supervision."
            ]
          },
          {
            "heading": "Temporal alignment and action labels",
            "paragraphs": [
              "Human videos often contain pauses, corrections and actions outside the camera view. Robot policies need consistent timing and command labels. Alignment methods match task phases or object motion, but the resulting action is inferred rather than observed. Teleoperation avoids part of this problem by recording the robot’s actual command stream."
            ]
          }
        ]
      },
      {
        "heading": "Key datasets and projects",
        "paragraphs": [
          "Ego-Exo4D pairs first-person and external views across skilled human activities, supporting cross-view understanding and fine-grained temporal analysis. EgoMimic explicitly connects Project Aria human recordings with robot learning, illustrating a practical route from human perspective to robot demonstration. EgoDex expands dexterous first-person manipulation coverage but remains human video rather than direct robot control data.",
          "EgoVerse, EgoLive, EgoEngine and HumanEgo explore larger or more interactive egocentric learning settings. Their value depends on the downstream target scene understanding may transfer from raw video, while low-level action needs additional robot data. Dataset scale should therefore be described alongside modalities and labels, not as a single measure of usefulness."
        ],
        "sources": [
          {
            "label": "Ego-Exo4D",
            "url": "https://ai.meta.com/blog/ego-exo4d-video-learning-perception/"
          },
          {
            "label": "EgoMimic",
            "url": "https://ai.meta.com/blog/egomimic-project-aria-georgia-tech-ego4d-robotics-embodied-ai/"
          },
          {
            "label": "EgoDex",
            "url": "https://github.com/apple/ml-egodex"
          },
          {
            "label": "EgoVerse",
            "url": "https://arxiv.org/abs/2604.07607"
          }
        ]
      },
      {
        "heading": "Evidence from robot learning",
        "paragraphs": [
          "The strongest transfer experiments combine human and robot data, using human video to improve semantic or temporal coverage and robot trajectories to ground actions. Evaluations should identify whether the target task, objects, room or robot were unseen and how much robot fine-tuning was still required.",
          "Results from a fixed laboratory camera setup do not establish robustness to head motion or household clutter. Conversely, large human video collections may improve recognition without improving control. A useful ablation compares robot-only training, human-video pre-training and combined training under the same target-robot protocol."
        ]
      },
      {
        "heading": "What a robot cannot learn from video alone",
        "paragraphs": [
          "RGB video does not reveal joint torque, grip force, friction coefficient, object mass or the motor command that caused a movement. Occluded contacts are invisible. Human compliance and skin deformation differ from robotic hands. A successful human action may rely on a fingernail, palm contact or passive wrist motion unavailable to the robot.",
          "Video also underdetermines causality. Seeing an object move after a hand approaches does not specify the exact force or whether another contact assisted. For control, the model needs robot interaction, simulation, tactile sensing, system identification or another source of action-grounded physical information."
        ]
      },
      {
        "heading": "Failure modes, bias and privacy",
        "paragraphs": [
          "Camera motion can be mistaken for object motion. Hands leave the frame, reflective objects break tracking and narration may describe intent after the action. Scene bias can make a model associate a task with one kitchen or tool style. Human demonstrations may contain unsafe shortcuts that should not be copied by a high-force robot.",
          "Egocentric cameras record faces, screens, addresses, conversations and private spaces. Consent must cover bystanders and downstream model use, not only the camera wearer. De-identification can remove visual identity while preserving sensitive behavior. Dataset governance, access controls and deletion procedures are therefore part of the engineering design."
        ]
      },
      {
        "heading": "Practical applications",
        "paragraphs": [
          "Egocentric data is credible for learning task segmentation, object relevance, hand-object affordances, language labels and coarse action sequences. It can guide data collection by identifying which human activities deserve robot demonstrations and can pre-train representations before target-robot fine-tuning.",
          "Direct low-level control from unconstrained human video remains experimental. Production systems should use action-grounded robot data for final policy learning, validate retargeted trajectories and apply safety constraints independent of the learned behavior."
        ]
      }
    ],
    "limitations": [
      "Dataset scales use incompatible units and may count overlapping views or clips differently.",
      "Human videos rarely contain complete robot-compatible action, force or proprioceptive labels.",
      "Retargeting quality depends on pose estimation and target-robot kinematics.",
      "Privacy and consent conditions vary and may restrict redistribution or commercial use.",
      "Reported downstream gains are not comparable across tasks, robots and fine-tuning budgets."
    ],
    "conclusion": [
      "Egocentric robotics data is valuable because it records tasks from the viewpoint of an acting body, including object choices, hand trajectories and temporal structure that external video may miss. Its value is highest for semantics, skill segmentation and representation learning.",
      "It is not a substitute for robot interaction data. Human morphology, missing forces, camera motion and absent action labels create an embodiment gap that must be bridged through retargeting, simulation, teleoperation or target-robot demonstrations. The best current pipelines combine broad human video with smaller action-grounded robot datasets and report exactly what each source contributes. Privacy, consent and access conditions should be treated as model constraints from the start, not as documentation added after collection."
    ],
    "faqs": [
      {
        "question": "What is egocentric data in robotics?",
        "answer": "Egocentric data is video or multimodal sensing recorded from the viewpoint of the acting human or robot. It may include head-mounted human video, robot head or wrist cameras, gaze, depth, audio, inertial signals and proprioception. Its usefulness for control depends on whether actions and robot state are recorded alongside the images."
      },
      {
        "question": "Can a robot learn manipulation from human first-person video?",
        "answer": "Human first-person video can teach task structure, object relevance and approximate hand-object motion. It usually cannot provide exact robot actions, forces or joint commands. Practical systems retarget human motion, infer object-centric goals and then use robot demonstrations, simulation or fine-tuning to ground the behavior in the target embodiment."
      },
      {
        "question": "What is the embodiment gap?",
        "answer": "The embodiment gap is the mismatch between the body that generated the data and the robot that must act. It includes arm length, hand shape, joint limits, strength, compliance, camera position, control frequency and available sensors. A visually similar action can require a different trajectory or be physically impossible on the target robot."
      },
      {
        "question": "Why is teleoperation data more useful for control?",
        "answer": "Teleoperation records the target robot’s camera views, proprioception and commanded actions at synchronized timestamps. That provides direct state-action examples for imitation learning. It is still human-generated behavior, but the action space and hardware match the robot. The drawback is cost, operator effort and limited coverage of failures or unusual situations."
      },
      {
        "question": "What cannot be learned from RGB video alone?",
        "answer": "RGB video does not directly reveal contact force, torque, friction, object mass, motor current or hidden contact state. It also cannot uniquely identify the action that caused an observed movement. These gaps matter for grasp stability, insertion, deformable objects and safe force control, so additional robot, tactile or simulated interaction data is needed."
      },
      {
        "question": "What privacy risks come with egocentric datasets?",
        "answer": "First-person cameras can capture bystanders, faces, conversations, computer screens, addresses and private routines. Consent from the camera wearer may not cover everyone recorded. Responsible datasets need collection rules, access control, de-identification, licensing, retention limits and a process for handling removal requests or sensitive downstream uses."
      }
    ],
    "methodology": [
      "Datasets were compared by viewpoint, modalities, reported scale, presence of robot action labels and likely robotics use. Human-video datasets are not labeled as robot-control datasets unless synchronized robot actions are included.",
      "Scale figures come from official project pages or papers and are reported without conversion because hours, episodes and clips are not equivalent. Access, dataset versions and publication status were rechecked September 26, 2026."
    ],
    "sourceLinks": [
      {
        "label": "Ego-Exo4D V2 change log",
        "organization": "Ego-Exo4D",
        "documentTitle": "Ego-Exo4D Change Log",
        "date": "March 25, 2024 · accessed September 26, 2026",
        "url": "https://docs.ego-exo4d-data.org/changelog/"
      },
      {
        "label": "Meta AI and academic partners",
        "organization": "Meta AI",
        "documentTitle": "Ego-Exo4D a foundational dataset for research on video learning and multimodal perception",
        "date": "2024 · accessed July 11, 2026",
        "url": "https://ai.meta.com/blog/ego-exo4d-video-learning-perception/"
      },
      {
        "label": "Meta AI and Georgia Tech",
        "organization": "Meta AI",
        "documentTitle": "EgoMimic scaling robot learning with egocentric human video",
        "date": "2025 · accessed July 11, 2026",
        "url": "https://ai.meta.com/blog/egomimic-project-aria-georgia-tech-ego4d-robotics-embodied-ai/"
      },
      {
        "label": "Apple Machine Learning Research",
        "organization": "Apple",
        "documentTitle": "EgoDex official repository",
        "date": "2025 · accessed July 11, 2026",
        "url": "https://github.com/apple/ml-egodex"
      },
      {
        "label": "EgoVerse authors",
        "organization": "Research collaboration",
        "documentTitle": "EgoVerse",
        "date": "April 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2604.07607"
      },
      {
        "label": "EgoLive authors",
        "organization": "Research collaboration",
        "documentTitle": "EgoLive",
        "date": "April 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2604.23570"
      },
      {
        "label": "EgoEngine authors",
        "organization": "Research collaboration",
        "documentTitle": "EgoEngine",
        "date": "June 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2606.12604"
      },
      {
        "label": "HumanEgo authors",
        "organization": "Research collaboration",
        "documentTitle": "HumanEgo",
        "date": "May 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2605.24934"
      }
    ],
    "internalLinks": [
      {
        "label": "Cross-embodiment learning and the embodiment gap",
        "path": "/robotics-news/cross-embodiment-learning"
      },
      {
        "label": "Generalist robot policies trained across diverse data",
        "path": "/robotics-news/generalist-robot-policies"
      },
      {
        "label": "Synthetic robotics data for missing interactions and labels",
        "path": "/robotics-news/synthetic-robotics-data"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot diagram aligning human ego video, robot ego video and action labels",
        "alt": "Human head-camera view and robot wrist-camera view aligned around the same object interaction",
        "source": "TechniaHQRobot original based on cited datasets",
        "sourceUrl": "https://ai.meta.com/blog/egomimic-project-aria-georgia-tech-ego4d-robotics-embodied-ai/"
      },
      {
        "type": "Secondary media",
        "caption": "Ego-Exo4D synchronized first- and third-person views",
        "alt": "One activity shown simultaneously from a head camera and external cameras",
        "source": "Meta AI and project partners",
        "sourceUrl": "https://ai.meta.com/blog/ego-exo4d-video-learning-perception/"
      },
      {
        "type": "Secondary media",
        "caption": "EgoDex manipulation examples",
        "alt": "First-person human hands performing varied dexterous tasks",
        "source": "Apple Machine Learning Research",
        "sourceUrl": "https://github.com/apple/ml-egodex"
      },
      {
        "type": "Original chart",
        "caption": "Data-value ladder from raw human video to synchronized robot trajectories",
        "alt": "Chart comparing semantics, geometry, action grounding and force information across data types",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/abs/2604.07607"
      },
      {
        "type": "Architecture",
        "caption": "Human-video-to-robot pipeline",
        "alt": "Diagram showing segmentation, hand-object tracking, retargeting, robot fine-tuning and validation",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://ai.meta.com/blog/egomimic-project-aria-georgia-tech-ego4d-robotics-embodied-ai/"
      }
    ],
    "structuredDataSummary": [
      "Article schema describes the egocentric-data technical guide.",
      "FAQPage schema contains six visible questions about first-person data and robot learning.",
      "BreadcrumbList schema follows Home, Robotics News and Egocentric Robotics Data."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "Ego-Exo4D v2 documents 1,286.30 total video hours across 5,035 takes, including 221.26 egocentric hours.",
        "EgoDex reports 829 hours and 194 tasks in its official repository.",
        "Human-video datasets are separated from action-grounded robot trajectories."
      ],
      "unconfirmed": [
        "Dataset scale figures are not directly comparable across hours, clips and episodes.",
        "Some recent datasets have limited independent downstream replication.",
        "Force and contact labels are absent or incomplete in most human egocentric collections."
      ],
      "fastChanging": [
        "Dataset releases, access licenses and annotations may change.",
        "Recent 2026 projects may publish additional benchmarks after verification."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-07-11",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "multiview-embodied-reasoning",
    "slug": "multiview-embodied-reasoning",
    "path": "/robotics-news/multiview-embodied-reasoning",
    "title": "Multiview Embodied Reasoning",
    "headline": "Multiview Embodied Reasoning Combining Cameras for Physical Tasks",
    "seoTitle": "Multiview Embodied Reasoning for Real-World Robot Tasks",
    "description": "How robots align multiple camera views for 3D reasoning, occlusion handling, object permanence and action planning, with models, benchmarks and failures.",
    "primaryKeyword": "multiview embodied reasoning",
    "keywords": [
      "multiview embodied reasoning",
      "multi-camera robot perception",
      "cross-view attention robotics",
      "robot camera calibration",
      "3D scene reasoning",
      "object permanence robot",
      "active perception",
      "Gemini Robotics ER",
      "MV-RoboBench",
      "DeMaVLA"
    ],
    "searchQuestions": [
      "What is multiview embodied reasoning?",
      "How do robots combine several cameras?",
      "Does multiview reasoning require 3D reconstruction?",
      "How do robots handle occlusion?",
      "What is cross-view attention?"
    ],
    "longTailKeywords": [
      "multiview reasoning for robot manipulation behind occlusion",
      "head camera and wrist camera fusion for robots",
      "camera calibration and coordinate frames in embodied AI",
      "multi camera benchmarks for robot reasoning"
    ],
    "entities": [
      "Gemini Robotics-ER 1.6",
      "MV-RoboBench",
      "DeMaVLA",
      "RoboVIP",
      "MultiWorld"
    ],
    "imageAlt": "Robot combining head, wrist and external camera views into a shared spatial representation",
    "introduction": [
      "A wrist camera may see a connector hidden from the robot’s head, while an external camera sees the arm but not the contact surface. Each view contains useful evidence and a different blind spot. Multiview embodied reasoning combines those observations so a robot can track objects, infer spatial relations and choose actions in a common coordinate frame.",
      "This article explains camera calibration, temporal synchronization, cross-view attention, depth estimation, 3D reconstruction, scene graphs and active perception. It distinguishes fixed external cameras from onboard head and hand cameras and examines what changes when views come from a human rather than the robot. Recent reasoning models and benchmarks are compared by inputs, outputs and real-robot evidence. The analysis also covers occlusion, object permanence, viewpoint shift and the question of whether a system needs an explicit 3D world model to reason across cameras."
    ],
    "keyFindings": [
      "Multiple views help only when the system knows how their timestamps and coordinate frames relate.",
      "Cross-view attention can fuse evidence without building a complete 3D mesh, but action still needs spatial grounding.",
      "Wrist cameras provide local contact detail; head and external cameras provide broader context and arm visibility.",
      "Benchmarks often test question answering or spatial reasoning rather than closed-loop robot control.",
      "Camera movement, calibration drift and inconsistent object identity remain major failure sources."
    ],
    "comparisonTables": [
      {
        "id": "multiview-system-comparison",
        "title": "Multiview embodied reasoning systems and benchmarks",
        "columns": [
          "System",
          "Year",
          "Views",
          "Primary output",
          "Robot control evidence",
          "Public status",
          "Main limitation"
        ],
        "rows": [
          [
            "Gemini Robotics-ER 1.6",
            "2026",
            "Multiple visual observations supported in embodied reasoning workflows",
            "Spatial and task reasoning for robot systems",
            "Used with robotics stack; not a standalone low-level policy",
            "Closed model access",
            "Training details and full evaluation set unavailable"
          ],
          [
            "MV-RoboBench",
            "2025",
            "Multi-view robot scenes",
            "Answers across spatial and embodied reasoning subtasks",
            "Benchmark, not a controller",
            "Dataset and paper status per project",
            "Question answering does not prove action execution"
          ],
          [
            "DeMaVLA",
            "2026",
            "Multiple cameras and temporal observations",
            "Action policy",
            "Author-reported robot manipulation",
            "Paper; artifacts limited",
            "Recent results and bounded setups"
          ],
          [
            "RoboVIP",
            "2026",
            "Viewpoint-conditioned robot observations",
            "Perception or policy representation",
            "Author-reported downstream robot tasks",
            "Paper; release status varies",
            "View transfer remains hardware-specific"
          ],
          [
            "MultiWorld",
            "2026",
            "Multi-view or multi-scene embodied observations",
            "World representation and reasoning",
            "Primarily research evaluation",
            "Paper; public artifacts incomplete",
            "Physical control evidence is limited"
          ]
        ],
        "description": "The table separates reasoning benchmarks from policies that execute actions. Multi-camera input alone does not establish multiview reasoning.",
        "note": "Public status and evidence were checked July 11, 2026."
      }
    ],
    "sections": [
      {
        "heading": "Definition what multiview embodied reasoning requires",
        "paragraphs": [
          "Multiview embodied reasoning uses observations from different cameras or viewpoints to infer task-relevant physical state and support an embodied decision. The views may be simultaneous or collected over time. A qualifying system must connect information across views, not simply concatenate images without spatial or temporal interpretation.",
          "The output can be a scene representation, answer, plan or robot action. A multi-camera detector is perception, while a policy that uses fused views to manipulate is embodied control. The distinction matters because success on spatial questions does not establish closed-loop action under contact and latency."
        ]
      },
      {
        "heading": "Camera geometry, synchronization and coordinate frames",
        "paragraphs": [
          "Intrinsic calibration describes how each camera maps 3D rays to pixels. Extrinsic calibration describes camera pose relative to the robot or world. A wrist camera moves with the hand, so its transform changes with joint state. External cameras can be stable but may drift or be bumped. Time synchronization is equally important because a moving arm appears in different positions when frames are offset.",
          "Systems align views through calibrated projection, learned correspondences, feature matching or common 3D coordinates. The action planner ultimately needs a target relative to the robot, not only a visual match. Errors in calibration translate directly into grasp or insertion error."
        ],
        "subsections": [
          {
            "heading": "Cross-view attention and feature fusion",
            "paragraphs": [
              "Cross-view attention lets tokens from one image query tokens from another, learning which regions correspond. It can preserve uncertainty better than forcing every pixel into one reconstruction. However, attention weights do not automatically provide metric geometry; many systems add depth, camera pose or positional encoding."
            ]
          },
          {
            "heading": "Object permanence and occlusion",
            "paragraphs": [
              "Object permanence means maintaining identity and estimated state when an object disappears behind the hand or another object. Multiple views reduce blind spots, while temporal memory carries state through periods when every camera is occluded. Identity errors can cause the planner to act on a stale or duplicated object representation."
            ]
          }
        ]
      },
      {
        "heading": "View types and their operational roles",
        "paragraphs": [
          "Head cameras provide a stable overview aligned with robot navigation and gaze. Wrist or palm cameras see the final approach and can reduce hand occlusion. External cameras reveal the whole body and workcell but require infrastructure and calibration. Fixed multi-camera rigs are effective in factories, while household robots cannot assume that external cameras exist.",
          "Human-view video introduces another viewpoint with different body geometry and camera motion. It can communicate task intent or show hidden areas, but converting a human coordinate frame into robot action requires localization, retargeting and uncertainty handling. A view should be described by who carries it, where it is calibrated and whether it is available at deployment."
        ]
      },
      {
        "heading": "Key systems and benchmarks",
        "paragraphs": [
          "Gemini Robotics-ER 1.6 is an embodied reasoning model intended to support spatial understanding and robot task reasoning. Its role is above low-level servo control. MV-RoboBench evaluates multiview reasoning through a curated set of questions and subtasks, providing diagnostic evidence but not direct motor performance.",
          "DeMaVLA, RoboVIP and MultiWorld explore learned fusion for policies or embodied representations. Their recent results indicate that view diversity can improve robustness to occlusion and camera shift, but the experiments remain tied to selected robots and scenes. Release status and evaluation protocols should be checked project by project."
        ],
        "sources": [
          {
            "label": "Gemini Robotics-ER 1.6",
            "url": "https://deepmind.google/blog/gemini-robotics-er-1-6/"
          },
          {
            "label": "MV-RoboBench",
            "url": "https://arxiv.org/abs/2510.19400"
          },
          {
            "label": "DeMaVLA",
            "url": "https://arxiv.org/html/2605.31286"
          },
          {
            "label": "RoboVIP",
            "url": "https://arxiv.org/html/2601.05241"
          }
        ]
      },
      {
        "heading": "Evidence from real robots",
        "paragraphs": [
          "Real-robot evidence is strongest when a task becomes impossible or measurably harder from one view, such as grasping behind an occluder or reading a gauge while the arm blocks the head camera. The experiment should compare the same policy with one and multiple views, report calibration and include camera perturbations.",
          "Many multiview benchmarks stop at perception or language answers. Those results can diagnose spatial reasoning, but an action policy adds control frequency, coordinate conversion and contact. A model that correctly identifies which object is behind another may still fail to move the gripper to the right metric pose."
        ]
      },
      {
        "heading": "Does multiview reasoning require a 3D world model?",
        "paragraphs": [
          "No complete 3D reconstruction is required for every task. A policy can use view-conditioned features, epipolar constraints or object-centric tokens to choose an action. For short tabletop tasks, learned correspondences may be sufficient. Active perception can also move a camera until one view becomes informative.",
          "Explicit 3D becomes valuable when the robot must plan around obstacles, maintain object permanence, share information across large viewpoint changes or reason about reach. A hybrid representation can combine metric geometry for safety with learned features for semantics. The useful question is which spatial facts must remain consistent for the action, not whether the system produces a photorealistic model."
        ]
      },
      {
        "heading": "Failure modes and practical applications",
        "paragraphs": [
          "Calibration drift can make two correct detections disagree in space. Unsynchronized frames create false motion. Reflections and repeated objects confuse correspondence. A camera change can shift color, focal length and distortion. Models may overtrust an external view that will not exist in deployment or fuse a stale wrist image after contact.",
          "Credible applications include inspection, gauge reading, assembly, bin picking, manipulation behind occlusion and human-robot workcell monitoring. Experimental applications include open-world household reasoning and cross-human-robot view transfer. Deployment should monitor camera health, timestamp integrity and confidence in the shared frame."
        ]
      }
    ],
    "limitations": [
      "Reasoning benchmarks and robot-control benchmarks measure different capabilities.",
      "Calibration procedures and drift tolerance are not consistently reported.",
      "Recent systems often lack independent replication and complete public artifacts.",
      "External-camera results may not transfer to mobile deployment without infrastructure.",
      "A correct semantic answer does not guarantee metric action accuracy."
    ],
    "conclusion": [
      "Multiview embodied reasoning is the process of connecting evidence from several cameras to a physical state and decision. Its benefit is concrete one view can recover objects, contacts or body motion hidden from another. Its difficulty is equally concrete every view has a timestamp, calibration and coordinate frame that can be wrong.",
      "Current models improve spatial reasoning and occlusion handling, but much public evidence remains benchmark-based or confined to controlled robot experiments. An explicit 3D world model is not mandatory for every task, yet action requires some stable spatial grounding. Practical systems combine calibrated geometry, learned cross-view features, temporal memory and active perception, then keep collision and safety checks independent of the reasoning model."
    ],
    "faqs": [
      {
        "question": "What is multiview embodied reasoning?",
        "answer": "Multiview embodied reasoning combines observations from different cameras or viewpoints to infer physical state and support an action, plan or answer. The system must connect information across views through geometry, learned correspondence or shared representations. Simply feeding several unrelated images to a model does not prove that it reasons about their spatial relationship."
      },
      {
        "question": "How do robots combine head and wrist cameras?",
        "answer": "The robot uses camera calibration and joint state to express observations in a shared coordinate frame, or learns cross-view correspondences with positional information. The head camera supplies global context, while the wrist camera resolves local geometry near contact. Temporal synchronization is necessary because the wrist pose changes as the arm moves."
      },
      {
        "question": "Does multiview reasoning require 3D reconstruction?",
        "answer": "Not always. A policy can fuse image features or object tokens without constructing a full mesh or point cloud. Explicit 3D is useful for collision checking, large viewpoint changes and persistent object state. Many practical systems use a hybrid learned features for semantics and calibrated geometry for metric planning and safety."
      },
      {
        "question": "What is cross-view attention?",
        "answer": "Cross-view attention is a neural mechanism that lets features from one camera select and combine relevant features from another. It can learn correspondences between an object seen from different angles. Attention alone does not guarantee accurate geometry, so systems often add camera pose, depth, epipolar constraints or 3D positional encoding."
      },
      {
        "question": "Why is camera synchronization important?",
        "answer": "If cameras capture a moving robot at different times, the same hand or object appears in inconsistent positions. The fusion system may interpret the mismatch as object motion or bad calibration. Accurate timestamps and latency compensation are essential for manipulation, especially when a wrist camera moves quickly or contact changes between frames."
      },
      {
        "question": "Can a multiview benchmark prove robot autonomy?",
        "answer": "No. A benchmark may show that a model answers spatial questions or recognizes objects across views. Autonomous control additionally requires action generation, metric coordinate conversion, closed-loop feedback, error recovery and safety. Evidence should state whether the model only reasons about images or actually controls a robot in repeated trials."
      }
    ],
    "methodology": [
      "Systems were separated into reasoning models, benchmarks and action policies. Inclusion required explicit use of more than one viewpoint or camera stream and a task involving spatial or embodied interpretation.",
      "Company and author results are attributed. Real-robot evidence is described separately from question answering, simulation and representation learning. Verification date July 11, 2026."
    ],
    "sourceLinks": [
      {
        "label": "Google DeepMind",
        "organization": "Google DeepMind",
        "documentTitle": "Gemini Robotics-ER 1.6",
        "date": "2026 · accessed July 11, 2026",
        "url": "https://deepmind.google/blog/gemini-robotics-er-1-6/"
      },
      {
        "label": "Google DeepMind",
        "organization": "Google DeepMind",
        "documentTitle": "Gemini Robotics model page",
        "date": "Accessed July 11, 2026",
        "url": "https://deepmind.google/models/gemini-robotics/gemini-robotics-er/"
      },
      {
        "label": "MV-RoboBench authors",
        "organization": "Research collaboration",
        "documentTitle": "MV-RoboBench",
        "date": "October 2025 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2510.19400"
      },
      {
        "label": "DeMaVLA authors",
        "organization": "Research collaboration",
        "documentTitle": "DeMaVLA",
        "date": "May 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/html/2605.31286"
      },
      {
        "label": "RoboVIP authors",
        "organization": "Research collaboration",
        "documentTitle": "RoboVIP",
        "date": "January 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/html/2601.05241"
      },
      {
        "label": "MultiWorld authors",
        "organization": "Research collaboration",
        "documentTitle": "MultiWorld",
        "date": "April 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/html/2604.18564"
      }
    ],
    "internalLinks": [
      {
        "label": "Real2Sim2Real and calibrated digital twins",
        "path": "/robotics-news/real2sim2real"
      },
      {
        "label": "Vision-language-action models for closed-loop control",
        "path": "/robotics-news/vision-language-action-models"
      },
      {
        "label": "Egocentric robotics data and viewpoint transfer",
        "path": "/robotics-news/egocentric-robotics-data"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot multiview fusion graphic",
        "alt": "Head, wrist and external cameras projecting observations into one task-centered representation",
        "source": "TechniaHQRobot original based on cited systems",
        "sourceUrl": "https://arxiv.org/abs/2510.19400"
      },
      {
        "type": "Secondary media",
        "caption": "Official Gemini Robotics-ER spatial reasoning example",
        "alt": "Robot scene with multiple visual observations used for embodied reasoning",
        "source": "Google DeepMind",
        "sourceUrl": "https://deepmind.google/blog/gemini-robotics-er-1-6/"
      },
      {
        "type": "Secondary media",
        "caption": "MV-RoboBench multiview task example",
        "alt": "The same robot workspace observed from several cameras for a spatial question",
        "source": "MV-RoboBench authors",
        "sourceUrl": "https://arxiv.org/abs/2510.19400"
      },
      {
        "type": "Original chart",
        "caption": "Camera-role comparison",
        "alt": "Chart comparing head, wrist, palm, external and human views by coverage, occlusion and calibration burden",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/html/2601.05241"
      },
      {
        "type": "Architecture",
        "caption": "Calibrated multiview reasoning and action pipeline",
        "alt": "Diagram showing synchronized cameras, cross-view fusion, spatial state, planner, action and feedback",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/html/2605.31286"
      }
    ],
    "structuredDataSummary": [
      "Article schema represents the multiview reasoning technical guide.",
      "FAQPage schema contains the six visible camera-fusion questions.",
      "BreadcrumbList schema follows Home, Robotics News and Multiview Embodied Reasoning."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "Benchmarks are separated from closed-loop action policies.",
        "The cited projects explicitly use multiple views or viewpoints.",
        "The article distinguishes learned fusion from calibrated metric geometry."
      ],
      "unconfirmed": [
        "Independent real-robot replication is limited for recent 2026 systems.",
        "Training data, latency and failure logs are incomplete for closed models.",
        "Question-answering accuracy cannot be converted into manipulation success."
      ],
      "fastChanging": [
        "Model access and benchmark releases may change.",
        "New multi-camera robot policies may add broader real-world evaluation."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-07-11",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "synthetic-robotics-data",
    "slug": "synthetic-robotics-data",
    "path": "/robotics-news/synthetic-robotics-data",
    "title": "Synthetic Robotics Data",
    "headline": "Synthetic Robotics Data What Simulation Can Generate and Where It Fails",
    "seoTitle": "Synthetic Robotics Data for Physical AI Training Systems",
    "description": "A technical comparison of robot simulators, domain randomization, synthetic trajectories, tactile and depth data, sim fidelity and the real data still required.",
    "primaryKeyword": "synthetic robotics data",
    "keywords": [
      "synthetic robotics data",
      "robot simulation data",
      "domain randomization",
      "Isaac Sim",
      "Isaac Lab",
      "MuJoCo",
      "ManiSkill",
      "SAPIEN",
      "Habitat robotics",
      "sim-to-real data"
    ],
    "searchQuestions": [
      "What is synthetic data in robotics?",
      "Which simulator is best for robot learning?",
      "How does domain randomization work?",
      "Can synthetic data replace real robot data?",
      "How much real data is needed after simulation?"
    ],
    "longTailKeywords": [
      "synthetic trajectories for robot policy training",
      "robot simulation fidelity contact friction and actuator delay",
      "Isaac Sim versus MuJoCo for physical AI",
      "how much real data is needed for sim to real"
    ],
    "entities": [
      "Isaac Sim",
      "Isaac Lab",
      "MuJoCo",
      "ManiSkill",
      "Habitat",
      "iGibson",
      "SAPIEN",
      "Gazebo",
      "Genesis",
      "PyBullet"
    ],
    "imageAlt": "Robot simulation pipeline generating varied scenes, trajectories, sensor data and annotations before real-world validation",
    "introduction": [
      "A simulated robot can repeat a grasp one million times without wearing a gearbox or requiring a technician to reset the scene. That scale makes synthetic data attractive, but it also makes systematic error easy to multiply. If friction, contact, actuator delay or camera noise are wrong, a large dataset can train a policy to exploit behavior that the physical robot cannot reproduce.",
      "Synthetic robotics data includes rendered images, depth, segmentation, trajectories, force signals, demonstrations and labels generated by simulators, procedural tools or learned generative models. This article compares major platforms, explains domain randomization, digital twins, motion retargeting and synthetic tactile data and separates photorealism from physical fidelity. It also examines what real data remains necessary for calibration, validation and residual correction, with practical criteria for choosing a simulator around the task rather than around visual quality alone."
    ],
    "keyFindings": [
      "Simulation can generate scale, labels and controlled variation, but data quality depends on the physics, assets, sensors and task generator.",
      "Photorealistic rendering does not guarantee correct friction, compliance, contact or actuator behavior.",
      "Domain randomization works when its parameter ranges include plausible reality; arbitrary variation can make learning harder without closing the gap.",
      "Different simulators optimize different goals, including rigid-body speed, photorealism, navigation, manipulation or systems integration.",
      "Real data remains necessary for system identification, calibration, target-domain validation and failure discovery."
    ],
    "comparisonTables": [
      {
        "id": "simulator-comparison",
        "title": "Major platforms used to generate robotics data",
        "columns": [
          "Platform",
          "Primary strength",
          "Physics and rendering",
          "Typical data",
          "Best fit",
          "Main limitation"
        ],
        "rows": [
          [
            "Isaac Sim",
            "Photorealistic robotics simulation and synthetic sensors",
            "PhysX with Omniverse rendering",
            "RGB, depth, segmentation, trajectories, sensor streams",
            "Digital twins, perception and robot workflows",
            "High compute and integration complexity"
          ],
          [
            "Isaac Lab",
            "Large-scale robot learning on Isaac Sim",
            "Vectorized PhysX workflows",
            "Policy rollouts, demonstrations, rewards",
            "RL, imitation and sim-to-real training",
            "Depends on task configuration and simulator assumptions"
          ],
          [
            "MuJoCo",
            "Fast articulated rigid-body dynamics",
            "High-performance physics; lighter rendering focus",
            "States, contacts, control rollouts",
            "Control research and locomotion",
            "Less focused on photorealistic sensor generation"
          ],
          [
            "ManiSkill / SAPIEN",
            "Manipulation benchmarks and scalable environments",
            "GPU-oriented simulation and rendering",
            "Trajectories, RGB-D, state labels",
            "Manipulation policy training",
            "Benchmark assets may not match a target workcell"
          ],
          [
            "Habitat",
            "Embodied navigation and indoor scenes",
            "Fast scene simulation and sensor rendering",
            "RGB-D, semantic maps, navigation episodes",
            "Navigation and embodied perception",
            "Limited contact-rich manipulation fidelity"
          ],
          [
            "iGibson",
            "Interactive household simulation",
            "Physics-enabled indoor environments",
            "Navigation and object interaction data",
            "Household embodied tasks",
            "Asset and dynamics fidelity vary"
          ],
          [
            "Gazebo",
            "Robot systems integration and ROS workflows",
            "Multiple physics engines and sensor plugins",
            "Sensor streams, controller tests",
            "Engineering integration and validation",
            "Large-scale learning throughput depends on setup"
          ],
          [
            "Genesis",
            "High-throughput generative physics workflows",
            "GPU simulation with broad material ambitions",
            "Rollouts and synthetic scenes",
            "Rapid research iteration",
            "Recent platform with evolving validation"
          ],
          [
            "PyBullet",
            "Accessible rigid-body simulation",
            "Bullet physics with Python interface",
            "Control rollouts and benchmark data",
            "Education and lightweight research",
            "Lower fidelity for some modern high-contact tasks"
          ]
        ],
        "description": "No platform is best on every axis. The relevant choice depends on contact fidelity, sensor realism, throughput, assets and integration requirements.",
        "note": "Platform capabilities and releases were checked July 11, 2026. Individual task implementations can differ substantially from the core engine."
      }
    ],
    "sections": [
      {
        "heading": "Definition what counts as synthetic robotics data",
        "paragraphs": [
          "Synthetic robotics data is generated rather than directly recorded from the target physical interaction. A simulator can produce state-action trajectories, camera images, depth, segmentation masks, optical flow, force estimates and success labels. Procedural generation varies scene layout, objects and tasks. Motion retargeting converts human or robot trajectories into a simulated embodiment.",
          "Generated video without action labels may support perception or world-model training but is not automatically a robot demonstration. A simulator does not automatically create high-quality data task logic, controller quality, reset distribution and asset metadata determine what the model actually learns."
        ]
      },
      {
        "heading": "How synthetic data is generated",
        "paragraphs": [
          "A scene defines robot geometry, joints, sensors, objects and physical parameters. A controller, scripted expert, planner, teleoperator or learned policy generates actions. The engine computes state transitions and renders observations. The pipeline records labels that are expensive or impossible to obtain reliably in the real world, such as exact object pose, contact pairs and segmentation.",
          "Procedural generation changes object identity, placement, lighting, camera pose and clutter. Domain randomization varies physical and visual parameters to prevent overfitting to one simulation. Digital twins instead attempt to reproduce a particular real workcell. Learned generators can expand textures, scenes or trajectories, but their outputs must still be checked for action consistency and physics."
        ],
        "subsections": [
          {
            "heading": "Synthetic tactile and force data",
            "paragraphs": [
              "Contact engines can output forces, pressure approximations and collision geometry. More detailed tactile simulation models elastomer deformation or optical sensor images. These signals are useful for pre-training, but small errors in compliance and friction can change insertion or slip behavior. Real tactile calibration remains necessary."
            ]
          },
          {
            "heading": "Synthetic annotations",
            "paragraphs": [
              "Simulation provides perfect labels relative to its own state depth, masks, poses and contacts. Those labels become imperfect relative to reality when assets, materials or sensors are wrong. The label is exact in the simulated world, not necessarily accurate for the target world."
            ]
          }
        ]
      },
      {
        "heading": "Comparing the main platforms",
        "paragraphs": [
          "Isaac Sim and Isaac Lab combine high-quality rendering, synthetic sensors and scalable robot-learning workflows. MuJoCo prioritizes efficient articulated dynamics and remains widely used for control. ManiSkill and SAPIEN provide manipulation environments and benchmarks. Habitat and iGibson focus on embodied navigation and indoor interaction, while Gazebo emphasizes robot integration and ROS-compatible engineering.",
          "Genesis and other newer GPU simulators target high-throughput physical simulation. PyBullet remains accessible for education and many control tasks. Selection should begin with the dominant failure mode contact-rich assembly needs different validation from visual navigation, and whole-body humanoid control needs different throughput and actuator modeling from a perception dataset."
        ],
        "sources": [
          {
            "label": "Isaac Sim",
            "url": "https://developer.nvidia.com/isaac/sim"
          },
          {
            "label": "Isaac Lab",
            "url": "https://isaac-sim.github.io/IsaacLab/"
          },
          {
            "label": "MuJoCo",
            "url": "https://mujoco.readthedocs.io/"
          },
          {
            "label": "ManiSkill",
            "url": "https://maniskill.ai/"
          }
        ]
      },
      {
        "heading": "Simulation fidelity the errors that matter",
        "paragraphs": [
          "Rigid geometry and visual appearance are only part of the transfer problem. Friction, backlash, joint damping, motor saturation, control delay, structural compliance and sensor filtering influence real motion. Deformable objects add material state that many rigid-body tasks ignore. Lighting and lens effects matter for vision, while contact solver settings matter for force.",
          "A useful fidelity study perturbs one parameter, measures policy sensitivity and compares simulated outputs with logged real trajectories. Visual similarity alone can hide wrong dynamics. Conversely, a simple simulator can transfer well when the policy uses robust state features and the task does not depend on detailed rendering."
        ]
      },
      {
        "heading": "Domain randomization and calibration",
        "paragraphs": [
          "Domain randomization trains across a distribution of possible worlds. The goal is for the real system to appear as one plausible sample. Randomization should be informed by measurements camera noise, mass range, friction, latency and actuator strength. Extremely broad or physically impossible ranges can waste capacity and obscure useful structure.",
          "System identification estimates real parameters from experiments. Calibration aligns cameras, robot geometry and sensors. Residual learning can compensate for errors left by the simulator. These techniques are complementary randomization addresses uncertainty, while calibration narrows it."
        ]
      },
      {
        "heading": "How much real data is still needed?",
        "paragraphs": [
          "There is no universal percentage. The amount depends on how sensitive the task is to unmodeled physics and how close the simulation is to the target robot. Vision-only detection may need real images for appearance calibration. Locomotion may transfer after system identification and randomization but still needs real disturbance testing. Insertion and deformable manipulation usually require more target contact data.",
          "A disciplined workflow reserves real data for measurement, adaptation and validation rather than trying to eliminate it. Start with a calibration set, train in simulation, evaluate on held-out real trials, collect failure cases and fine-tune or update the simulator. The relevant metric is real performance per hour of real data, not the synthetic-to-real ratio alone."
        ]
      },
      {
        "heading": "Failure modes and practical applications",
        "paragraphs": [
          "Policies can exploit simulator artifacts, such as unrealistic contact bounce or exact state variables unavailable on hardware. Generated demonstrations may contain unreachable paths. Assets can have wrong mass or collision meshes. Sensor noise may be independent in simulation even though real errors are correlated. A digital twin may become stale after the workcell changes.",
          "Synthetic data is credible for perception pre-training, locomotion, navigation, grasp candidate generation, policy stress testing and rare-event coverage. It is especially valuable when paired with real logs and explicit validation. It is not evidence of deployment until the resulting policy is tested on the physical system under relevant conditions."
        ]
      }
    ],
    "limitations": [
      "Simulator names do not determine fidelity; task assets, settings and controllers matter.",
      "Performance benchmarks across engines often use different hardware, scenes and code.",
      "Synthetic labels are exact only relative to the simulated state.",
      "Deformable contact, wear and long-duration hardware effects remain difficult to reproduce.",
      "The amount of real data required is task- and embodiment-specific."
    ],
    "conclusion": [
      "Synthetic robotics data provides scale, exact labels and controlled variation that real hardware cannot match economically. It is most effective when the simulator is designed around the task’s important physics and when the generated actions are valid for the target robot.",
      "The main risk is false confidence. Photorealistic images can coexist with wrong contact, friction or actuator timing, and millions of samples can amplify that error. Strong pipelines measure the real system, randomize plausible uncertainty, validate on held-out hardware trials and collect the failures simulation missed. Synthetic data can reduce real data requirements, but it cannot define reality by itself. The practical goal is not zero real data; it is better real-world performance for each hour of expensive physical collection."
    ],
    "faqs": [
      {
        "question": "What is synthetic robotics data?",
        "answer": "Synthetic robotics data is generated in simulation or by another computational process rather than recorded directly from the target robot in the real world. It can include images, depth, segmentation, trajectories, actions, contacts and labels. Its usefulness depends on whether the simulated sensors, dynamics, assets and task distribution match the intended application."
      },
      {
        "question": "Which simulator is best for robot learning?",
        "answer": "There is no universal best simulator. Isaac Sim and Isaac Lab emphasize integrated sensors, rendering and scalable learning. MuJoCo is strong for articulated control. ManiSkill and SAPIEN target manipulation, Habitat targets navigation and Gazebo supports systems integration. The correct choice follows the task’s contact, sensor, throughput and deployment requirements."
      },
      {
        "question": "What is domain randomization?",
        "answer": "Domain randomization varies simulation parameters such as lighting, texture, mass, friction, camera pose and actuator behavior during training. The objective is to make the real system fall inside the trained distribution. Randomization is most effective when ranges are based on measurements; arbitrary extreme variation can reduce learning efficiency without improving transfer."
      },
      {
        "question": "Can synthetic data replace real robot data?",
        "answer": "Usually not completely. Synthetic data can pre-train perception and policies, generate rare cases and reduce hardware collection. Real data is still needed for calibration, system identification, target-domain adaptation and validation. Contact-rich, deformable and safety-critical tasks generally require more real evidence than tasks dominated by geometry or visual recognition."
      },
      {
        "question": "Does photorealism improve sim-to-real transfer?",
        "answer": "Photorealism can help image-based policies when texture, lighting and sensor appearance matter, but it does not guarantee correct dynamics. A visually accurate scene may have wrong friction, mass, compliance or latency. Transfer depends on the variables used by the policy and the physical sensitivities of the task, not image quality alone."
      },
      {
        "question": "How should synthetic data quality be measured?",
        "answer": "Measure downstream performance on held-out real trials and compare it with real-only and mixed-data baselines. Inspect whether synthetic trajectories are physically reachable, whether sensor noise resembles real logs and whether failure modes transfer. Data volume, render quality and simulator frame rate are secondary to validated target-system performance."
      }
    ],
    "methodology": [
      "Platforms were compared using official documentation and project pages. The table describes core strengths, not every plugin or third-party environment. Claims about transfer are framed as task-dependent because a shared independent benchmark across all simulators does not exist.",
      "The article separates simulated success from real-robot validation and avoids converting synthetic sample counts into claims of real experience. Verification date July 11, 2026."
    ],
    "sourceLinks": [
      {
        "label": "NVIDIA",
        "organization": "NVIDIA",
        "documentTitle": "Isaac Sim official platform",
        "date": "Accessed July 11, 2026",
        "url": "https://developer.nvidia.com/isaac/sim"
      },
      {
        "label": "Isaac Lab contributors",
        "organization": "NVIDIA and open-source contributors",
        "documentTitle": "Isaac Lab documentation",
        "date": "Accessed July 11, 2026",
        "url": "https://isaac-sim.github.io/IsaacLab/"
      },
      {
        "label": "Google DeepMind",
        "organization": "Google DeepMind",
        "documentTitle": "MuJoCo documentation",
        "date": "Accessed July 11, 2026",
        "url": "https://mujoco.readthedocs.io/"
      },
      {
        "label": "ManiSkill team",
        "organization": "SAPIEN and academic collaborators",
        "documentTitle": "ManiSkill",
        "date": "Accessed July 11, 2026",
        "url": "https://maniskill.ai/"
      },
      {
        "label": "Habitat team",
        "organization": "Meta AI and academic partners",
        "documentTitle": "AI Habitat",
        "date": "Accessed July 11, 2026",
        "url": "https://aihabitat.org/"
      },
      {
        "label": "Stanford Vision and Learning Lab",
        "organization": "Stanford University",
        "documentTitle": "iGibson",
        "date": "Accessed July 11, 2026",
        "url": "https://stanfordvl.github.io/iGibson/"
      },
      {
        "label": "Open Robotics",
        "organization": "Open Robotics",
        "documentTitle": "Gazebo",
        "date": "Accessed July 11, 2026",
        "url": "https://gazebosim.org/"
      },
      {
        "label": "Genesis team",
        "organization": "Genesis project",
        "documentTitle": "Genesis Embodied AI",
        "date": "Accessed July 11, 2026",
        "url": "https://genesis-embodied-ai.github.io/"
      },
      {
        "label": "Bullet Physics contributors",
        "organization": "Bullet Physics",
        "documentTitle": "PyBullet and Bullet3",
        "date": "Accessed July 11, 2026",
        "url": "https://github.com/bulletphysics/bullet3"
      }
    ],
    "internalLinks": [
      {
        "label": "Real2Sim2Real pipelines for calibrated digital twins",
        "path": "/robotics-news/real2sim2real"
      },
      {
        "label": "World action models and learned predictive dynamics",
        "path": "/robotics-news/world-action-models"
      },
      {
        "label": "Cross-embodiment learning from heterogeneous robot data",
        "path": "/robotics-news/cross-embodiment-learning"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot synthetic-data factory graphic",
        "alt": "Simulator producing varied robot scenes, trajectories, sensor streams and labels before real validation",
        "source": "TechniaHQRobot original based on official simulator documentation",
        "sourceUrl": "https://developer.nvidia.com/isaac/sim"
      },
      {
        "type": "Secondary media",
        "caption": "Official Isaac Sim robotics scene",
        "alt": "Photorealistic simulated robot workcell with virtual sensors",
        "source": "NVIDIA",
        "sourceUrl": "https://developer.nvidia.com/isaac/sim"
      },
      {
        "type": "Secondary media",
        "caption": "Official ManiSkill manipulation environments",
        "alt": "Several simulated manipulation tasks and robot embodiments",
        "source": "ManiSkill team",
        "sourceUrl": "https://maniskill.ai/"
      },
      {
        "type": "Original chart",
        "caption": "Simulator selection matrix",
        "alt": "Matrix comparing physics throughput, rendering, navigation, manipulation and systems integration",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://mujoco.readthedocs.io/"
      },
      {
        "type": "Architecture",
        "caption": "Synthetic-to-real validation loop",
        "alt": "Diagram showing scene generation, randomization, policy training, real test, failure logging and simulator update",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://isaac-sim.github.io/IsaacLab/"
      }
    ],
    "structuredDataSummary": [
      "Article schema contains the synthetic-data headline and publication metadata.",
      "FAQPage schema contains the six visible simulator and data questions.",
      "BreadcrumbList schema follows Home, Robotics News and Synthetic Robotics Data."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "Each listed platform is documented by its official project or documentation site.",
        "Simulation-only data and real-robot evidence are clearly separated.",
        "The article does not equate photorealism with physical fidelity."
      ],
      "unconfirmed": [
        "Cross-platform performance is not independently comparable.",
        "New simulator releases may change feature and licensing details.",
        "No universal amount of real data is sufficient for all tasks."
      ],
      "fastChanging": [
        "Simulator versions, APIs and GPU requirements change frequently.",
        "Recent platforms such as Genesis continue to evolve rapidly."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-10-11",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "generalist-robot-policies",
    "slug": "generalist-robot-policies",
    "path": "/robotics-news/generalist-robot-policies",
    "title": "Generalist Robot Policies",
    "headline": "Generalist Robot Policies General Across Which Tasks, Robots and Environments?",
    "seoTitle": "Generalist Robot Policies Capabilities and Real Limits",
    "description": "A methodology for evaluating generalist robot policies across tasks, objects, environments, embodiments, action spaces, adaptation and real-world recovery.",
    "primaryKeyword": "generalist robot policies",
    "keywords": [
      "generalist robot policy",
      "multi-task robot learning",
      "robot policy generalization",
      "Octo policy",
      "OpenVLA",
      "pi0.5",
      "GR00T",
      "Gemini Robotics",
      "robot zero shot learning",
      "cross-embodiment policy"
    ],
    "searchQuestions": [
      "What is a generalist robot policy?",
      "How is robot policy generalization measured?",
      "Can one policy control different robots?",
      "What does zero-shot mean in robot learning?",
      "Which generalist robot policies are open source?"
    ],
    "longTailKeywords": [
      "generalist robot policy across tasks versus robots",
      "how to evaluate zero shot robot policy claims",
      "multi task robot policy comparison methodology",
      "generalist robot policy error recovery and memory"
    ],
    "entities": [
  "RoboCat",
  "RT-X",
  "Octo",
  "OpenVLA",
  "π0.5",
  "GR00T N1.7",
  "Gemini Robotics 2",
  "Helix"
],
    "imageAlt": "Matrix measuring a robot policy across tasks, objects, environments, embodiments, sensors and task duration",
    "introduction": [
      "A policy can complete one hundred tasks in the same kitchen and still fail when the camera moves ten centimeters. Calling that system generalist hides which dimension actually changed. In robot learning, generality can refer to tasks, objects, rooms, instructions, sensors, embodiments or time horizon, and success on one axis does not imply success on the others.",
      "This article defines a generalist robot policy as a control model evaluated across a declared set of variation axes. It compares major policy families using a common methodology, covering pre-training, fine-tuning, prompting, diffusion, transformers, action spaces, memory and recovery. It also separates zero-shot object variation from new-task and new-robot transfer. A useful generalist claim specifies its coverage and failure boundary clearly enough for another team to reproduce."
    ],
    "keyFindings": [
      "Generality must be reported as a vector across tasks, objects, environments, embodiments, sensors and horizon.",
      "A policy general across tasks on one arm is not automatically general across robots.",
      "Zero-shot claims are meaningful only when the unseen variable and adaptation budget are stated.",
      "Long-horizon recovery and memory remain weaker than short episodic task success.",
      "Open policies improve reproducibility, while closed systems often publish broader but less inspectable demonstrations."
    ],
    "comparisonTables": [
  {
    "id": "generalist-policy-comparison",
    "title": "Generalist policy evidence across different axes",
    "columns": [
      "Policy",
      "Task breadth",
      "Robot breadth",
      "Environment evidence",
      "Adaptation",
      "Open status",
      "Main boundary"
    ],
    "rows": [
      [
        "RoboCat",
        "Multi-task manipulation reported",
        "Multiple robot arms through self-improvement experiments",
        "Controlled lab environments",
        "Fine-tuning and self-generated data",
        "Weights unavailable",
        "Limited public reproducibility"
      ],
      [
        "RT-X",
        "Broad shared task mixture",
        "22 embodiments represented in project data",
        "Distributed laboratory datasets",
        "Cross-dataset transfer and target adaptation",
        "Dataset/project artifacts public",
        "Heterogeneous protocols and robot coverage"
      ],
      [
        "Octo",
        "Many language-conditioned manipulation tasks",
        "Nine training platforms reported",
        "Multiple labs and target setups",
        "Designed for target fine-tuning",
        "Code and checkpoints public",
        "Requires embodiment-specific adaptation"
      ],
      [
        "OpenVLA",
        "Broad Open X-Embodiment pre-training",
        "Multiple source robots; target evaluation through fine-tuning",
        "Lab manipulation settings",
        "Full or parameter-efficient fine-tuning",
        "Code and 7B weights public",
        "Compute and control latency"
      ],
      [
        "π0.5",
        "Broad household and manipulation tasks reported",
        "Multiple company robot types",
        "New-home generalization reported",
        "Public fine-tuning workflow in openpi",
        "Code and checkpoints public",
        "Company-reported protocols"
      ],
      [
        "GR00T N1.7",
        "Humanoid, semi-humanoid and bimanual task families",
        "Multiple embodiments supported through post-training",
        "Simulation and real-robot evaluation paths",
        "Embodiment conditioning and fine-tuning",
        "Public code and checkpoints; code Apache 2.0, weights NVIDIA Open Model License",
        "Backbone access approval and target-robot validation are separate requirements"
      ],
      [
        "Gemini Robotics 2",
        "Whole-body and dexterous manipulation tasks reported",
        "Apollo 2, Franka and dexterous hand configurations shown",
        "Controlled real-robot evaluations",
        "Partner adaptation paths",
        "VLA available to early-access partners",
        "Training scale and long-duration failure rates unavailable"
      ],
      [
        "Helix",
        "Humanoid upper- and whole-body tasks",
        "Figure humanoid only in public evidence",
        "Company environments",
        "Proprietary continual improvement",
        "Closed",
        "General across tasks, not across robots"
      ]
    ],
    "description": "The table avoids a single generalist score. Each row identifies the axes supported by public evidence.",
    "note": "The GR00T N1.7 and Gemini Robotics 2 rows were checked against their linked official documentation on October 11, 2026. Other rows retain the source dates listed below. Task counts and success rates are not directly comparable."
  }
],
    "sections": [
      {
        "heading": "Definition generalist is a set of axes, not a label",
        "paragraphs": [
          "A generalist robot policy maps observations and goals to actions across more than one task distribution. The claim should name the axes number of tasks, object diversity, environment changes, robot embodiments, sensors, language forms and task duration. A model can be broad on one axis and narrow on another.",
          "A fixed library of scripted behaviors is not a learned generalist policy. A large language model that selects tools is a planner unless it generates grounded robot control. A policy trained on one arm can be a task generalist, but it should not be described as embodiment-general without transfer evidence."
        ]
      },
      {
        "heading": "How generalist policies are trained",
        "paragraphs": [
          "Multi-task datasets combine trajectories with language or goal labels. Transformers model long multimodal sequences, while diffusion decoders represent several plausible action trajectories. Pre-training learns shared features and action patterns; fine-tuning aligns the policy with a target robot and task distribution. Prompting changes task intent but rarely resolves a new action space.",
          "Few-shot adaptation uses a small target dataset. Zero-shot evaluation uses no target examples for the declared variable. Memory may be an observation window, recurrent state or external task record. Long-horizon policies often split planning from execution because one model call cannot reliably maintain state through many contacts and corrections."
        ]
      },
      {
        "heading": "A common evaluation methodology",
        "paragraphs": [
          "Report a coverage vector tasks, objects, environments, embodiments, sensors and maximum uninterrupted horizon. For each test, identify what was held out and whether fine-tuning occurred. Use the same reset policy, intervention definition and number of trials when comparing baselines.",
          "Measure task completion, unsafe contacts, recovery, latency and adaptation data. A policy that succeeds after a human resets every failed grasp is different from one that detects the drop and retries. Benchmark performance should be separated from deployment evidence such as repeated shifts or operation around workers."
        ]
      },
      {
        "heading": "Key policies and their actual scope",
        "paragraphs": [
          "RoboCat, RT-X, Octo and OpenVLA established multi-task and multi-robot learning paths. Octo and OpenVLA provide reusable open checkpoints, while RT-X emphasizes the shared data mixture. π0.5, GR00T N1.7 and Gemini Robotics 2 report broader household or humanoid capabilities with different access models.",
          "Figure’s Helix is general across a growing set of tasks on one humanoid embodiment. That is technically meaningful but different from cross-robot generality. The distinction allows a strong task-general system to be described accurately without inflating its embodiment coverage."
        ],
        "sources": [
          {
            "label": "RoboCat",
            "url": "https://deepmind.google/discover/blog/robocat-a-self-improving-robotic-agent/"
          },
          {
            "label": "Open X-Embodiment",
            "url": "https://robotics-transformer-x.github.io/"
          },
          {
            "label": "Octo",
            "url": "https://octo-models.github.io/"
          },
          {
            "label": "π0.5",
            "url": "https://www.physicalintelligence.company/blog/pi05"
          }
        ]
      },
      {
        "heading": "Generalist across tasks is not generalist across robots",
        "paragraphs": [
          "Different robots expose different joint spaces, grippers, camera positions, velocity limits and control rates. End-effector deltas can provide a common task-space interface, but identical commands may produce different contact because reach, compliance and calibration differ. An embodiment token can tell the model which robot is active; it does not guarantee sufficient data for that body.",
          "Cross-robot evaluation should include a held-out embodiment and state how much adaptation was allowed. Transferring semantic perception is easier than transferring precise motor behavior. A policy may recognize the correct object immediately while requiring substantial target demonstrations to grasp it."
        ]
      },
      {
        "heading": "Failure modes and recovery",
        "paragraphs": [
          "Multi-task training can create interference improving one task reduces another. Rare tasks are underrepresented. Language aliases may map to different actions. Long tasks accumulate pose error and lose state after interruption. A policy may repeat a failed action because the observation window does not include the original goal or prior failure.",
          "Recovery needs explicit training data, memory and safe exploratory actions. Failure datasets are often smaller than successful demonstrations because operators reset quickly. A generalist policy that cannot recognize uncertainty or request assistance remains fragile outside prepared test distributions."
        ]
      },
      {
        "heading": "Practical applications",
        "paragraphs": [
          "Generalist policies are credible for flexible manipulation cells, research platforms and robots that need several related tasks with shared objects and tooling. They can reduce separate policy maintenance and improve adaptation to product variants.",
          "Open-ended home assistance and unrestricted industrial autonomy remain experimental. Deployment should define an approved task envelope, monitor confidence and provide deterministic limits, remote support or safe stopping outside that envelope."
        ]
      }
    ],
    "limitations": [
      "No universal benchmark spans tasks, robots, sensors, environments and long horizons.",
      "Task counts may include minor variations rather than distinct skills.",
      "Closed systems do not publish complete training distributions or failure logs.",
      "Open models still require target hardware, calibration and adaptation data.",
      "Long-duration reliability and recovery are reported less often than short episode success."
    ],
    "conclusion": [
      "A useful generalist-policy claim states its tested coverage across tasks, objects, environments, embodiments, sensors and duration. That makes broad task coverage on one robot distinguishable from cross-robot generalization.",
      "Current policies show multi-task and, in some cases, multi-robot transfer. Octo and OpenVLA provide inspectable open baselines. GR00T N1.7 publishes code and weights under separate licenses, while Gemini Robotics 2 uses controlled access. Helix demonstrates task breadth on one humanoid. Evaluation still needs sustained execution, recovery under distribution shift and a stated adaptation budget."
    ],
    "faqs": [
  {
    "question": "What is a generalist robot policy?",
    "answer": "A generalist robot policy is a learned controller evaluated across a declared range of tasks, objects, environments or robots. The term should specify which dimensions vary and which remain fixed. A policy that performs many tasks on one arm can be task-generalist while still being narrow in embodiment, sensors and environment."
  },
  {
    "question": "How is robot policy generalization measured?",
    "answer": "Researchers hold out a variable such as an object, task, room or robot, then evaluate without or with limited target adaptation. A useful report includes trials, success, interventions, unsafe contacts and adaptation data. Generalization results are only comparable when the held-out variable, reset rules and hardware conditions are similar."
  },
  {
    "question": "Can one policy control different robots?",
    "answer": "Yes, but usually through action normalization, embodiment tokens, task-space commands, adapters or target fine-tuning. Robots have different kinematics, grippers, cameras and control rates. Semantic features often transfer more easily than precise motor commands, so cross-robot success should report how much target data and engineering were still required."
  },
  {
    "question": "What does zero-shot mean in robot learning?",
    "answer": "Zero-shot means no training examples were used for a specified test variable. It can refer to a new object, instruction, task, environment or robot. The term is incomplete unless the paper states which variable was unseen. Most zero-shot robot results still use familiar hardware and a known action space."
  },
  {
    "question": "Are generalist robot policies autonomous?",
    "answer": "They can generate actions without continuous teleoperation, but system autonomy depends on task selection, supervision, resets, safety controls and recovery. A human may still start the episode, prepare objects or intervene after failure. The policy’s action output should not be confused with an entirely unsupervised deployment."
  },
  {
    "question": "Which generalist robot policies are open?",
    "answer": "Octo, OpenVLA and openpi provide public code and checkpoints; openpi includes π0.5. Open X-Embodiment provides shared data. Check licenses, released components and target-robot interfaces separately. Public weights do not establish deployment reliability."
  }
],
    "methodology": [
  "Policies are compared as coverage vectors rather than ranked by one score. The review separates task breadth, embodiment breadth, environment variation, adaptation and recovery.",
  "The original July 11, 2026 source review is retained for the older project entries. The GR00T N1.7 and Gemini Robotics 2 rows use the separately dated sources linked below. Company-reported demonstrations are not independent deployment evidence.",
  "The October 11, 2026 targeted update checks openpi availability, the GR00T N1.7 repository and the July 30 Gemini Robotics 2 release. NVIDIA’s license section separates the code and model-weight terms, and its setup instructions require access to a gated backbone. This update does not revalidate every older model entry or reproduce any benchmark."
],
    "sourceLinks": [
  {
    "label": "Google DeepMind",
    "organization": "Google DeepMind",
    "documentTitle": "RoboCat a self-improving robotic agent",
    "date": "June 2023 · accessed July 11, 2026",
    "url": "https://deepmind.google/discover/blog/robocat-a-self-improving-robotic-agent/"
  },
  {
    "label": "Open X-Embodiment Collaboration",
    "organization": "Google DeepMind and partners",
    "documentTitle": "Open X-Embodiment and RT-X",
    "date": "2023 · accessed July 11, 2026",
    "url": "https://robotics-transformer-x.github.io/"
  },
  {
    "label": "Octo Model Team",
    "organization": "UC Berkeley and collaborators",
    "documentTitle": "Octo",
    "date": "2024 · accessed July 11, 2026",
    "url": "https://octo-models.github.io/"
  },
  {
    "label": "OpenVLA team",
    "organization": "Stanford and UC Berkeley",
    "documentTitle": "OpenVLA",
    "date": "2024 · accessed July 11, 2026",
    "url": "https://github.com/openvla/openvla"
  },
  {
    "label": "Physical Intelligence",
    "organization": "Physical Intelligence",
    "documentTitle": "π0.5",
    "date": "April 22, 2025 · accessed July 11, 2026",
    "url": "https://www.physicalintelligence.company/blog/pi05"
  },
  {
    "label": "NVIDIA Isaac GR00T repository",
    "organization": "NVIDIA",
    "documentTitle": "GR00T N1.7: model checkpoints, adaptation and licenses",
    "date": "Accessed October 11, 2026",
    "url": "https://github.com/NVIDIA/Isaac-GR00T"
  },
  {
    "label": "Google DeepMind",
    "organization": "Google DeepMind",
    "documentTitle": "Gemini Robotics 2 brings whole body intelligence to robots",
    "date": "July 30, 2026 · accessed October 11, 2026",
    "url": "https://deepmind.google/blog/gemini-robotics-2-brings-whole-body-intelligence-to-robots/"
  },
  {
    "label": "Figure AI",
    "organization": "Figure AI",
    "documentTitle": "Helix",
    "date": "February 20, 2025 · accessed July 11, 2026",
    "url": "https://www.figure.ai/news/helix"
  },
  {
    "label": "Official openpi repository",
    "organization": "Physical Intelligence",
    "documentTitle": "openpi model releases and checkpoints",
    "date": "Accessed October 11, 2026",
    "url": "https://github.com/Physical-Intelligence/openpi"
  }
],
    "internalLinks": [
      {
        "label": "Robot foundation models and pre-training evidence",
        "path": "/robotics-news/robot-foundation-models"
      },
      {
        "label": "Cross-embodiment learning across robot bodies",
        "path": "/robotics-news/cross-embodiment-learning"
      },
      {
        "label": "Vision-language-action models and action generation",
        "path": "/robotics-news/vision-language-action-models"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot generality coverage matrix",
        "alt": "Matrix with tasks, objects, environments, robots, sensors and horizon as separate axes",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://octo-models.github.io/"
      },
      {
        "type": "Secondary media",
        "caption": "Open X-Embodiment robot diversity montage",
        "alt": "Multiple robot platforms represented in the RT-X dataset mixture",
        "source": "Open X-Embodiment Collaboration",
        "sourceUrl": "https://robotics-transformer-x.github.io/"
      },
      {
        "type": "Secondary media",
        "caption": "Physical Intelligence π0.5 home task example",
        "alt": "Mobile manipulator performing a language-conditioned task in a new home",
        "source": "Physical Intelligence",
        "sourceUrl": "https://www.physicalintelligence.company/blog/pi05"
      },
      {
        "type": "Original chart",
        "caption": "Generality radar without a composite score",
        "alt": "Radar plot showing six independent coverage axes for representative policies",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://github.com/openvla/openvla"
      },
      {
        "type": "Architecture",
        "caption": "Generalist policy pre-training, adapter and recovery loop",
        "alt": "Diagram from mixed datasets to shared policy, embodiment adapter, controller and failure memory",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://research.nvidia.com/labs/gear/gr00t-n1_6/"
      }
    ],
    "structuredDataSummary": [
      "Article schema represents the methodology-focused technical guide.",
      "FAQPage schema contains six visible questions about generalist policies.",
      "BreadcrumbList schema follows Home, Robotics News and Generalist Robot Policies."
    ],
    "factCheck": {
  "verifiedDate": "July 11, 2026 baseline; targeted model-source review October 11, 2026",
  "confirmed": [
    "The comparison separates task breadth from robot breadth.",
    "October 11 targeted checks cover openpi availability, GR00T N1.7 documentation and licenses, and the Gemini Robotics 2 release and VLA access statement.",
    "Zero-shot is defined by the specific held-out variable."
  ],
  "unconfirmed": [
    "Cross-system success rates and task counts are not directly comparable.",
    "Closed-system training distributions and failure logs are incomplete.",
    "Long-horizon recovery evidence remains limited."
  ],
  "fastChanging": [
    "Policies and access programs can receive new versions.",
    "New held-out-embodiment evaluations may change coverage assessments."
  ]
}
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-07-11",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "real2sim2real",
    "slug": "real2sim2real",
    "path": "/robotics-news/real2sim2real",
    "title": "Real2Sim2Real",
    "headline": "Real2Sim2Real Building Digital Twins That Improve Real Robot Policies",
    "seoTitle": "Real2Sim2Real for Robot Learning and Accurate Digital Twins",
    "description": "A complete Real2Sim2Real pipeline covering scene capture, system identification, NeRF and Gaussian splatting, simulation training, transfer and validation.",
    "primaryKeyword": "Real2Sim2Real",
    "keywords": [
      "Real2Sim2Real",
      "robot digital twin",
      "real to sim robot learning",
      "sim to real robotics",
      "Gaussian splatting robotics",
      "NeRF robot simulation",
      "system identification robot",
      "domain randomization",
      "SimFoundry",
      "RoboGSim"
    ],
    "searchQuestions": [
      "What is Real2Sim2Real?",
      "How is a robot digital twin created?",
      "Why can a photorealistic twin be physically wrong?",
      "What is system identification in robotics?",
      "How are policies transferred back to the real robot?"
    ],
    "longTailKeywords": [
      "Real2Sim2Real pipeline for industrial robot learning",
      "Gaussian splatting digital twin for robotics",
      "system identification and residual learning sim to real",
      "visually accurate digital twin physically inaccurate"
    ],
    "entities": [
      "SimFoundry",
      "Scalable Real2Sim",
      "RoboGSim",
      "PIN-WM",
      "TwinAligner",
      "D-REX",
      "Video2Sim2Real"
    ],
    "imageAlt": "Real workcell captured into a digital twin, varied in simulation and transferred back to the physical robot",
    "introduction": [
      "A factory digital twin can match every visible surface and still predict the wrong grasp because the object mass, gripper compliance or control delay is incorrect. Real2Sim2Real begins with a real scene, reconstructs a usable simulation, trains or evaluates a robot policy there and transfers the result back to the original hardware.",
      "This article follows the full pipeline data capture, asset reconstruction, robot calibration, system identification, variation generation, simulation training, real transfer, validation and correction. It explains where neural radiance fields, Gaussian splatting, motion capture and learned residuals fit, and why visual reconstruction is only one layer of a functional twin. Recent research systems are compared by what they capture, what they rebuild, what is trained in simulation and what real-world adjustment remains after transfer."
    ],
    "keyFindings": [
      "Real2Sim2Real differs from generic sim-to-real because the simulation is grounded in measurements from a specific real scene or system.",
      "NeRF and Gaussian splatting improve visual reconstruction but do not identify mass, friction, compliance or actuator delay.",
      "System identification and calibration are as important as geometry for control transfer.",
      "A useful twin represents uncertainty and change rather than freezing one perfect-looking scene.",
      "The final evidence is performance on held-out real trials, not simulation success or rendering quality."
    ],
    "comparisonTables": [
      {
        "id": "real2sim-project-comparison",
        "title": "Recent Real2Sim2Real and digital-twin learning systems",
        "columns": [
          "Project",
          "Real capture",
          "Simulation representation",
          "Training or planning use",
          "Real transfer evidence",
          "Public status",
          "Main limitation"
        ],
        "rows": [
          [
            "SimFoundry",
            "Recorded real environments and assets",
            "Scalable reconstructed simulation worlds",
            "Generate robot-learning environments and data",
            "NVIDIA reports downstream physical AI workflows",
            "Official project page",
            "Full production pipeline details are limited"
          ],
          [
            "Scalable Real2Sim",
            "Real scenes and robot observations",
            "Reconstructed task environments",
            "Policy training and evaluation",
            "Author-reported real transfer",
            "Paper",
            "Task-specific reconstruction assumptions"
          ],
          [
            "RoboGSim",
            "Images or video of real scenes",
            "Gaussian-splatting-based robot simulation",
            "Visual and manipulation policy training",
            "Author-reported real robot results",
            "Paper",
            "Physical properties need separate estimation"
          ],
          [
            "PIN-WM",
            "Real interaction and scene data",
            "Physics-informed neural world representation",
            "Prediction and planning",
            "Author-reported robot evaluation",
            "RSS paper",
            "Learned dynamics remain bounded by training interactions"
          ],
          [
            "TwinAligner",
            "Real and simulated trajectory pairs",
            "Aligned digital-twin representation",
            "Reduce simulation-real mismatch",
            "Author-reported transfer",
            "Paper",
            "Needs representative paired data"
          ],
          [
            "D-REX",
            "Real demonstrations and reconstructed scene",
            "Digital replica with generated variations",
            "Policy learning and data expansion",
            "Author-reported real tasks",
            "Paper",
            "Recent work with limited independent replication"
          ],
          [
            "Video2Sim2Real",
            "Real video",
            "Reconstructed simulated task and motion",
            "Train policies from video-derived scenes",
            "Author-reported transfer",
            "Paper",
            "Video cannot recover all hidden physics"
          ]
        ],
        "description": "Projects are compared by the information moved from reality into simulation and by the evidence transferred back.",
        "note": "The listed systems use different robots and tasks; success rates are not directly comparable."
      }
    ],
    "sections": [
      {
        "heading": "Definition what makes the loop Real2Sim2Real",
        "paragraphs": [
          "Real2Sim2Real starts with measurements from a real robot, scene or task. Those measurements create or calibrate a simulation. The simulation then generates training experience, tests plans or produces variations. A policy or model returns to the physical system for validation and further correction.",
          "A generic simulator trained without target measurements is sim-to-real, not necessarily Real2Sim2Real. A photogrammetry model used only for visualization is a digital replica, not a control twin. The loop is complete only when simulated learning is evaluated on the real system and the mismatch informs another update."
        ]
      },
      {
        "heading": "Step 1 capture the real system",
        "paragraphs": [
          "Capture may include calibrated RGB-D images, video, LiDAR, robot joint logs, force data, motor current, object trajectories and motion-capture markers. Static images recover appearance and geometry. Dynamic interactions reveal friction, mass, damping and compliance. The robot model must include link dimensions, joint limits, controller behavior and sensor timing.",
          "Data should cover the states that matter to the task. A twin built from one empty workcell scan may fail when bins, cables or people alter occlusion. For contact tasks, collect pushes, grasps or free-motion responses that excite the physical parameters being estimated."
        ]
      },
      {
        "heading": "Step 2 reconstruct geometry, appearance and physics",
        "paragraphs": [
          "Meshes, depth fusion, neural radiance fields and Gaussian splatting reconstruct scene appearance and view-dependent detail. Collision geometry often needs simplification because a visually dense representation is not directly suitable for physics. Articulated objects require joint axes and limits, while deformable items require material models.",
          "System identification fits mass, inertia, friction, damping, actuator strength and delay to real trajectories. Camera and robot calibration align coordinate frames. A residual model can learn errors left by the engine. Each layer should record uncertainty because several parameter combinations can explain the same observed motion."
        ],
        "subsections": [
          {
            "heading": "NeRF and Gaussian splatting",
            "paragraphs": [
              "NeRF represents radiance as a neural field and can render novel views. Gaussian splatting represents a scene with optimized 3D Gaussians and often renders faster. Both improve visual realism and camera coverage. Neither automatically provides watertight collision meshes, object articulation or correct physical parameters."
            ]
          },
          {
            "heading": "Motion capture and asset reconstruction",
            "paragraphs": [
              "Motion capture provides metric trajectories for robots, humans and objects, but requires markers or reliable tracking. Asset reconstruction estimates shape and pose from images or scans. The simulation needs stable object identity and collision geometry, not only a visually accurate point cloud."
            ]
          }
        ]
      },
      {
        "heading": "Step 3 generate variations and train in simulation",
        "paragraphs": [
          "The reconstructed twin can vary object poses, lighting, camera noise, friction, mass and workflow timing. Controllers, planners or policies generate rollouts at scale. Demonstration augmentation changes scene layout while preserving task structure. Domain randomization should remain consistent with measured uncertainty rather than inventing arbitrary worlds.",
          "Training may use reinforcement learning, imitation, synthetic demonstrations or model predictive control. The target policy should receive observations available on the real robot. Privileged simulator state can train a teacher, but deployment requires distillation or state estimation."
        ]
      },
      {
        "heading": "Step 4 transfer, validate and correct",
        "paragraphs": [
          "The policy is deployed under a conservative safety envelope. Validation compares task success, trajectory error, contact forces, latency and failure modes with simulation predictions. A held-out real test set prevents the same logs from being used for both calibration and proof.",
          "Mismatch can be corrected through parameter updates, residual learning, target fine-tuning or expanded randomization. The loop should prioritize failure cases slips, occlusions, actuator saturation and scene changes. A twin that is not updated after maintenance or layout changes becomes stale."
        ]
      },
      {
        "heading": "Key systems and evidence",
        "paragraphs": [
          "SimFoundry focuses on scalable construction of simulation environments from real-world inputs for physical AI development. RoboGSim and related work use Gaussian-splatting representations to improve visual grounding in reconstructed scenes. PIN-WM brings physics-informed prediction into the loop, while TwinAligner targets alignment between simulated and real trajectories.",
          "D-REX and Video2Sim2Real explore data generation and policy learning from reconstructed real scenes or video. These systems demonstrate different parts of the loop rather than one standardized architecture. Their real-robot evidence remains task-specific and is reported by the authors."
        ],
        "sources": [
          {
            "label": "SimFoundry",
            "url": "https://research.nvidia.com/labs/gear/simfoundry/"
          },
          {
            "label": "Scalable Real2Sim",
            "url": "https://arxiv.org/abs/2503.00370"
          },
          {
            "label": "RoboGSim",
            "url": "https://arxiv.org/abs/2411.11839"
          },
          {
            "label": "PIN-WM",
            "url": "https://www.roboticsproceedings.org/rss21/p153.html"
          }
        ]
      },
      {
        "heading": "Why a visually accurate digital twin may still be physically wrong",
        "paragraphs": [
          "Images constrain surfaces and appearance but leave mass, friction and compliance underdetermined. Two objects can look identical and behave differently. A shiny floor can be high- or low-friction. A gripper pad may deform under load even when its mesh is rigid. Control delay and backlash are invisible in a static reconstruction.",
          "Physical accuracy therefore requires interaction data and parameter identification. The twin should be judged by predicted trajectories, contacts and sensor signals under the target actions. A beautiful render is useful for perception; it is not evidence that a learned policy will transfer."
        ]
      },
      {
        "heading": "Failure modes and practical applications",
        "paragraphs": [
          "Reconstruction holes create collision gaps. Dynamic objects move after capture. Scale drift shifts reach targets. A wrong controller model produces stable simulation and oscillatory hardware. Overfitting to one twin can reduce robustness to normal production variation. Learned residuals can also hide errors without extrapolating safely.",
          "Credible applications include workcell commissioning, synthetic perception data, grasp planning, policy stress testing and adaptation to a known site. Open-world digital twins and automatic reconstruction of deformable household tasks remain experimental. Real validation and change management are mandatory for deployment."
        ]
      }
    ],
    "limitations": [
      "No common benchmark covers reconstruction accuracy, physical identification and downstream real-robot performance together.",
      "Visual reconstruction metrics do not measure contact or control fidelity.",
      "Recent systems use different capture hardware, robots and task protocols.",
      "Hidden properties such as friction and compliance may not be identifiable from passive video.",
      "Digital twins require maintenance when the real environment or robot changes."
    ],
    "conclusion": [
      "Real2Sim2Real is a measured engineering loop capture a real system, reconstruct the variables needed for a task, train or plan in simulation, return to hardware and use the mismatch to improve the model. Its value comes from grounding synthetic scale in a specific physical target.",
      "The strongest pipelines treat geometry, appearance, dynamics, sensors and controller timing as separate calibration problems. NeRF and Gaussian splatting can improve views, but they do not supply mass, friction or contact. Real transfer must be evaluated on held-out trials with failures and safety limits included. A digital twin becomes useful for robot learning when it predicts the consequences that matter to control, not when it merely looks like the workcell."
    ],
    "faqs": [
      {
        "question": "What is Real2Sim2Real?",
        "answer": "Real2Sim2Real is a robot-learning loop that begins with data from a real scene or system, uses it to build or calibrate a simulation, trains or evaluates a policy there and transfers the result back to the real robot. Real validation then supplies errors for another simulation or policy update."
      },
      {
        "question": "How is a robot digital twin created?",
        "answer": "Engineers capture geometry, camera calibration, robot kinematics, sensor timing and interaction data. They reconstruct visual assets and collision geometry, then identify physical parameters such as mass, friction, damping and actuator delay. The twin is validated by comparing simulated trajectories and sensor outputs with held-out real experiments."
      },
      {
        "question": "Why can a photorealistic digital twin be physically wrong?",
        "answer": "Photorealistic reconstruction constrains what surfaces look like, not how they move or contact. Mass, friction, compliance, backlash and control delay can remain unknown. A twin may render the correct object and still predict the wrong grasp or push. Physical accuracy requires interaction data, system identification and task-specific validation."
      },
      {
        "question": "What is system identification?",
        "answer": "System identification estimates parameters of a physical or control model from observed inputs and outputs. In robotics, it can fit mass, inertia, friction, motor strength, damping and delay using recorded motions or contacts. The goal is not perfect parameter recovery but a model that predicts behavior accurately enough for the target task."
      },
      {
        "question": "How do policies transfer from simulation to reality?",
        "answer": "Policies transfer through calibrated observations, action interfaces and robust training distributions. Domain randomization covers uncertainty, while target fine-tuning or residual learning corrects remaining mismatch. Deployment should begin within a conservative safety envelope and compare real trajectories, contacts and failures with simulation predictions before broader use."
      },
      {
        "question": "Are NeRF and Gaussian splatting robot simulators?",
        "answer": "No. They are scene representation and rendering techniques. They can reconstruct views and support synthetic camera data, but a robot simulator also needs collision geometry, articulation, dynamics, sensors and control interfaces. Real2Sim systems often combine neural rendering with a physics engine and separate system-identification pipeline."
      }
    ],
    "methodology": [
      "Projects were included when they reconstructed or calibrated simulation from real data and reported a return to real robot evaluation. Pure rendering methods and generic sim-to-real studies were excluded from the main comparison unless they completed this loop.",
      "The review separates visual reconstruction, physical identification, policy training and real transfer. Author-reported results are not combined into one ranking. Verification date July 11, 2026."
    ],
    "sourceLinks": [
      {
        "label": "NVIDIA GEAR Lab",
        "organization": "NVIDIA",
        "documentTitle": "SimFoundry",
        "date": "Accessed July 11, 2026",
        "url": "https://research.nvidia.com/labs/gear/simfoundry/"
      },
      {
        "label": "Scalable Real2Sim authors",
        "organization": "Research collaboration",
        "documentTitle": "Scalable Real2Sim",
        "date": "March 2025 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2503.00370"
      },
      {
        "label": "RoboGSim authors",
        "organization": "Research collaboration",
        "documentTitle": "RoboGSim",
        "date": "November 2024 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2411.11839"
      },
      {
        "label": "PIN-WM authors",
        "organization": "Robotics Science and Systems",
        "documentTitle": "PIN-WM",
        "date": "RSS 2025 · accessed July 11, 2026",
        "url": "https://www.roboticsproceedings.org/rss21/p153.html"
      },
      {
        "label": "TwinAligner authors",
        "organization": "Research collaboration",
        "documentTitle": "TwinAligner",
        "date": "December 2025 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2512.19390"
      },
      {
        "label": "D-REX authors",
        "organization": "Research collaboration",
        "documentTitle": "D-REX",
        "date": "March 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/html/2603.01151"
      },
      {
        "label": "Video2Sim2Real authors",
        "organization": "Research collaboration",
        "documentTitle": "Video2Sim2Real",
        "date": "June 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/html/2606.08828"
      }
    ],
    "internalLinks": [
      {
        "label": "Synthetic robotics data and simulator fidelity",
        "path": "/robotics-news/synthetic-robotics-data"
      },
      {
        "label": "Multiview embodied reasoning and camera calibration",
        "path": "/robotics-news/multiview-embodied-reasoning"
      },
      {
        "label": "World action models and learned predictive dynamics",
        "path": "/robotics-news/world-action-models"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot Real2Sim2Real loop",
        "alt": "Real workcell captured into a digital twin, varied for training and validated back on the robot",
        "source": "TechniaHQRobot original based on cited projects",
        "sourceUrl": "https://research.nvidia.com/labs/gear/simfoundry/"
      },
      {
        "type": "Secondary media",
        "caption": "Official SimFoundry reconstructed environment example",
        "alt": "Real-world scene converted into a scalable simulation environment",
        "source": "NVIDIA Research",
        "sourceUrl": "https://research.nvidia.com/labs/gear/simfoundry/"
      },
      {
        "type": "Secondary media",
        "caption": "RoboGSim Gaussian-splatting robot scene",
        "alt": "Robot scene represented with Gaussian splats and linked to simulation",
        "source": "RoboGSim authors",
        "sourceUrl": "https://arxiv.org/abs/2411.11839"
      },
      {
        "type": "Original chart",
        "caption": "Digital-twin fidelity layers",
        "alt": "Chart separating appearance, geometry, dynamics, sensors and controller timing",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/abs/2503.00370"
      },
      {
        "type": "Architecture",
        "caption": "Complete Real2Sim2Real engineering pipeline",
        "alt": "Diagram from capture and identification to variation, training, transfer, validation and correction",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://arxiv.org/html/2603.01151"
      }
    ],
    "structuredDataSummary": [
      "Article schema describes the Real2Sim2Real technical guide.",
      "FAQPage schema contains six visible digital-twin questions.",
      "BreadcrumbList schema follows Home, Robotics News and Real2Sim2Real."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "Each main project uses real data to construct or align a simulation and reports a return to real evaluation.",
        "NeRF and Gaussian splatting are described as representations, not complete simulators.",
        "Visual and physical fidelity are evaluated as separate layers."
      ],
      "unconfirmed": [
        "Independent reproduction is limited for several recent systems.",
        "Comparable real-data budgets and transfer success rates are unavailable.",
        "Some project code, assets or trained policies are not fully released."
      ],
      "fastChanging": [
        "Digital-twin toolchains and project artifacts are evolving rapidly.",
        "New simulator integrations may alter implementation details."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-08-13",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "cross-embodiment-learning",
    "slug": "cross-embodiment-learning",
    "path": "/robotics-news/cross-embodiment-learning",
    "title": "Cross-Embodiment Learning",
    "headline": "Cross-Embodiment Learning What Transfers Between Different Robot Bodies",
    "seoTitle": "Cross-Embodiment Learning for Modern Robot Control Policies",
    "description": "How robot policies transfer across arms, grippers, humanoids and mobile manipulators using normalized actions, shared latent spaces, adapters and retargeting.",
    "primaryKeyword": "cross-embodiment learning",
    "keywords": [
      "cross-embodiment learning",
      "multi-robot learning",
      "robot action normalization",
      "embodiment tokens",
      "robot retargeting",
      "Open X-Embodiment",
      "Octo",
      "OpenVLA",
      "GR00T",
      "robot policy adapters"
    ],
    "searchQuestions": [
      "What is cross-embodiment robot learning?",
      "How can one policy work on different robots?",
      "What is action normalization?",
      "What transfers across robot bodies?",
      "Do cross-embodiment models need fine-tuning?"
    ],
    "longTailKeywords": [
      "cross embodiment robot policy action representation",
      "transfer learning between different robot arms and grippers",
      "embodiment tokens and robot specific action heads",
      "what robot skills transfer across humanoids and manipulators"
    ],
    "entities": [
      "Open X-Embodiment",
      "RT-X",
      "Octo",
      "OpenVLA",
      "GR00T N1.6",
      "OSCAR",
      "HumanEgo"
    ],
    "imageAlt": "Different robot arms, grippers and humanoids connected through a shared task representation and embodiment-specific action heads",
    "introduction": [
      "A command to move the gripper five centimeters forward has a different physical meaning on a short tabletop arm, a mobile manipulator and a humanoid with a moving torso. Cross-embodiment learning tries to reuse data or policies across those bodies without pretending their joints, cameras and end effectors are identical.",
      "This article explains action normalization, task-space control, embodiment tokens, shared latent spaces, retargeting, modular policies and robot-specific output heads. It examines multi-robot datasets and models, then separates what transfers easily, such as object semantics, from what remains embodiment-bound, such as contact geometry and torque. The evidence section asks which robots were included, whether the target robot was held out and how much fine-tuning remained. It also covers different action frequencies, dual-arm coordination, camera placement and the limits of calling a dataset cross-embodiment when its tasks do not overlap."
    ],
    "keyFindings": [
      "Visual semantics and task structure usually transfer more readily than precise motor commands.",
      "A shared action representation reduces mismatch but cannot erase different reach, compliance, grippers or control rates.",
      "Embodiment tokens identify the robot; target data is still needed when the model has not learned that body’s dynamics.",
      "Cross-embodiment evaluation should hold out a robot and report performance before and after adaptation.",
      "Dataset diversity is useful only when tasks and labels are aligned enough for the model to learn shared structure."
    ],
    "comparisonTables": [
      {
        "id": "cross-embodiment-comparison",
        "title": "Cross-embodiment datasets and policy systems",
        "columns": [
          "System",
          "Robots included",
          "Shared representation",
          "Target transfer method",
          "Held-out robot evidence",
          "Public status",
          "Main limitation"
        ],
        "rows": [
          [
            "Open X-Embodiment / RT-X",
            "22 embodiments reported",
            "Normalized trajectory and language dataset mixture",
            "Cross-dataset training and target adaptation",
            "Cross-embodiment studies reported",
            "Data project public",
            "Heterogeneous quality and action definitions"
          ],
          [
            "Octo",
            "Nine training platforms reported",
            "Tokenized observations with diffusion action head",
            "Fine-tuning and robot-specific interfaces",
            "Target-platform adaptation reported",
            "Code and checkpoints public",
            "Not zero-configuration on arbitrary robots"
          ],
          [
            "OpenVLA",
            "Multiple source embodiments through Open X data",
            "Tokenized multimodal policy",
            "Target-robot fine-tuning",
            "Real target-robot evaluations reported",
            "Code and 7B weights public",
            "Large model and embodiment-specific integration"
          ],
          [
            "GR00T N1.6",
            "Several humanoid embodiments targeted",
            "Embodiment-conditioned policy representation",
            "Adapters, fine-tuning and synthetic data",
            "NVIDIA reports multi-embodiment evaluation",
            "Open model release reported",
            "Independent hardware replication limited"
          ],
          [
            "OSCAR",
            "Multiple embodiments reported",
            "Embodiment-aware predictive and action representation",
            "Cross-robot adaptation",
            "Author-reported held-out transfer",
            "Paper; artifacts incomplete",
            "Recent work with limited replication"
          ],
          [
            "HumanEgo",
            "Human ego data and robot embodiments",
            "Shared human-robot task representation",
            "Retargeting and robot adaptation",
            "Author-reported robot transfer",
            "Paper",
            "Human motion lacks direct robot force and action labels"
          ]
        ],
        "description": "The table focuses on representation and adaptation. Robot counts alone do not establish successful transfer.",
        "note": "Performance values are omitted because tasks, targets and adaptation budgets are not comparable."
      }
    ],
    "sections": [
      {
        "heading": "Definition learning across different bodies",
        "paragraphs": [
          "Cross-embodiment learning uses data from one or more bodies to improve a policy or representation for another. Bodies may differ in arm length, number of joints, gripper type, mobility, cameras and control frequency. The target can be present during pre-training or held out entirely.",
          "Multi-robot training is not automatically cross-embodiment transfer. If every robot performs unrelated tasks, the model may learn separate modes without sharing useful structure. Strong evidence shows that source-robot data improves a common target task or reduces target data compared with training from scratch."
        ]
      },
      {
        "heading": "How action spaces are aligned",
        "paragraphs": [
          "Joint commands are embodiment-specific, so many datasets convert actions into task space end-effector translation, rotation and gripper state. Values are normalized by scale and control interval. A model may also predict keypoints, object-relative waypoints or latent action tokens that a robot-specific decoder converts into commands.",
          "Task-space actions simplify sharing but hide feasibility. Two robots can request the same hand motion while one hits a joint limit or collides with its torso. Embodiment-specific heads, kinematic projection and low-level controllers restore robot constraints after the shared policy proposes an action."
        ],
        "subsections": [
          {
            "heading": "Embodiment tokens and adapters",
            "paragraphs": [
              "An embodiment token tells the model which hardware is active. Adapters modify a subset of parameters for the target robot, reducing fine-tuning cost. These mechanisms help separate common task knowledge from body-specific control, but they need representative target examples and do not create unseen mechanical capabilities."
            ]
          },
          {
            "heading": "Retargeting and shared latent spaces",
            "paragraphs": [
              "Retargeting maps a source trajectory onto the target robot while respecting reach and joint limits. Shared latent spaces instead learn a representation where similar task phases align across bodies. Retargeting is explicit and interpretable; latent alignment can capture broader patterns but may conceal failure when bodies interact differently with the object."
            ]
          }
        ]
      },
      {
        "heading": "Camera placement and action frequency",
        "paragraphs": [
          "A wrist camera on one robot may be a head camera on another. The model must separate object appearance from viewpoint and know the transform between camera and action frame. External cameras can standardize observations in a laboratory but may not exist at deployment.",
          "Action frequency changes the meaning of a delta. A ten-millimeter command at 5 hertz is not equivalent to the same command at 50 hertz. Datasets resample trajectories or include timing, but resampling can smooth contact events. Target controllers need explicit rate and latency handling."
        ]
      },
      {
        "heading": "Key datasets and models",
        "paragraphs": [
          "Open X-Embodiment aggregates data from many institutions and enabled RT-X cross-robot studies. Octo and OpenVLA turn that mixture into open policies intended for target adaptation. Their public artifacts make it possible to inspect normalization and fine-tuning, although deployment still requires robot-specific code.",
          "NVIDIA’s GR00T targets humanoids with embodiment-conditioned learning and synthetic data. OSCAR explores embodiment-aware predictive control, while HumanEgo studies transfer from human first-person behavior into robot embodiments. These newer results expand the source bodies but remain author-reported and task-specific."
        ],
        "sources": [
          {
            "label": "Open X-Embodiment",
            "url": "https://robotics-transformer-x.github.io/"
          },
          {
            "label": "Octo",
            "url": "https://octo-models.github.io/"
          },
          {
            "label": "OpenVLA",
            "url": "https://github.com/openvla/openvla"
          },
          {
            "label": "GR00T N1.6",
            "url": "https://research.nvidia.com/labs/gear/gr00t-n1_6/"
          }
        ]
      },
      {
        "heading": "Evidence from real robots",
        "paragraphs": [
          "A strong experiment trains on several source robots, holds out the target embodiment and evaluates a common task before target fine-tuning. It then reports adaptation data and compares with a target-only baseline. Merely evaluating on a robot that contributed training data measures multi-robot coverage, not held-out transfer.",
          "Real tests should include target camera geometry, gripper contact and control rate. Simulation can expand robot diversity but may understate mechanical mismatch. Reported gains are most credible when source data improves target performance with the same target-data budget and when negative transfer is disclosed."
        ]
      },
      {
        "heading": "What transfers across robots and what does not",
        "paragraphs": [
          "Object identity, language grounding, coarse task phases and some object-relative motion patterns often transfer. Reaching toward a handle or moving an object from one region to another has shared structure. Learned visual features can recognize the same object across cameras after adaptation.",
          "Precise grasp pose, force, collision clearance, balance and timing remain tied to the embodiment. A parallel gripper and five-finger hand create different contacts. A humanoid must coordinate torso and feet, while a fixed arm does not. Transfer should therefore be layered shared semantics and planning above embodiment-specific control and safety."
        ]
      },
      {
        "heading": "Failure modes and practical applications",
        "paragraphs": [
          "Normalization can compress rare but important actions. The policy may confuse two robots with similar observations but different limits. A target adapter can overfit a small dataset. Retargeting may preserve hand position while producing unnatural joint motion. Negative transfer can make target learning slower than a target-only baseline.",
          "Credible uses include initializing policies for new arms, sharing perception and task representations across a fleet and reducing demonstrations for related grippers. Zero-data transfer to arbitrary humanoids remains experimental. Deployment should measure target performance, not rely on source-robot benchmark scores."
        ]
      }
    ],
    "limitations": [
      "Robot counts do not reveal task overlap, dataset balance or data quality.",
      "Held-out-embodiment protocols are uncommon and inconsistent.",
      "Task-space normalization can hide feasibility and timing differences.",
      "Closed humanoid datasets limit independent transfer analysis.",
      "Contact-rich and whole-body transfer remains substantially harder than semantic transfer."
    ],
    "conclusion": [
      "Cross-embodiment learning is valuable because robot data is expensive and many tasks share visual and semantic structure. The transferable part, however, is not the entire controller. Object understanding, language and coarse task phases usually move across bodies more easily than contact, timing and dynamics.",
      "Open X-Embodiment, Octo and OpenVLA provide the clearest public infrastructure for multi-robot learning. GR00T and newer embodiment-aware systems extend the idea toward humanoids, with less independent evidence. A rigorous transfer claim should hold out a target robot, state the adaptation budget and compare against target-only training. The practical architecture uses shared representations and task-space intent above robot-specific kinematics, control and safety."
    ],
    "faqs": [
      {
        "question": "What is cross-embodiment robot learning?",
        "answer": "Cross-embodiment learning uses data or representations from one robot or body to improve another. The robots may differ in joints, arm length, grippers, cameras or mobility. A strong result shows that source data reduces target training or improves a held-out target task compared with training only on target-robot data."
      },
      {
        "question": "How can one policy work on different robots?",
        "answer": "The policy can use normalized task-space actions, embodiment tokens, shared latent representations and robot-specific action heads. A kinematic or low-level controller converts common intent into feasible commands. Most systems still require target calibration, adapters or fine-tuning because cameras, reach, timing and contact differ across robots."
      },
      {
        "question": "What is action normalization?",
        "answer": "Action normalization converts robot commands into a shared scale and representation, such as end-effector position deltas and gripper state. It makes heterogeneous datasets easier to combine. The process must account for control frequency and physical limits; otherwise identical normalized values can produce different speeds, reach or contact on different hardware."
      },
      {
        "question": "What transfers well across robot embodiments?",
        "answer": "Visual object features, language grounding, task order and object-relative motion often transfer better than low-level control. For example, several robots can learn that a handle should be approached before a drawer moves. Exact grasp pose, force, collision clearance, balance and actuator timing usually need embodiment-specific adaptation."
      },
      {
        "question": "Do cross-embodiment models need fine-tuning?",
        "answer": "Usually. Fine-tuning or adapters align the policy with the target robot’s observations and actions. Some studies report zero-shot transfer for limited task-space interfaces, but arbitrary new robots differ in camera placement, kinematics and dynamics. The required target data should be reported rather than hidden inside integration work."
      },
      {
        "question": "How should cross-embodiment transfer be evaluated?",
        "answer": "Hold out a target robot, evaluate before adaptation, then fine-tune with a stated amount of target data. Compare with a target-only baseline using the same budget. Report task success, unsafe contacts, latency and failures. Tests should distinguish a new robot from a familiar robot performing a new object or task."
      }
    ],
    "methodology": [
      "The comparison records source robots, shared action representation, target adaptation and held-out-robot evidence. Multi-robot datasets were not treated as transfer evidence unless the primary material evaluated reuse across embodiments.",
      "Counts and release status come from official project pages and papers. Success rates are not combined because targets and adaptation budgets differ. Verification date July 11, 2026."
    ],
    "sourceLinks": [
      {
        "label": "Open X-Embodiment Collaboration",
        "organization": "Google DeepMind and 33 institutions",
        "documentTitle": "Open X-Embodiment and RT-X",
        "date": "2023 · accessed July 11, 2026",
        "url": "https://robotics-transformer-x.github.io/"
      },
      {
        "label": "Octo Model Team",
        "organization": "UC Berkeley and collaborators",
        "documentTitle": "Octo",
        "date": "2024 · accessed July 11, 2026",
        "url": "https://octo-models.github.io/"
      },
      {
        "label": "OpenVLA team",
        "organization": "Stanford and UC Berkeley",
        "documentTitle": "OpenVLA",
        "date": "2024 · accessed July 11, 2026",
        "url": "https://github.com/openvla/openvla"
      },
      {
        "label": "NVIDIA GEAR Lab",
        "organization": "NVIDIA",
        "documentTitle": "GR00T N1.6",
        "date": "December 15, 2025 · accessed July 11, 2026",
        "url": "https://research.nvidia.com/labs/gear/gr00t-n1_6/"
      },
      {
        "label": "OSCAR authors",
        "organization": "Research collaboration",
        "documentTitle": "OSCAR",
        "date": "June 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2606.04463"
      },
      {
        "label": "HumanEgo authors",
        "organization": "Research collaboration",
        "documentTitle": "HumanEgo",
        "date": "May 2026 · accessed July 11, 2026",
        "url": "https://arxiv.org/abs/2605.24934"
      }
    ],
    "internalLinks": [
      {
        "label": "Vision-language-action models and shared action representations",
        "path": "/robotics-news/vision-language-action-models"
      },
      {
        "label": "Egocentric robotics data and human-to-robot transfer",
        "path": "/robotics-news/egocentric-robotics-data"
      },
      {
        "label": "Generalist robot policies across tasks and bodies",
        "path": "/robotics-news/generalist-robot-policies"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot cross-embodiment transfer map",
        "alt": "Several robot bodies connected to a shared task representation and separate action heads",
        "source": "TechniaHQRobot original based on cited projects",
        "sourceUrl": "https://robotics-transformer-x.github.io/"
      },
      {
        "type": "Secondary media",
        "caption": "Open X-Embodiment robot montage",
        "alt": "Different robot arms and mobile manipulators included in the official project",
        "source": "Open X-Embodiment Collaboration",
        "sourceUrl": "https://robotics-transformer-x.github.io/"
      },
      {
        "type": "Secondary media",
        "caption": "Octo target-robot adaptation examples",
        "alt": "Multiple robot platforms fine-tuned from a shared Octo checkpoint",
        "source": "Octo Model Team",
        "sourceUrl": "https://octo-models.github.io/"
      },
      {
        "type": "Original chart",
        "caption": "Transferability by representation layer",
        "alt": "Chart ranking semantics, task phase, task-space motion, contact and torque by transfer difficulty",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://github.com/openvla/openvla"
      },
      {
        "type": "Architecture",
        "caption": "Shared encoder with embodiment tokens and robot-specific heads",
        "alt": "Diagram of multi-robot observations feeding a common policy and separate target controllers",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://research.nvidia.com/labs/gear/gr00t-n1_6/"
      }
    ],
    "structuredDataSummary": [
      "Article schema represents the cross-embodiment technical guide.",
      "FAQPage schema contains six visible transfer questions.",
      "BreadcrumbList schema follows Home, Robotics News and Cross-Embodiment Learning."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "Open X-Embodiment reports 22 robot embodiments.",
        "Octo and OpenVLA provide public code and checkpoints.",
        "The article separates multi-robot training from held-out-robot transfer."
      ],
      "unconfirmed": [
        "Comparable before-and-after adaptation results are not available across all systems.",
        "Closed humanoid datasets and failure logs remain unavailable.",
        "Some recent 2026 project artifacts are incomplete."
      ],
      "fastChanging": [
        "New embodiment adapters and datasets may alter transfer results.",
        "Repository and checkpoint availability can change."
      ]
    }
  },
  {
    "category": "Physical AI technical guide",
    "datePublished": "2026-07-11",
    "dateModified": "2026-08-15",
    "readingTime": "18 min read",
    "authorName": "TechniaHQRobot",
    "authorHandle": "@techniahqrobot",
    "schemaType": "Article",
    "image": "/og-image-tech-ai-2025.png",
    "imageWidth": 1200,
    "imageHeight": 630,
    "id": "physical-ai-safety-architectures",
    "slug": "physical-ai-safety-architectures",
    "path": "/robotics-news/physical-ai-safety-architectures",
    "title": "Safety Architectures for Physical AI",
    "headline": "Safety Architectures for Physical AI From Mechanics to Model Monitoring",
    "seoTitle": "Safety Architectures for Physical AI and Autonomous Robots",
    "description": "A layered safety architecture for Physical AI covering mechanics, functional safety, runtime monitoring, cybersecurity, standards, fallbacks and evidence.",
    "primaryKeyword": "safety architectures for Physical AI",
    "keywords": [
      "Physical AI safety",
      "robot safety architecture",
      "functional safety robotics",
      "humanoid robot safety",
      "ISO 10218",
      "ISO TS 15066",
      "ISO 13482",
      "IEC 61508",
      "ISO 13849",
      "UL 3300",
      "robot cybersecurity"
    ],
    "searchQuestions": [
      "How are Physical AI robots made safe?",
      "What is functional safety in robotics?",
      "Which safety standards apply to robots?",
      "Can AI alignment replace robot safety systems?",
      "How should robot policies be monitored at runtime?"
    ],
    "longTailKeywords": [
      "layered safety architecture for AI controlled robots",
      "functional safety versus model safety in robotics",
      "humanoid robot hazard detection and fallback matrix",
      "cybersecurity for teleoperated and autonomous robots"
    ],
    "entities": [
      "ISO 10218-1:2025",
      "ISO 10218-2:2025",
      "ISO/TS 15066:2016",
      "ISO 13482",
      "IEC 61508",
      "ISO 13849",
      "UL 3300",
      "NIST SP 800-82 Rev. 3"
    ],
    "imageAlt": "Layered robot safety architecture from mechanical limits and certified controls to perception, policy monitoring and cybersecurity",
    "introduction": [
      "A robot policy can choose a reasonable goal and still produce an unsafe motion because a depth estimate is wrong, a sensor is delayed or an actuator saturates. Physical AI safety therefore cannot be implemented as one content filter around a model. It is a layered architecture that limits energy, monitors state, constrains plans, detects faults and moves the machine into a known fallback state.",
      "This article separates mechanical, functional, operational, model, cybersecurity and human-robot interaction safety. It maps hazards to detection methods, prevention layers, fallbacks and required evidence. Applicable standards are discussed by scope without claiming that any named humanoid or learned policy is certified. The guide also covers unsafe language instructions, hallucinated object state, distribution shift, unauthorized teleoperation, communication loss and model-update regression, then explains why software alignment cannot replace physical protection."
    ],
    "keyFindings": [
      "Safety must remain effective when the learned model is wrong, unavailable or compromised.",
      "Mechanical limits, independent safety-rated controls and emergency stops address hazards that model-level safeguards cannot.",
      "Functional safety is about reliable risk reduction under faults; model safety addresses uncertainty and unsafe learned behavior.",
      "Cybersecurity becomes a physical safety issue when networks can change commands, sensors, updates or teleoperation authority.",
      "Certification applies to defined products, configurations and uses, not to a robot category or model family in general."
    ],
    "comparisonTables": [
      {
        "id": "physical-ai-hazard-matrix",
        "title": "Physical AI hazard and fallback matrix",
        "columns": [
          "Hazard",
          "Detection method",
          "Prevention layer",
          "Fallback behavior",
          "Remaining risk",
          "Evidence required"
        ],
        "rows": [
          [
            "Hallucinated object state",
            "Sensor consistency checks, redundant perception, confidence monitor",
            "Geometric collision model and constrained planner",
            "Stop, rescan or request supervision",
            "Shared sensor failure or undetected occlusion",
            "Scenario tests, false-negative rates and logged interventions"
          ],
          [
            "Unsafe language instruction",
            "Instruction policy, task whitelist and hazard classifier",
            "Application rules and safety-rated motion limits",
            "Reject command and explain allowed alternative",
            "Ambiguous phrasing or malicious paraphrase",
            "Red-team cases and validated command taxonomy"
          ],
          [
            "Sensor failure or stale data",
            "Heartbeat, timestamp and plausibility checks",
            "Redundant sensing and safe-speed limits",
            "Controlled stop or degraded mode",
            "Common-mode faults",
            "Fault-injection tests and diagnostic coverage"
          ],
          [
            "Delayed or unstable action",
            "Deadline monitor, trajectory divergence and torque limits",
            "Real-time controller and bounded action interface",
            "Freeze command, hold or stop safely",
            "Momentum during stopping",
            "Worst-case latency and stopping-distance tests"
          ],
          [
            "Unexpected human entry",
            "Safety scanner, vision and protective field",
            "Speed-and-separation monitoring or guarded cell",
            "Slow, stop or retreat to safe pose",
            "Occlusion and detection latency",
            "Coverage validation and human-approach trials"
          ],
          [
            "Uncontrolled contact",
            "Force-torque, joint torque, tactile and collision monitoring",
            "Mechanical compliance and force limits",
            "Release force, backdrive or stop",
            "Pinch geometry and sensor saturation",
            "Force/pressure testing for the application"
          ],
          [
            "Loss of communication",
            "Link heartbeat and command timeout",
            "Local safety controller and authority timeout",
            "Stop, sit, park or maintain stable safe state",
            "Unsafe terrain or carried load",
            "Communications fault tests and fallback validation"
          ],
          [
            "Unauthorized teleoperation",
            "Identity, authorization, signed commands and anomaly logs",
            "Network segmentation and local command limits",
            "Revoke session and stop robot",
            "Credential theft or insider misuse",
            "Penetration tests, access logs and incident response"
          ],
          [
            "Model update regression",
            "Versioned validation, shadow tests and behavior change detection",
            "Signed deployment and rollback control",
            "Revert model or disable learned mode",
            "Undetected rare-scenario regression",
            "Release gates, regression suite and traceability"
          ]
        ],
        "description": "The matrix separates detection, prevention and fallback. No single layer is assumed to be perfect.",
        "note": "Required evidence depends on the product, environment, task and applicable regulatory framework."
      },
      {
        "id": "robot-safety-standards",
        "title": "Standards and guidance relevant to Physical AI systems",
        "columns": [
          "Document",
          "Scope",
          "Relevance",
          "Important boundary"
        ],
        "rows": [
          [
            "ISO 10218-1:2025",
            "Industrial robot manufacturers",
            "Design and protective measures for industrial robots",
            "Does not certify a specific AI policy by itself"
          ],
          [
            "ISO 10218-2:2025",
            "Industrial robot applications and cells",
            "Integration, safeguarding and validation",
            "Application and installation matter as much as robot hardware"
          ],
          [
            "ISO/TS 15066:2016",
            "Collaborative industrial robot operations",
            "Guidance for collaborative applications and contact limits",
            "Technical specification; revision work continues"
          ],
          [
            "ISO 13482",
            "Personal care robots",
            "Safety requirements for non-medical personal care robots",
            "Scope and robot type must match the product"
          ],
          [
            "IEC 61508",
            "Electrical, electronic and programmable electronic safety systems",
            "Functional safety lifecycle and integrity concepts",
            "Sector standards may provide more specific requirements"
          ],
          [
            "ISO 13849",
            "Safety-related parts of control systems",
            "Performance levels and control-system risk reduction",
            "Learned performance is not a substitute for validated safety functions"
          ],
          [
            "UL 3300",
            "Service, communication, information and education robots",
            "Product safety evaluation in covered service-robot categories",
            "Certification must be confirmed for the exact product"
          ],
          [
            "NIST SP 800-82 Rev. 3",
            "Operational technology security",
            "Cybersecurity controls for industrial control environments",
            "Guidance is not product safety certification"
          ]
        ],
        "description": "This is a scope map, not legal advice or a claim that every listed document applies to every robot.",
        "note": "Standards status was checked through official ISO, UL and NIST sources on July 11, 2026."
      }
    ],
    "sections": [
      {
        "heading": "Definition safety as a layered architecture",
        "paragraphs": [
          "A safety architecture is the set of independent and interacting measures that reduce risk across hardware, control, software, operations and security. It begins with hazard analysis identify potential harm, estimate severity and exposure, then design risk reduction that remains effective under foreseeable faults and misuse.",
          "A model refusing an unsafe command is one layer. It does not replace a torque limit, safety-rated stop or physical guard. Layers should fail independently where practical, and the safe state must be defined for the task. A biped carrying a load may need to place it down before powering off; an industrial arm may need an immediate protective stop."
        ]
      },
      {
        "heading": "Mechanical and actuator safety",
        "paragraphs": [
          "Mechanical design controls maximum energy through mass, speed, gearing, compliance, padding, rounded geometry and pinch-point reduction. Joint and actuator limits constrain position, velocity, torque and temperature. Backdrivability or series elasticity can reduce impact force, but compliance can also store energy and create unpredictable rebound.",
          "Emergency stops and protective stops need defined categories and stopping behavior. Brakes, gravity loads and battery faults must be considered. A software command to stop is insufficient if the same processor or network is part of the failure."
        ]
      },
      {
        "heading": "Perception, planning and runtime monitoring",
        "paragraphs": [
          "Perception safety checks sensor health, timestamps, field of view and consistency between modalities. Human detection should be linked to protective behavior rather than used only for scene understanding. Planning constraints can exclude keep-out zones, limit speed near people and reject paths with uncertain clearance.",
          "Runtime monitors compare commanded and measured motion, enforce action bounds and detect policy oscillation, stale observations or missed deadlines. A safety supervisor should be simpler and more verifiable than the learned policy. It can project proposed actions into a safe set, switch to a conventional controller or stop execution."
        ],
        "subsections": [
          {
            "heading": "Model-level safeguards",
            "paragraphs": [
              "Model-level controls include instruction filtering, uncertainty estimation, out-of-distribution detection, constrained decoding and policy verification on test suites. They reduce unsafe proposals but cannot guarantee the physical state is correct. Their outputs should remain subordinate to real-time motion and force constraints."
            ]
          },
          {
            "heading": "Fail-safe and degraded states",
            "paragraphs": [
              "A fail-safe state minimizes risk after a fault. It may be a stop, low-power hold, seated posture, parked base or controlled release. Degraded operation permits a reduced capability, such as slow teleoperation after one camera fails. The transition itself must be validated because stopping a moving legged robot can cause a fall."
            ]
          }
        ]
      },
      {
        "heading": "Functional, operational and application-specific safety",
        "paragraphs": [
          "Functional safety addresses whether safety-related control functions achieve the required risk reduction when faults occur. Operational safety covers procedures, training, maintenance, protective zones and human supervision. Human-robot interaction safety examines contact, intent communication and predictable behavior. Application-specific safety depends on the task, payload, environment and population exposed.",
          "A warehouse robot, home assistant and medical robot face different hazards and standards. The same humanoid hardware can require different safeguards when carrying a sharp tool, a box or an empty tray. Risk assessment belongs to the complete application, not only the robot model."
        ]
      },
      {
        "heading": "Cybersecurity as physical safety",
        "paragraphs": [
          "Physical AI systems receive updates, remote commands and sensor data over networks. Unauthorized teleoperation, altered maps, replayed commands or compromised model packages can create motion hazards. Controls include authenticated identities, least privilege, signed updates, encrypted links, network segmentation and local command limits.",
          "Remote shutdown must not become a single remote point of failure. The robot needs local timeout and safe fallback when connectivity is lost. Audit logs should record command authority, model version, safety events and interventions without exposing unnecessary personal data."
        ],
        "sources": [
          {
            "label": "NIST SP 800-82 Rev. 3",
            "url": "https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-82r3.pdf"
          },
          {
            "label": "NISTIR 8219",
            "url": "https://nvlpubs.nist.gov/nistpubs/ir/2020/NIST.IR.8219.pdf"
          }
        ]
      },
      {
        "heading": "Standards and certification boundaries",
        "paragraphs": [
          "ISO 10218-1:2025 and ISO 10218-2:2025 address industrial robots and applications. ISO/TS 15066:2016 provides collaborative-operation guidance, while ISO work continues on successor material. ISO 13482 covers personal care robot categories. IEC 61508 and ISO 13849 provide functional-safety concepts and control-system methods. UL 3300 covers specified service robot categories.",
          "Applicability depends on product type, integration and jurisdiction. Conformance or certification must be confirmed through official evidence for the exact product and configuration. A company statement that a robot was designed with a standard in mind is not equivalent to third-party certification."
        ],
        "sources": [
          {
            "label": "ISO 10218-1:2025",
            "url": "https://www.iso.org/standard/73933.html"
          },
          {
            "label": "ISO 10218-2:2025",
            "url": "https://www.iso.org/standard/73934.html"
          },
          {
            "label": "ISO/TS 15066:2016",
            "url": "https://www.iso.org/standard/62996.html"
          },
          {
            "label": "UL 3300 robot services",
            "url": "https://www.ul.com/services/consumer-and-commercial-robots"
          }
        ]
      },
      {
        "heading": "Why software alignment cannot replace physical safety",
        "paragraphs": [
          "Alignment techniques operate on the model’s interpretation of the instruction and scene. They can reduce deliberate unsafe requests and some policy errors. They cannot change the robot’s mass, stop distance, sensor blind spots or stored mechanical energy. They also depend on the same observations that may be wrong.",
          "Physical safety assumes the intelligent layer can fail. Independent limits, guards, brakes, force monitoring and emergency controls constrain the consequence. The correct design uses model safeguards to reduce how often unsafe proposals occur and physical safeguards to limit harm when they still do."
        ]
      },
      {
        "heading": "Validation, model updates and evidence",
        "paragraphs": [
          "Validation should cover nominal tasks, foreseeable misuse, sensor faults, network loss, timing overruns, human approach and physical disturbances. Learned models need frozen versions, traceable data, regression suites and controlled rollout. Shadow mode can compare a new policy with the deployed one before it receives command authority.",
          "Evidence should include stopping distance, force and pressure tests, diagnostic coverage, cybersecurity testing, intervention logs and residual risk. A benchmark score or successful video does not establish application safety. Safety claims need the exact robot, software version, payload, environment and operating limits."
        ]
      },
      {
        "heading": "2026 update policy uncertainty must trigger a defined fallback",
        "paragraphs": [
          "Uncertainty estimation can identify observations or generated action chunks that deserve more evidence, but a score has value only when it changes robot behavior. A monitored system can shorten the action horizon, reduce speed, gather another camera view, select a conservative controller or request human assistance. The threshold should depend on the hazard and must be calibrated for the stated robot and environment.",
          "Current VLA research studies flow-specific uncertainty, conformal action sets, distribution-shift detection and execution residuals. None is a substitute for safety-rated limits or emergency control. Statistical calibration constrains measured prediction behavior under assumptions; physical safety still requires independent protection when those assumptions fail."
        ]
      }
    ],
    "limitations": [
      "Standards applicability depends on jurisdiction, robot category, application and integration.",
      "This article maps engineering concepts and is not a certification decision or legal opinion.",
      "Public humanoid safety cases and independent incident data remain limited.",
      "Model uncertainty metrics are not universally calibrated for physical risk.",
      "A safe state for a legged robot can be task-dependent and difficult to execute after some faults."
    ],
    "conclusion": [
      "Physical AI safety is a property of the complete robot system and application, not a feature of the learned model alone. The architecture must control energy, detect faults, constrain motion, protect command authority and reach a validated fallback state when perception or policy fails.",
      "Mechanical design, independent safety controls, runtime monitoring, operational procedures and cybersecurity address different failure paths. Model-level safeguards add value by rejecting unsafe instructions and detecting uncertainty, but they cannot replace stopping distance, force limits or guards. Standards provide scope-specific requirements and methods; certification must be verified for the exact product and use. The practical test is whether each hazard has a detection path, prevention layer, fallback behavior and evidence that the remaining risk is acceptable."
    ],
    "faqs": [
      {
        "question": "How are Physical AI robots made safe?",
        "answer": "Safety is built in layers mechanical energy limits, actuator and joint constraints, safety-rated control functions, perception checks, constrained planning, runtime monitoring, emergency stops, operational procedures and cybersecurity. The learned model proposes or selects actions inside this architecture. Independent layers must still reduce risk when the model, sensor or network fails."
      },
      {
        "question": "What is functional safety in robotics?",
        "answer": "Functional safety concerns safety-related control functions that respond correctly to faults and hazardous conditions. Examples include protective stops, speed limits and monitored separation. It uses a lifecycle of hazard analysis, design, verification and validation. A robot policy’s average task success is not a functional-safety measure and cannot replace validated safety functions."
      },
      {
        "question": "Which safety standards apply to humanoid robots?",
        "answer": "There is no single standard that automatically covers every humanoid. Applicable documents depend on whether the system is industrial, collaborative, personal-care, service, mobile or medical and how it is used. ISO 10218, ISO/TS 15066, ISO 13482, ISO 13849, IEC 61508 and UL 3300 may be relevant within their stated scopes."
      },
      {
        "question": "Can AI alignment replace physical robot safety?",
        "answer": "No. Alignment can reduce unsafe instructions or model outputs, but it cannot reduce robot mass, shorten physical stopping distance or guarantee sensor accuracy. Physical safeguards assume the model can be wrong. Mechanical limits, independent monitors, force constraints, guards and emergency controls limit consequences when software-level protections fail."
      },
      {
        "question": "How should a robot policy be monitored at runtime?",
        "answer": "A runtime supervisor should check observation age, sensor health, action bounds, trajectory deviation, speed, force, collision risk and inference deadlines. It can reject or project unsafe actions, switch to a verified controller or stop the robot. The monitor should be simpler, independent where practical and tested with injected faults."
      },
      {
        "question": "Why is cybersecurity a robot safety issue?",
        "answer": "A compromised network or account can alter commands, disable updates, falsify sensor data or grant unauthorized teleoperation. Because these actions move physical hardware, security failures can become collision or contact hazards. Robots need authenticated authority, signed software, segmented networks, local limits, command timeouts, audit logs and tested recovery from communication loss."
      },
      {
        "question": "Does compliance with a standard mean a robot is safe everywhere?",
        "answer": "No. Standards apply to defined scopes, configurations and applications. A conforming component can still be integrated unsafely, and a certified robot may require guards, payload limits or operating procedures at a specific site. Safety must be reassessed when software, tooling, environment, task or human exposure changes."
      }
    ],
    "methodology": [
      "The architecture was built from official standards pages, UL robot-safety material and NIST operational-technology guidance. Standards are summarized by scope rather than interpreted as universal requirements.",
      "No certification is claimed for any manufacturer or model. The hazard matrix is an engineering template; required risk reduction and evidence must be determined for the actual application. Verification date July 11, 2026."
    ],
    "sourceLinks": [
      {
        "label": "International Organization for Standardization",
        "organization": "ISO",
        "documentTitle": "ISO 10218-1:2025 Robotics - Safety requirements - Part 1 Industrial robots",
        "date": "2025 · accessed July 11, 2026",
        "url": "https://www.iso.org/standard/73933.html"
      },
      {
        "label": "International Organization for Standardization",
        "organization": "ISO",
        "documentTitle": "ISO 10218-2:2025 Robotics - Safety requirements - Part 2 Industrial robot applications and robot cells",
        "date": "2025 · accessed July 11, 2026",
        "url": "https://www.iso.org/standard/73934.html"
      },
      {
        "label": "International Organization for Standardization",
        "organization": "ISO",
        "documentTitle": "ISO/TS 15066:2016 Robots and robotic devices - Collaborative robots",
        "date": "2016 · accessed July 11, 2026",
        "url": "https://www.iso.org/standard/62996.html"
      },
      {
        "label": "International Organization for Standardization",
        "organization": "ISO",
        "documentTitle": "Robotics standards overview including ISO 13482",
        "date": "Accessed July 11, 2026",
        "url": "https://www.iso.org/sectors/engineering/robotics"
      },
      {
        "label": "International Organization for Standardization",
        "organization": "ISO",
        "documentTitle": "ISO/AWI 15066-1 under development",
        "date": "Accessed July 11, 2026",
        "url": "https://www.iso.org/standard/91522.html"
      },
      {
        "label": "UL Solutions",
        "organization": "UL Solutions",
        "documentTitle": "Consumer and Commercial Robots - UL 3300 services",
        "date": "Accessed July 11, 2026",
        "url": "https://www.ul.com/services/consumer-and-commercial-robots"
      },
      {
        "label": "National Institute of Standards and Technology",
        "organization": "NIST",
        "documentTitle": "SP 800-82 Rev. 3 Guide to Operational Technology Security",
        "date": "September 2023 · accessed July 11, 2026",
        "url": "https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-82r3.pdf"
      },
      {
        "label": "National Institute of Standards and Technology",
        "organization": "NIST",
        "documentTitle": "NISTIR 8219 Securing Manufacturing Industrial Control Systems",
        "date": "2020 · accessed July 11, 2026",
        "url": "https://nvlpubs.nist.gov/nistpubs/ir/2020/NIST.IR.8219.pdf"
      }
    ],
    "internalLinks": [
      {
        "label": "Whole-body loco-manipulation and balance-related hazards",
        "path": "/robotics-news/whole-body-loco-manipulation"
      },
      {
        "label": "World action models and predictive-control uncertainty",
        "path": "/robotics-news/world-action-models"
      },
      {
        "label": "Vision-language-action models and runtime latency",
        "path": "/robotics-news/vision-language-action-models"
      },
      {
        "label": "Robot policy uncertainty, calibration and safe fallbacks",
        "path": "/robotics-news/robot-policy-uncertainty-estimation"
      }
    ],
    "imageRecommendations": [
      {
        "type": "Hero image",
        "caption": "TechniaHQRobot layered Physical AI safety architecture",
        "alt": "Concentric safety layers from mechanics and actuators to runtime monitoring, model safeguards and cybersecurity",
        "source": "TechniaHQRobot original based on cited standards",
        "sourceUrl": "https://www.iso.org/standard/73933.html"
      },
      {
        "type": "Secondary media",
        "caption": "Original hazard-to-fallback matrix infographic",
        "alt": "Matrix linking nine robot hazards to detection, prevention, fallback and evidence",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://www.iso.org/standard/73934.html"
      },
      {
        "type": "Secondary media",
        "caption": "Robot protective field and monitored stop illustration",
        "alt": "Industrial robot workspace with human detection, speed reduction and protective stop zones",
        "source": "TechniaHQRobot original based on ISO scope",
        "sourceUrl": "https://www.iso.org/standard/62996.html"
      },
      {
        "type": "Original chart",
        "caption": "Safety evidence stack",
        "alt": "Chart separating benchmark evidence, fault injection, force tests, stopping tests, cybersecurity and application validation",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://www.ul.com/services/consumer-and-commercial-robots"
      },
      {
        "type": "Architecture",
        "caption": "Independent safety supervisor around a learned policy",
        "alt": "Diagram showing perception, VLA policy, action filter, real-time controller, safety PLC, sensors and emergency stop",
        "source": "TechniaHQRobot original",
        "sourceUrl": "https://nvlpubs.nist.gov/nistpubs/SpecialPublications/NIST.SP.800-82r3.pdf"
      }
    ],
    "structuredDataSummary": [
      "Article schema represents the safety-architecture technical guide.",
      "FAQPage schema contains the seven visible safety questions.",
      "BreadcrumbList schema follows Home, Robotics News and Safety Architectures for Physical AI."
    ],
    "factCheck": {
      "verifiedDate": "July 11, 2026",
      "confirmed": [
        "ISO 10218 Parts 1 and 2 have 2025 editions on official ISO pages.",
        "ISO/TS 15066:2016 remains an official published technical specification and successor work is listed as under development.",
        "No certification claim is made for a robot or AI model."
      ],
      "unconfirmed": [
        "Applicable standards and required performance levels depend on the exact product, task and jurisdiction.",
        "Public independent safety cases for current humanoid systems are limited.",
        "Model-level uncertainty measures are not directly comparable across policies."
      ],
      "fastChanging": [
        "Standards projects and national adoption can change.",
        "Robot software versions and cybersecurity threats change after publication."
      ]
    }
  }
];
